import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { ChatUser } from '../types/chat'
import type {
  BackgroundFilterSelection,
  CallMediaType,
  CallParticipant,
  CallParticipantState,
  CarModeSignalPayload,
  MediaSwitchPayload,
  RoomSignalPayload,
  StartCallOptions,
  VoiceCallEndCause,
  VoiceCallEndReason,
  VoiceCallPeer,
  VoiceCallPhase,
  VoiceCallSignal,
  VoiceCallSession,
} from '../types/voice-call'
import {
  isTransferInProgress,
  type TransferSignalPayload,
} from '@shared/calls/transfer'
import { getCallEligibilityConfig } from '../features/calls/data/call-eligibility-config'
import { buildCallTargetContext } from '../features/calls/utils/call-target-context'
import { syncCarRecentsFromHistory } from '../features/calls/utils/car-recents-sync'
import {
  resolveCarStartFailureAlert,
  syncCarContactsFromDirectory,
} from '../features/calls/utils/car-contacts-sync'
import { evaluateCallEligibility } from '@shared/calls/call-eligibility'
import {
  buildPostConnectRecordingConsentAcceptPayload,
  buildPriorRecordingConsentAcceptPayload,
  buildRecordingConsentOptOutPayload,
  buildRecordingConsentSignalPayload,
  canMemberOptOutOfRecording,
  CONSENT_WINDOW_MS,
  noticeSkipReasonForUnnoticedAnswer,
  RECORDING_CONSENT_MISSING_END_REASON,
  owesPostConnectConsentNotice,
  planRejoinedCalleeConsent,
  requiresRecordingConsent,
  requiresRecordingPolicyAcceptance,
  type ConsentCallEndedNotice,
  type ConsentNotRecordedNotice,
  RECORDING_CONSENT_REQUIRED_PEER,
  RECORDING_CONSENT_REQUIRED_SELF,
  recordingConsentRequiredSelfMessage,
} from '@shared/calls/recording-consent'
import { resolveUnansweredEndReason } from '@shared/calls/call-terminal-status'
import { advertisedDeviceClaimCaps } from '@shared/calls/device-claim-liveness'
import { resolveHeldPartyUserId } from '@shared/calls/transfer-peer'
import { setPendingCallGate } from '../stores/call-gate-store'
import {
  initCarConnectionStore,
  isCarConnected,
  refreshCarConnection,
  useCarConnected,
} from '../stores/car-connection-store'
import { setPendingRecordingPolicyGate } from '../stores/recording-policy-gate-store'
import {
  getRecordingPolicyConsent,
  hasAcceptedCurrentRecordingPolicy,
  readCachedRecordingPolicyConsent,
} from '../stores/recording-policy-store'
import {
  acceptRecordingPolicy,
  declineRecordingPolicy,
  loadRecordingPolicyConsent,
  resolveRecordingPolicyConsent,
} from '../services/recording-policy-service'
import { showRecordingConsentPeerNotice } from '../stores/recording-consent-peer-store'
import {
  summarizeGroupInviteConsent,
  type GroupInviteOutcome,
} from '../features/calls/utils/group-invite-consent'
import {
  consentFirstName,
  PeerRecordingConsentRequiredError,
  recordingConsentRejectionFromError,
} from '../features/calls/utils/recording-consent-required'
import { isAppLocked } from '../stores/app-lock-store'
import { ensureSoftUnlocked } from '../features/security/soft-lock-bridge'
import {
  clearWaitingCall,
  getWaitingCall,
  setWaitingCall,
} from '../stores/waiting-call-store'
import { HoldMusicPlayer } from '../features/calls/utils/hold-music-player'
import { noteIncomingInviteReceived } from '../features/calls/utils/incoming-ring-timing'
import { getTransferEligibilityConfig } from '../features/calls/data/transfer-config'
import { resolveMediaUrl } from '../utils/ringtone-preference'
import { publishTransferInput } from '../features/calls/utils/transfer-signals'
import {
  chimeService,
  CallEndedError,
  CallFullError,
  type ChimeJoinTokenResponse,
} from '../services/chime-service'
import { graphqlSubscription } from '../services/appsync-client'
import { emitConsentWindowEnded, shouldReclassifyAsConsentEnd } from '../utils/consent-window-end'
import { invalidateCallHistoryCache } from '../services/call-history-service'
import { recordUserActivity } from '../services/activity-presence'
import {
  buildCallEndPayload,
  extractCallerInvitePayload,
  PEER_LOST_END_DETAIL,
  type CallEndPayload,
  fetchMyActiveCall,
  onCallRoomSignalSubscription,
  onVoiceCallSignalSubscription,
  parseSignalPayload,
  publishDeviceClaim,
  publishDeviceClaimKeepalive,
  publishVoiceCallEndKeepalive,
  publishVoiceCallSignal,
} from '../services/voice-call-service'
import {
  getCachedDirectoryUser,
  getCachedChatUser,
  getCurrentChatUser,
  resolveDirectoryUser,
} from '../services/chat-service'
import { ChimeCallAdapter } from '../services/voice-call/chime-call-adapter'
import {
  getVoiceCallSession,
  isTerminalCallPhase,
  patchVoiceCallSession,
  setVoiceCallSession,
} from '../stores/voice-call-store'
import { ensureCallPermissionsForCall, cameraDeniedMessage, microphoneDeniedMessage } from '../utils/request-call-permissions'
import { getNativePlatform, isNativeApp } from '../utils/platform'
import { shouldAutoPauseVideoForCar } from '../utils/car-video-pause-guard'
import { isCallAlreadyEndedError, isNotParticipantError } from '../utils/format-error'
import {
  decideConnectingStall,
  decideOnHeartbeatMiss,
  shouldClearEndedSession,
  shouldIgnoreOwnAnswerEcho,
  shouldInitiateNativeIosVoiceJoin,
} from '../utils/call-connection-guards'
import { isDocumentHidden, waitUntilDocumentVisible, yieldToDom } from '../utils/yield-to-dom'
import {
  callAudioRouteArbiter,
  desiredCallSpeakerOn,
} from '../utils/call-audio-route'
import {
  applyDesiredNativeSpeakerphone,
  noteHardwareRoute,
  prepareNativeRingback,
  resetNativeAudioRoute,
  setNativeSpeakerphone,
} from '../services/voice-call/native-audio-route'
import {
  getActiveOutgoingDialPlan,
  prepareOutgoingDialPlan,
} from '../features/dial-tones/outgoing-dial-plan'
import { getDeviceId } from '../utils/device-id'
import {
  isCallTombstoned,
  recordCallEndedTombstone,
  tombstonedCallReason,
} from '../utils/call-ended-tombstones'
import { isPlaceholderCallerName } from '../utils/caller-display-name'
import {
  isOutboundRedialBlocked,
  noteRemoteCallRejected,
} from '../utils/outbound-redial-guard'
import { decideRehydrateAction } from '../utils/call-rehydrate-policy'
import { isDuplicateOfLiveCarDial } from '../utils/car-start-dedupe'
import {
  claimSiblingRelayOnce,
  isSiblingRelaySignal,
  shouldRelayPeerTerminal,
} from '../utils/voice-call-sibling-relay'
import {
  addIncomingCallActionListener,
  clearCallNotification,
  showIncomingCallActionNotification,
} from '../services/push-notification-service'
import {
  addNativeAudioRouteListener,
  addNativeAudioSessionActivatedListener,
  addNativeAudioSessionDeactivatedListener,
  addNativeCallActionListener,
  addNativeCallKitReturnListener,
  addNativeConnectionDestroyedListener,
  addNativeDeviceUnlockedListener,
  addNativeIncomingRingListener,
  consumeNativePendingCallAction,
  consumeNativePendingIncomingCall,
  consumeNativePendingCarStartCall,
  cancelNativeIncomingCall,
  endNativeOutgoingCall,
  expectNativeDialReport,
  noteNativeOutgoingAnswered,
  type NativeCallOwner,
  getActiveCallKitCall,
  getNativeKeyguardLocked,
  isNativeIncomingRingActive,
  recordNativeCarRecent,
  notifyNativeCarStartFailed,
  addNativeCarStartCallListener,
  isNativeCarStartCancelledSince,
  setNativeCallActive,
  setNativeCallDisconnected,
  setNativeSignedInUser,
  sweepNativeTelecomConnections,
  getNativeEndedCallIds,
  showNativeIncomingCall,
  startNativeOngoingCall,
  stopNativeOngoingCall,
  type NativePendingIncomingCall,
} from '../services/voice-call/native-call'
import { rekickHtml5OutgoingDialTone } from '../services/voice-call/html-ringback-rekick'
import { startLockScreenCallSync } from '../services/voice-call/lock-screen-call-sync'
import { LockedAnswerVideoGate } from '../features/calls/utils/locked-answer-video'
import {
  decideOnNativeConnectionDestroyed,
  endSourceFromNativeAction,
  isCallEndSource,
  isOutgoingDialStillLive,
  liveCallIdForNativeSweep,
  resolveTelecomPeerName,
  routeNativeDismiss,
  routeNativeEndAction,
  shouldAbortStaleOutgoingInvite,
  shouldEndInsteadOfKeepAlive,
  shouldEndLateAccept,
  ONE_TO_ONE_TELECOM_FALLBACK_NAME,
  telecomDisconnectReasonForEnd,
  type CallEndSource,
} from '../services/voice-call/telecom-connection-lifecycle'
import { showConsentCallEndedNotice } from '../stores/consent-call-ended-store'
import {
  consentNotRecordedNotice,
  consentWindowEndNotice,
  planConsentNotRecordedNotice,
  routeHeartbeatReply,
} from '../features/calls/utils/consent-window-end'
import { nativeAnswerAcceptOptions } from '../features/calls/utils/native-answer-accept-options'
import { roomEndParticipantIds } from '../features/group-call/utils/room-end-participants'
import {
  abortUnbuildableNativeAnswer,
  type NativeAnswerAbortReason,
} from '../features/calls/utils/native-answer-abort'
import {
  addNativeIosMediaListeners,
  getNativeIosMediaActiveCall,
  isNativeIosAudioEnabled,
  isNativeIosMediaAvailable,
  joinNativeIosMedia,
  leaveNativeIosMedia,
  setNativeIosMediaMuted,
  setNativeIosMediaSpeaker,
  syncNativeIosMediaEnabled,
} from '../services/voice-call/native-ios-media'
import { addCallKitAnsweredDeepLinkListener } from '../utils/native-call-deeplink'
import { recordCallFlow, setCallFlowCall } from '../debug/call-flow-log'

const RING_TIMEOUT_MS = 45_000
const HEARTBEAT_INTERVAL_MS = 20_000
const CONSENT_BACKSTOP_HEARTBEAT_DELAYS_MS = [
  CONSENT_WINDOW_MS + 1_500,
  CONSENT_WINDOW_MS + 6_000,
  CONSENT_WINDOW_MS + 12_000,
]
/** Allow three missed intervals before treating peer as gone (reduces false dropouts). */
const HEARTBEAT_MISS_MS = HEARTBEAT_INTERVAL_MS * 3
/** Chime may briefly stop during ICE renegotiation — wait before ending the call. */
const CHIME_DISCONNECT_GRACE_MS = 5_000
/**
 * 1:1 remote-left grace: the last remote attendee leaving is only *presumed*
 * to be a hangup. During a multi-device Switch the peer's OLD attendee leaves
 * moments before their NEW device's attendee appears — ending immediately
 * killed the call mid-Switch. A peer that really hung up normally also sends
 * END (handled instantly), so this grace only delays the no-signal fallback.
 */
const REMOTE_LEFT_GRACE_MS = 5_000
/**
 * Group calls: periodic in-room HEARTBEAT that refreshes the ROOM# row's
 * ttl/updatedAt server-side. Group calls run no 1:1 heartbeat loop (liveness
 * is Chime attendee presence), so without this keep-alive a long group call's
 * live roster row froze at invite time and could be reaped by the 6h TTL /
 * stale sweeper mid-call. Low frequency — this is a DB keep-alive, not a
 * liveness signal.
 */
const ROOM_KEEPALIVE_INTERVAL_MS = 10 * 60_000
/**
 * iOS-only: max bounded rejoins for a Chime session that terminated during the
 * INITIAL connect (before media ever started). On iOS the WKWebView signaling
 * WebSocket can be suspended/torn down at the CallKit-answer handoff, and the
 * SDK maps that to a TERMINAL SignalingBadRequest (code 11) it never retries —
 * a rejoin once the WebView network settles connects normally.
 */
const MAX_INITIAL_CONNECT_REJOINS = 2
/**
 * Chime MeetingSessionStatusCodes for an initial-connect failure that is
 * plausibly TRANSIENT (network/signaling not ready) and therefore worth a
 * bounded rejoin: audio/signaling server + socket errors, ICE timeout, generic
 * task failure. Deliberately EXCLUDES terminal "give up" causes like
 * MeetingEnded(5), AudioJoinedFromAnotherDevice(2), AuthRejected(3),
 * AtCapacity(4), TURNCredentialsForbidden(20), AudioAttendeeRemoved(22).
 */
const RETRYABLE_INITIAL_CONNECT_CODES = new Set([6, 7, 8, 11, 12, 13, 14, 15, 18])

/**
 * Resolve once the WebView is foregrounded (visible) — bounded by `maxMs` — then
 * wait a short beat for the network stack to settle. Used before an iOS rejoin:
 * the initial signaling socket failed precisely because the WebView network was
 * not ready at the CallKit handoff, so reopening it immediately would just fail
 * again.
 */
async function waitForWebViewForeground(maxMs: number): Promise<void> {
  if (typeof document === 'undefined') return
  const started = Date.now()
  while (document.visibilityState !== 'visible' && Date.now() - started < maxMs) {
    await new Promise((r) => setTimeout(r, 150))
  }
  await new Promise((r) => setTimeout(r, 400))
}

/**
 * True when a Chime JoinInfo response actually belongs to `callId`. The backend
 * sets the meeting's `ExternalMeetingId` to the callId (truncated to 64 chars,
 * see chime-meeting-token lambda), so this is the authoritative guard against
 * reusing a PRIOR call's JoinInfo — the deterministic stale-meeting bug where
 * the iOS callee joined a torn-down previous meeting and got a terminal
 * SignalingBadRequest (2026-07-08). A response with no ExternalMeetingId (older
 * backend) is trusted rather than blocked, since the check can't be performed.
 */
function chimeCredentialsMatchCall(
  credentials: ChimeJoinTokenResponse | null | undefined,
  callId: string,
): boolean {
  if (!credentials) return false
  const externalMeetingId = credentials.meeting?.Meeting?.ExternalMeetingId
  if (!externalMeetingId) return true
  return externalMeetingId === callId.slice(0, 64)
}
/** Decline a video-switch request that goes unanswered after this long. */
const MEDIA_SWITCH_TIMEOUT_MS = 15_000

/**
 * Ring-group label for the urgent leadership ring. Shown while dialling /
 * ringing instead of any individual member; the answerer's real name takes
 * over on ACCEPT. Deliberately NOT 'Care team' — that string is the banned
 * generic Telecom fallback (see telecom-connection-lifecycle.ts).
 */
const LEADERSHIP_TEAM_DISPLAY_NAME = 'Leadership team'
/** How long a transient overlay notice (toast) stays visible. */
const NOTICE_TTL_MS = 4_000
/** Long enough to read the full "not being recorded" sentence. */
const CONSENT_NOT_RECORDED_NOTICE_TTL_MS = 10_000

/**
 * One representCall per foreground transition per call: mount +
 * visibilitychange + 'resume' + deep link all fire within the same return to
 * the app (8× in ~200ms on build 108), and each re-run re-asserted the native
 * audio route. Repeats inside this window are dropped.
 */
const REPRESENT_CALL_COALESCE_MS = 1_000
/**
 * The persistent overlay notice shown while the rejoin/rehydrate flow tries to
 * re-establish an interrupted call. Unlike a transient toast it has no auto-clear
 * timer, so it MUST be cleared explicitly when the call reconnects OR reaches a
 * terminal state — otherwise it lingers as a stale "Reconnecting…" banner over
 * "Call ended". Referenced from both the module-level finishCall and the hook.
 */
const RECONNECTING_NOTICE = 'Reconnecting you to your call…'

/**
 * Shown on a 1:1 call that promoted to 'active' on LOCAL media up while the
 * remote attendee is not in the Chime roster yet (local-up ≠ two-way; Zach
 * talked alone for 22s on 2026-11-02 believing Olivia could hear). Cleared the
 * moment the remote attendee joins (onAdapterRemotePresent). Informational
 * only — it never changes the promote timing.
 */
const WAITING_FOR_PEER_NOTICE = 'Waiting for the other person\u2019s audio to connect…'

/** Shown when a pending mic/camera permission prompt is blocking call setup. */
const PERMISSION_PROMPT_NOTICE =
  'Waiting for microphone access — check the permission prompt to continue.'
/** Grace before surfacing the permission notice (instant grants stay silent). */
const PERMISSION_PROMPT_NOTICE_DELAY_MS = 1_500
/** Allow React to commit overlay media elements before Chime join (web). */
const MEDIA_ELEMENT_SETTLE_MS = 100

function groupMigrationWaitKey(callId: string, peerUserId: string): string {
  return `${callId}:${peerUserId}`
}

/** Hidden audio element when overlay mount lags accept/join (native cold start). */
function createFallbackAudioElement(): HTMLAudioElement {
  const el = document.createElement('audio')
  el.setAttribute('playsinline', 'true')
  el.setAttribute('aria-hidden', 'true')
  el.hidden = true
  document.body.appendChild(el)
  return el
}

function disposeFallbackAudioElement(el: HTMLAudioElement | null): void {
  if (!el) return
  try {
    el.pause()
    el.srcObject = null
    el.remove()
  } catch {
    /* already removed */
  }
}

/** Hidden video elements when VideoCallOverlay mount lags accept/join (native cold start). */
function createFallbackVideoElement(): HTMLVideoElement {
  const el = document.createElement('video')
  el.setAttribute('playsinline', 'true')
  el.setAttribute('autoplay', 'true')
  el.muted = true
  el.hidden = true
  document.body.appendChild(el)
  return el
}

function disposeFallbackVideoElement(el: HTMLVideoElement | null): void {
  if (!el) return
  try {
    el.pause()
    el.srcObject = null
    el.remove()
  } catch {
    /* already removed */
  }
}

type SubscriptionHandle = {
  unsubscribe: () => void
}

type AcceptCallOptions = {
  /**
   * Skip the in-app recording-consent modal. Set for answers performed in the
   * NATIVE call UI (iOS CallKit): iOS cannot foreground the app on answer, so
   * the modal would sit invisible while the caller keeps ringing — the callee
   * answered but nothing connected (the Sprint 8 "answer does nothing" bug).
   * Because no notice was shown, the ACCEPT must NOT claim consent: it carries
   * the bypassed marker instead, leaving the session without a consent record
   * until the notice is actually presented.
   */
  bypassRecordingConsent?: boolean
  /**
   * Skip the getUserMedia permission pre-flight before joining. Set for iOS
   * CallKit answers: the WebView may still be backgrounded when the accept
   * runs, where getUserMedia hangs or rejects even though permission is
   * granted — the pre-flight then mis-reported "denied" and TERMINATED the
   * call the user just answered. The Chime join itself still surfaces real
   * permission errors, and a video call degrades to audio-only + camera retry
   * on foreground instead of dying (see joinChimeMeeting videoOptional).
   */
  skipPermissionPreflight?: boolean
  /**
   * The recording-consent modal WAS displayed and the user pressed its
   * confirm button — set ONLY by RecordingConsentOverlay. This is what lets
   * an ACCEPT claim consent; it must never be inferred from call ordering.
   * Before this flag existed, "confirmed" was inferred from acceptCall being
   * re-entered while `recordingConsentPending` was set, so a double-tap on
   * the incoming-screen Accept button fabricated a MODAL_CONFIRMED consent
   * record (and the durable policy acceptance) for a notice the user never
   * saw — the 2026-10-01 "I did not get warned about audio recording" bug.
   */
  consentNoticeConfirmed?: boolean
}

type SubscriptionEvent = {
  data?: {
    onVoiceCallSignal?: VoiceCallSignal | null
    onCallRoomSignal?: VoiceCallSignal | null
  }
}

/** Locked product decision: a call (1:1 or group) is capped at 20 participants. */
const MAX_CALL_PARTICIPANTS = 20

function countActiveRemote(
  participants: CallParticipant[] | undefined,
  selfUserId: string | undefined,
): number {
  if (!participants) return 0
  return participants.filter((p) => p.userId !== selfUserId && p.state === 'active').length
}

function participantStateRank(state: CallParticipantState): number {
  switch (state) {
    case 'active':
      return 4
    case 'ringing':
      return 3
    case 'invited':
      return 2
    default:
      return 1 // left / declined
  }
}

function pickDisplayName(a?: string, b?: string): string {
  const an = a?.trim()
  if (an && an !== 'Participant') return an
  const bn = b?.trim()
  if (bn && bn !== 'Participant') return bn
  return an || bn || 'Participant'
}

/**
 * Collapse a roster to exactly one entry per userId, preferring the most "live"
 * data (active state, a real display name, presence/tile metadata). Defends
 * against phantom/duplicate roster entries — e.g. a placeholder created before a
 * name resolved, or the same person arriving from both an INVITE and the host's
 * ROSTER broadcast — that would otherwise render as an extra participant.
 */
function dedupeParticipants(participants: CallParticipant[]): CallParticipant[] {
  const byId = new Map<string, CallParticipant>()
  const order: string[] = []
  for (const p of participants) {
    if (!p.userId) continue
    const prev = byId.get(p.userId)
    if (!prev) {
      byId.set(p.userId, p)
      order.push(p.userId)
      continue
    }
    const better = participantStateRank(p.state) >= participantStateRank(prev.state) ? p : prev
    const worse = better === p ? prev : p
    byId.set(p.userId, {
      ...worse,
      ...better,
      displayName: pickDisplayName(better.displayName, worse.displayName),
      avatarUrl: better.avatarUrl ?? worse.avatarUrl,
      attendeeId: better.attendeeId ?? worse.attendeeId,
      tileId: better.tileId ?? worse.tileId,
      role: better.role === 'host' || worse.role === 'host' ? 'host' : better.role,
    })
  }
  return order.map((id) => byId.get(id) as CallParticipant)
}

/**
 * Drop any participant marked 'active' who is NOT actually present in the Chime
 * meeting (a ghost / stale entry), reconciling the roster against Chime's
 * authoritative attendee list. Never touches self, pending invitees
 * (ringing/invited), or already-left/declined entries.
 */
function reconcileActiveAgainstPresence(
  participants: CallParticipant[],
  presentIds: Set<string>,
  selfUserId: string | undefined,
): CallParticipant[] {
  return participants.filter((p) => {
    if (p.userId === selfUserId) return true
    if (p.state !== 'active') return true
    return presentIds.has(p.userId)
  })
}

function rostersEqual(a: CallParticipant[], b: CallParticipant[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]
    const y = b[i]
    if (
      x.userId !== y.userId ||
      x.state !== y.state ||
      x.role !== y.role ||
      x.tileId !== y.tileId ||
      x.attendeeId !== y.attendeeId ||
      x.displayName !== y.displayName ||
      x.mutedSelf !== y.mutedSelf ||
      x.speaking !== y.speaking ||
      x.avatarUrl !== y.avatarUrl
    ) {
      return false
    }
  }
  return true
}

function upsertGroupParticipant(
  userId: string,
  patch: Partial<CallParticipant>,
): void {
  const session = getVoiceCallSession()
  if (!session?.isGroup) return
  if (!userId) return
  const list = session.participants ?? []
  const idx = list.findIndex((p) => p.userId === userId)
  let next: CallParticipant[]
  if (idx === -1) {
    const created: CallParticipant = {
      userId,
      displayName: patch.displayName ?? 'Participant',
      avatarUrl: patch.avatarUrl,
      role: patch.role ?? 'guest',
      state: patch.state ?? 'invited',
      ...patch,
    }
    next = [...list, created]
  } else {
    next = list.map((p, i) => (i === idx ? { ...p, ...patch } : p))
  }
  const deduped = dedupeParticipants(next)
  if (!rostersEqual(list, deduped)) {
    patchVoiceCallSession({ participants: deduped })
  }
}

function findParticipantByTile(tileId: number): CallParticipant | undefined {
  return getVoiceCallSession()?.participants?.find((p) => p.tileId === tileId)
}

type VoiceCallSubscription = {
  subscribe: (handlers: {
    next: (event: SubscriptionEvent) => void
    error?: (err: unknown) => void
  }) => SubscriptionHandle
}

function createCallId(): string {
  return crypto.randomUUID()
}

function peerFromUser(user: ChatUser): VoiceCallPeer {
  return {
    userId: user.userId,
    displayName: user.displayName,
    avatarUrl: user.avatarUrl,
  }
}

/** The cached chat profile, only when it is the controller's signed-in user. */
function cachedSelfChatUser(currentUserId: string | undefined): ChatUser | undefined {
  const me = getCachedChatUser()
  return currentUserId && me?.userId === currentUserId ? me : undefined
}

function localAttendeeDisplayName(currentUserId: string | undefined): string | undefined {
  const name = cachedSelfChatUser(currentUserId)?.displayName.trim()
  return name || undefined
}

async function resolvePeerByUserId(
  userId: string,
  hint?: { displayName?: string; avatarUrl?: string | null },
): Promise<VoiceCallPeer> {
  let displayName = hint?.displayName?.trim()
  let avatarUrl = hint?.avatarUrl ?? undefined

  const cached = getCachedDirectoryUser(userId)
  if (!displayName && cached?.displayName?.trim()) {
    displayName = cached.displayName.trim()
    avatarUrl = cached.avatarUrl ?? avatarUrl
  }

  if (!displayName) {
    try {
      const user = await resolveDirectoryUser(userId)
      if (user?.displayName?.trim()) {
        displayName = user.displayName.trim()
        avatarUrl = user.avatarUrl ?? avatarUrl
      }
    } catch {
      /* roster lookup is best-effort */
    }
  }

  return {
    userId,
    displayName: displayName || 'Unknown',
    avatarUrl,
  }
}

async function resolveIncomingCallerPeer(
  callerId: string,
  calleeId: string,
  currentUserId: string | undefined,
  payload: string | Record<string, unknown> | null | undefined,
): Promise<VoiceCallPeer> {
  const invite = extractCallerInvitePayload(payload)
  let displayName = invite?.callerDisplayName?.trim()
  let avatarUrl = invite?.callerAvatarUrl ?? undefined
  // A generic fallback baked in by the DIALING client ('Someone' — e.g. a
  // car-originated dial that carries no caller profile) is NOT a resolved
  // identity: treat it as missing so the directory cache/lookup below can
  // supply the real name (2026-10-07 "Someone is calling you" field bug).
  const payloadFallbackName = displayName
  if (isPlaceholderCallerName(displayName)) displayName = undefined

  const cached = getCachedDirectoryUser(callerId)
  if (!displayName && cached?.displayName?.trim()) {
    displayName = cached.displayName.trim()
    avatarUrl = cached.avatarUrl ?? avatarUrl
  }

  if (!displayName) {
    try {
      const caller = await resolveDirectoryUser(callerId)
      if (caller?.displayName?.trim()) {
        displayName = caller.displayName.trim()
        avatarUrl = caller.avatarUrl ?? avatarUrl
      }
    } catch {
      /* roster lookup is best-effort */
    }
  }

  const me = getCachedChatUser()
  if (
    currentUserId &&
    me?.userId === currentUserId &&
    displayName &&
    displayName.trim().toLowerCase() === me.displayName.trim().toLowerCase()
  ) {
    displayName = cached?.displayName?.trim() || undefined
    if (!displayName || displayName.trim().toLowerCase() === me.displayName.trim().toLowerCase()) {
      try {
        const caller = await resolveDirectoryUser(callerId)
        if (caller?.displayName?.trim()) {
          displayName = caller.displayName.trim()
          avatarUrl = caller.avatarUrl ?? avatarUrl
        }
      } catch {
        /* roster lookup is best-effort */
      }
    }
  }

  if (callerId === calleeId || (currentUserId && callerId === currentUserId)) {
    throw new Error('Invalid incoming call signal')
  }

  return {
    userId: callerId,
    // True last resort: the payload's own generic label, then 'Unknown caller'.
    displayName: displayName || payloadFallbackName || 'Unknown caller',
    avatarUrl,
  }
}

/**
 * Late repair for a live session whose peer label is still a generic
 * placeholder ('Someone' / 'Unknown caller' / 'Call in progress'): retry the
 * directory lookup and patch the store in place, so everything that reads
 * session.peer.displayName — the return-to-call pill, the call overlay, the
 * ongoing-call notification on re-foreground — upgrades to the real name
 * mid-call (2026-10-07 field bug: the in-app green bar showed "Someone" for
 * the whole answered call because the name was only ever resolved at ring
 * time). Fire-and-forget: no-ops when the session changed underneath or the
 * lookup still has nothing better to offer.
 */
function upgradePlaceholderPeerName(callId: string): void {
  const session = getVoiceCallSession()
  if (!session || session.callId !== callId || session.isGroup) return
  if (!isPlaceholderCallerName(session.peer.displayName)) return
  const peerId = session.peer.userId
  if (!peerId || peerId === 'unknown') return
  void resolveDirectoryUser(peerId)
    .then((user) => {
      const name = user?.displayName?.trim()
      if (!name || isPlaceholderCallerName(name)) return
      const live = getVoiceCallSession()
      if (live?.callId !== callId || live.peer.userId !== peerId) return
      if (!isPlaceholderCallerName(live.peer.displayName)) return
      patchVoiceCallSession({
        peer: {
          ...live.peer,
          displayName: name,
          avatarUrl: user?.avatarUrl ?? live.peer.avatarUrl,
        },
      })
    })
    .catch(() => {
      /* directory lookup is best-effort — keep the placeholder */
    })
}

function recordingConsentRolesForSession(
  session: Pick<VoiceCallSession, 'callerId'>,
): { local: { roleType?: string | null }; remote: { roleType?: string | null } | null } {
  const me = getCachedChatUser()
  const remote = getCachedDirectoryUser(session.callerId)
  return {
    local: { roleType: me?.roleType ?? null },
    remote: remote ? { roleType: remote.roleType ?? null } : null,
  }
}

/**
 * Same roles as {@link recordingConsentRolesForSession}, but fetched when the
 * in-memory caches are cold (cold start / process woken by a car answer) — the
 * consent decision on the rejoin path must not guess the local role.
 */
async function loadRecordingConsentRoles(
  callerId: string,
): Promise<{ local: { roleType?: string | null }; remote: { roleType?: string | null } | null }> {
  const [local, remote] = await Promise.all([
    loadLocalRecordingConsentRole(),
    getCachedDirectoryUser(callerId) ?? resolveDirectoryUser(callerId).catch(() => undefined),
  ])
  return { local, remote: remote ? { roleType: remote.roleType ?? null } : null }
}

async function loadLocalRecordingConsentRole(): Promise<{ roleType?: string | null }> {
  const me = getCachedChatUser() ?? (await getCurrentChatUser().catch(() => undefined))
  return { roleType: me?.roleType ?? null }
}

/**
 * Whether a 1:1 meeting create (dial-time or ring-time prewarm) must ask the
 * server to DEFER recording/transcription start until the callee's recording
 * consent resolves. Provider-to-provider calls never owe the notice, so they
 * keep capture-at-create; unknown roles defer pessimistically — capture then
 * starts server-side on the consent-carrying ACCEPT/CONSENT signal.
 */
function shouldDeferRecordingCapture(
  remote: { roleType?: string | null } | null | undefined,
): boolean {
  const me = getCachedChatUser()
  return requiresRecordingConsent({ roleType: me?.roleType ?? null }, remote ?? null)
}

/**
 * Whether an OUTGOING call must be intercepted by the pre-dial recording-policy
 * gate: the local user is a Member/Independent (same cached role source the
 * incoming consent modal uses) without a durable recording-policy ACCEPTED.
 * The gate dialog re-checks the freshly-loaded status and auto-proceeds when
 * the user turns out to have already accepted.
 */
function needsOutgoingRecordingPolicyGate(): boolean {
  const me = getCachedChatUser()
  if (!requiresRecordingPolicyAcceptance({ roleType: me?.roleType ?? null })) return false
  const consent = getRecordingPolicyConsent()
  if (consent.loaded) return !hasAcceptedCurrentRecordingPolicy(consent)
  // Server answer not in yet: trust this account's last server-confirmed answer
  // rather than flashing the gate; the gate dialog re-checks the server anyway.
  return !hasAcceptedCurrentRecordingPolicy(readCachedRecordingPolicyConsent(me?.userId))
}

function endPayloadReasonFor(
  reason: VoiceCallEndReason,
  phase: VoiceCallPhase,
): string {
  if (reason === 'completed') return 'COMPLETED'
  if (reason === 'missed') return 'MISSED'
  if (reason === 'declined') return 'DECLINED'
  if (reason === 'cancelled') return 'CANCELLED'
  if (reason === 'interrupted') return 'INTERRUPTED'
  if (reason === 'busy') return 'BUSY'
  if (reason === 'consent-missing') return RECORDING_CONSENT_MISSING_END_REASON
  return phase === 'active' ? 'INTERRUPTED' : 'CANCELLED'
}

function shouldAbortJoin(callId: string): boolean {
  const live = getVoiceCallSession()
  return !live || live.callId !== callId || isTerminalCallPhase(live.phase)
}

/** Dual-account / stale-token ack: server refused because this Cognito user is not on the call. */
function ignoreForeignCallAck(
  err: unknown,
  callId: string | null,
  signalType: string,
): boolean {
  if (!isNotParticipantError(err)) return false
  recordCallFlow('ctrl:ack:notParticipant', { signalType }, { callId })
  return true
}

/** Stop ringing UI on this device even when there is no (or a stale) JS session. */
function dismissIncomingRingSurface(callId: string): void {
  clearCallNotification(callId)
  if (isNativeApp()) void setNativeCallDisconnected(callId, 'canceled')
}

function endReasonFromTerminalSignal(
  type: VoiceCallSignal['type'],
  payload: string | Record<string, unknown> | null | undefined,
): VoiceCallEndReason {
  if (type === 'BUSY') return 'busy'
  if (type === 'REJECT') return 'declined'
  const reason = (parseSignalPayload<{ reason?: string }>(payload)?.reason ?? '').toUpperCase()
  if (reason === 'MISSED') return 'missed'
  if (reason === 'BUSY') return 'busy'
  if (reason === 'DECLINED') return 'declined'
  if (reason === 'INTERRUPTED') return 'interrupted'
  return 'cancelled'
}

/** Tell our other signed-in devices to stop ringing (pure relay — no DB writes). */
function relayTerminalToSiblingDevices(
  signal: Pick<
    VoiceCallSignal,
    'callId' | 'type' | 'callerId' | 'calleeId' | 'conversationId' | 'payload'
  >,
  siblingUserId: string,
): void {
  // Belt to the isSiblingRelaySignal suspenders: whatever the trigger, never
  // relay the same signal type for the same call twice from this device
  // (2026-08-17 relay-storm fix — see voice-call-sibling-relay.ts).
  if (!claimSiblingRelayOnce(signal.callId, signal.type)) return
  void publishVoiceCallSignal({
    callId: signal.callId,
    type: signal.type,
    callerId: signal.callerId,
    calleeId: signal.calleeId,
    conversationId: signal.conversationId,
    payload: signal.payload,
    recipientUserId: siblingUserId,
  }).catch(() => {})
}

/** Clears timers/grace registered by the active controller instance. */
let callLifecycleCleanup: (() => void) | null = null

/** `ctrl:hangUp` telemetry: every user/system end carries where it came from. */
function recordCallEndSource(
  callId: string | null | undefined,
  endSource: CallEndSource,
  phase: VoiceCallPhase | null | undefined,
  extra?: Record<string, unknown>,
): void {
  recordCallFlow('ctrl:hangUp', { endSource, phase: phase ?? null, ...extra }, { callId: callId ?? null })
}

function finishCall(
  reason: VoiceCallEndReason,
  errorMessage?: string,
  disconnectDetail?: string,
  endCause?: VoiceCallEndCause,
): void {
  callLifecycleCleanup?.()
  const session = getVoiceCallSession()
  if (!session || isTerminalCallPhase(session.phase)) return
  // User-ended tombstone: rehydrate/auto-join must NEVER resurrect this call,
  // even if the backend SESSION row outlives it (peer's END lost, sweeper
  // pending). This is the primary defence against the post-hangup reconnect
  // loop; the backend freshness window is the backstop.
  if (session.callId) recordCallEndedTombstone(session.callId, reason)
  const peerUserId =
    session.localRole === 'caller' ? session.calleeId : session.callerId
  if (peerUserId && (reason === 'declined' || reason === 'busy')) {
    noteRemoteCallRejected(peerUserId, reason)
  }
  patchVoiceCallSession({
    phase: 'ended',
    endReason: reason,
    endCause,
    errorMessage,
    disconnectDetail,
    endedAt: Date.now(),
    // Drop the OS-ring flag so the next call's ringtone effect cannot inherit
    // a stale "native ring is playing" and skip WebView dial-tone (bug 1b).
    osRingActive: false,
    // A terminal transition supersedes any in-flight rejoin/rehydrate: clear the
    // "Reconnecting…" flag AND the persistent reconnecting notice so the overlay
    // can never show a stale "Reconnecting you to your call…" banner over
    // "Call ended". (This is the single terminal-transition point for every end
    // reason — completed, declined, missed, cancelled, interrupted, error.)
    reconnecting: false,
    userMinimized: false,
    ...(session.notice === RECONNECTING_NOTICE ? { notice: undefined } : {}),
  })
  // Bust the call-history cache so the Calls hub reflects this just-ended call
  // on the next read instead of waiting for the short TTL to lapse.
  invalidateCallHistoryCache()
  // Tell the OS the (self-managed Telecom) call ended so the status-bar call
  // chip / ringing call clears. Maps the end reason to a Telecom DisconnectCause.
  // iOS: only map `cancelled` → `canceled` while still RINGING (incoming). After
  // CallKit answer, `canceled` is treated as a spurious ring-dismiss and can be
  // suppressed during deferred handoff — use an unreasoned disconnect so intentional
  // hang-up while connecting still tears CallKit down.
  resetNativeAudioRoute()
  if (isNativeApp() && session.callId) {
    const telecomReason = telecomDisconnectReasonForEnd(
      reason,
      session.phase,
      getNativePlatform(),
    )
    void setNativeCallDisconnected(session.callId, telecomReason, session.carTapAt)
    // Hangup of a known callId cannot address a Connection Telecom created
    // without one (Pixel extras-stripped outgoing). Sweep after disconnect.
    void sweepNativeTelecomConnections(null)
  }
  // Calls started from a chat persist an inline SYSTEM message server-side;
  // that write doesn't fire onMessageReceived, so nudge the open thread.
  if (session?.conversationId) {
    window.dispatchEvent(
      new CustomEvent('ascend:call-thread-updated', {
        detail: { conversationId: session.conversationId },
      }),
    )
  }
  // The terminal call-history row is written server-side when the END/REJECT
  // signal lands. Nudge the activity feed so the completed/missed/declined call
  // shows up promptly instead of waiting out the 30s poll.
  window.dispatchEvent(new CustomEvent('ascend:call-ended'))
  recordCallFlow('ctrl:callEnded', { reason: reason ?? null }, { callId: session?.callId ?? null })
  setCallFlowCall(null)
  // Guard the deferred sweep by callId + phase: a NEW call can start during
  // these 4s (fast redial), and the unguarded timer used to wipe its session.
  const endedCallId = session.callId
  setTimeout(() => {
    if (shouldClearEndedSession(getVoiceCallSession(), endedCallId)) {
      setVoiceCallSession(null)
    }
  }, 4000)
}

export function useVoiceCallController(currentUserId: string | undefined) {
  // A single video-capable adapter powers BOTH voice and video calls. Voice
  // calls join audio-only (camera never started) so they can be upgraded to
  // video in place — no dispose / re-join — which is what makes voice↔video
  // switching reliable instead of dropping the call.
  const callAdapterRef = useRef<ChimeCallAdapter | null>(null)
  const ringTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Auto-decline timeout for a second (call-waiting) incoming call. */
  const waitingRingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const heartbeatWatchRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const consentBackstopTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const handledSignalsRef = useRef<Set<string>>(new Set())
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null)
  const fallbackAudioRef = useRef<HTMLAudioElement | null>(null)
  const fallbackLocalVideoRef = useRef<HTMLVideoElement | null>(null)
  const fallbackRemoteVideoRef = useRef<HTMLVideoElement | null>(null)
  const localVideoRef = useRef<HTMLVideoElement | null>(null)
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null)
  const contentVideoRef = useRef<HTMLVideoElement | null>(null)
  const holdMusicPlayerRef = useRef(new HoldMusicPlayer())
  const joiningRef = useRef(false)
  /**
   * Call id this device's JS adapter has ALREADY joined. A duplicate join
   * trigger for the same call (the callee's app-level ACCEPT arriving after
   * the native-ack lambda's ACCEPT already joined us, the connect-stall
   * watchdog, a rehydrate) must be ignored: Chime treats a second connection
   * for the same attendee as another device and KICKS the first one
   * (`kicked-other-device` mid-connecting on the 2026-08-20 build-104 car
   * calls — an audible dropout + SCO bounce). Cleared in disposeAdapter.
   */
  const joinedChimeCallIdRef = useRef<string | null>(null)
  /**
   * Set to the callId whose AUDIO is running through the NATIVE iOS Chime SDK
   * (ChimeMediaController) rather than the JS ChimeCallAdapter. When set, the
   * controller routes mute/speaker/teardown to the native plugin and maps the
   * native lifecycle events (connected/failed/roster/remoteLeft) onto the same
   * voice-call session state the overlay consumes. iOS VOICE calls only.
   */
  const nativeMediaActiveRef = useRef<string | null>(null)
  /**
   * The callId this device is answering (or has answered) LOCALLY — stamped the
   * moment an answer reaches JS, BEFORE any async work. A locked/background
   * CallKit answer relays its ACCEPT through the `voice-call-native-ack` lambda,
   * which fans it back to every device of the callee (including this one) with
   * `senderId` === our own user id, so it looks exactly like a sibling-device
   * answer. Acting on that echo disposed the freshly-joined native media session
   * (web→iOS locked "stuck on Connecting…"). This device-local marker lets the
   * sibling-signal path recognise our OWN answer and leave the call intact.
   * Overwritten by the next local answer (one answered call per device at a
   * time) and cleared on teardown.
   */
  const answeredHereCallIdRef = useRef<string | null>(null)
  /** Last native (Telecom/CallKit) answer — shields it from the orphan sweep. */
  const nativeAnswerRef = useRef<{ callId: string; atMs: number } | null>(null)
  /** Android locked-keyguard answer: video waits for the unlock. */
  const lockedVideoGateRef = useRef(new LockedAnswerVideoGate())
  /** Lock-screen route pick that arrived before its session was rebuilt (cold WebView). */
  const queuedLockScreenPickRef = useRef<{
    callId: string
    output: 'speaker' | 'earpiece' | 'external'
  } | null>(null)
  const nativeRebuildFailuresRef = useRef(
    new Map<string, { invite: NativePendingIncomingCall; reason: NativeAnswerAbortReason }>(),
  )
  /** Join token requested while the callee is still ringing — speeds up connect. */
  const prewarmedJoinRef = useRef<{
    callId: string
    promise: Promise<ChimeJoinTokenResponse | null>
  } | null>(null)
  const disconnectGraceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** 1:1 remote-left grace — see REMOTE_LEFT_GRACE_MS. */
  const remoteLeftGraceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Auto-decline timer for an outgoing video-switch request. */
  const mediaSwitchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Auto-clear timer for the transient overlay notice. */
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastRecallPeerRef = useRef<ChatUser | null>(null)
  const lastRecallConversationRef = useRef<string | null>(null)
  const lastRecallMediaRef = useRef<CallMediaType>('voice')
  const terminateCallRef = useRef<
    (
      reason: VoiceCallEndReason,
      errorMessage?: string,
      disconnectDetail?: string,
      options?: { notifyPeer?: boolean; endPayloadReason?: string; endCause?: VoiceCallEndCause },
    ) => Promise<void>
  >(() => Promise.resolve())
  /** The car-originated dial in flight (a car End before its callId cancels it). */
  const carDialRef = useRef<{ callId: string; tapAt: number } | null>(null)
  /** Set below once hangUp exists (keep-alive refusal + native teardown paths). */
  const hangUpRef = useRef<(options?: { endSource?: CallEndSource }) => Promise<void>>(() =>
    Promise.resolve(),
  )
  /** Set below; called when a peer (or this device) upgrades the call to video. */
  const performVideoSwapRef = useRef<() => Promise<void>>(() => Promise.resolve())
  /** Set below; called when a peer (or this device) downgrades the call to voice. */
  const performVoiceSwapRef = useRef<() => Promise<void>>(() => Promise.resolve())
  /** Set below; publishes this device's car-mode driving state to the peer. */
  const announceCarModeRef = useRef<(driving: boolean) => void>(() => {})
  /** Set below; handles the peer's car-mode driving state (MEDIA_SWITCH relay). */
  const handleCarModeSignalRef = useRef<(driving: boolean) => void>(() => {})
  /** Set below; handles an incoming MEDIA_SWITCH signal from the peer. */
  const handleMediaSwitchRef = useRef<(payload: MediaSwitchPayload) => void>(() => {})

  // --- Multi-party (group call) state ---
  /** Simultaneous leadership urgent ring — multiple INVITEs, first ACCEPT wins. */
  const leadershipRingRef = useRef<{
    invites: Array<{ callId: string; peerUserId: string; peer: VoiceCallPeer }>
    responded: Set<string>
    conversationId?: string | null
  } | null>(null)
  const roomSubRef = useRef<SubscriptionHandle | null>(null)
  const roomSubCallIdRef = useRef<string | null>(null)
  /** Periodic ROOM# row keep-alive while in a group call (see subscribeRoom). */
  const roomKeepAliveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /** Debounce timer for reconciling the roster against Chime attendee presence. */
  const rosterReconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Resolves when a peer announces PARTICIPANT_JOIN after upgrade_group. */
  const groupMigrationWaitersRef = useRef<
    Map<string, { resolve: () => void; timer: ReturnType<typeof setTimeout> }>
  >(new Map())
  /** Set below; leaves the room for THIS device (PARTICIPANT_LEFT, no room END). */
  const leaveRoomRef = useRef<() => Promise<void>>(() => Promise.resolve())
  const migrateToGroupModeRef =
    useRef<(asHost: boolean, hostIdOverride?: string) => Promise<void>>(() => Promise.resolve())
  const handleTransferSignalRef =
    useRef<(payload: TransferSignalPayload, senderId: string) => Promise<void>>(() => Promise.resolve())
  const completeBlindTransferRef = useRef<(target: ChatUser) => Promise<void>>(() => Promise.resolve())
  const acceptCallRef = useRef<(options?: AcceptCallOptions) => Promise<void>>(() =>
    Promise.resolve(),
  )
  /** Set below; re-entrant start used by the recording-policy gate proceed. */
  const startLeadershipUrgentRingRef = useRef<
    | ((
        peers: ChatUser[],
        conversationId?: string | null,
        caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
        options?: StartCallOptions,
      ) => Promise<void>)
    | null
  >(null)
  /** Set below; re-entrant start used by the status-gate "Call anyway" proceed. */
  const startCallWithMediaRef = useRef<
    (
      peer: ChatUser,
      mediaType: CallMediaType,
      conversationId?: string | null,
      caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
      options?: StartCallOptions,
    ) => Promise<void>
  >(() => Promise.resolve())
  /** Set below; re-entrant group start used by the recording-consent prompt's proceed. */
  const startGroupCallRef = useRef<
    | ((
        members: ChatUser[],
        mediaType: CallMediaType,
        conversationId?: string | null,
        groupTitle?: string,
        caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
        options?: Pick<StartCallOptions, 'bypassRecordingPolicyGate' | 'recordingConsentRetried'>,
      ) => Promise<void>)
    | null
  >(null)
  /** Carried from startGroupCall into the video-lobby confirm → dial path. */
  const groupCallStartOptionsRef = useRef<
    Pick<StartCallOptions, 'recordingConsentRetried'>
  >({})
  /** Set below; joins a multi-party meeting (audio-only; camera on demand). */
  const joinGroupMeetingRef = useRef<
    (callId: string, displayName?: string, mediaType?: CallMediaType) => Promise<void>
  >(() => Promise.resolve())
  /** Set below; handles a Chime initial-connect failure (stale-closure safe). */
  const handleInitialConnectFailedRef = useRef<(code: number) => void>(() => {})
  /** Per-call bounded-rejoin counter for initial-connect failures (iOS). */
  const initialConnectRetryRef = useRef<{ callId: string; attempts: number }>({
    callId: '',
    attempts: 0,
  })
  /** Set below; routes adapter group callbacks (stale-closure safe). */
  const handleRosterPresenceRef = useRef<
    (attendee: { attendeeId: string; externalUserId: string }, present: boolean) => void
  >(() => {})
  const handleAttendeeAudioRef = useRef<
    (
      attendee: { attendeeId: string; externalUserId: string },
      state: { muted: boolean; speaking: boolean },
    ) => void
  >(() => {})
  const handleRemoteTileAddedRef = useRef<
    (tile: { tileId: number; externalUserId: string }) => void
  >(() => {})
  const handleRemoteTileRemovedRef = useRef<(tileId: number) => void>(() => {})
  const handleVideoSendUnavailableRef = useRef<() => void>(() => {})
  /**
   * iOS Q3C: after a sibling DEVICE_CLAIM takeover, keep CallKit up until the
   * new device reports media_started, then end with .answeredElsewhere.
   */
  const pendingCallKitHandoffRef = useRef<string | null>(null)
  /** True while Switch is claiming + joining on this device. */
  const switchingDeviceRef = useRef(false)

  const clearRingTimer = useCallback(() => {
    if (ringTimerRef.current) {
      clearTimeout(ringTimerRef.current)
      ringTimerRef.current = null
    }
  }, [])

  const clearWaitingRingTimer = useCallback(() => {
    if (waitingRingTimerRef.current) {
      clearTimeout(waitingRingTimerRef.current)
      waitingRingTimerRef.current = null
    }
  }, [])

  /**
   * Send the auto-decline/decline terminal signal for a pending call-waiting
   * invite and clear it. Used on explicit decline, ring timeout, and whenever
   * the main call tears down before the user acted on the waiting call.
   */
  const dropWaitingCall = useCallback(
    (reason: 'BUSY' | 'MISSED' = 'BUSY') => {
      clearWaitingRingTimer()
      const waiting = getWaitingCall()
      if (!waiting) return
      clearWaitingCall()
      if (reason === 'MISSED') {
        void publishVoiceCallSignal({
          callId: waiting.callId,
          type: 'END',
          callerId: waiting.callerId,
          calleeId: waiting.calleeId,
          conversationId: waiting.conversationId,
          payload: buildCallEndPayload('MISSED', waiting.startedAt),
        }).catch(() => {})
        return
      }
      void publishVoiceCallSignal({
        callId: waiting.callId,
        type: 'BUSY',
        callerId: waiting.callerId,
        calleeId: waiting.calleeId,
        conversationId: waiting.conversationId,
      }).catch(() => {})
    },
    [clearWaitingRingTimer],
  )

  const resolvePeerGroupMigration = useCallback((callId: string, peerUserId: string) => {
    groupMigrationWaitersRef.current.get(groupMigrationWaitKey(callId, peerUserId))?.resolve()
  }, [])

  const waitForPeerGroupMigration = useCallback(
    (callId: string, peerUserId: string, timeoutMs = 8000): Promise<boolean> =>
      new Promise((resolve) => {
        const key = groupMigrationWaitKey(callId, peerUserId)
        const existing = groupMigrationWaitersRef.current.get(key)
        if (existing) clearTimeout(existing.timer)
        const timer = setTimeout(() => {
          groupMigrationWaitersRef.current.delete(key)
          resolve(false)
        }, timeoutMs)
        groupMigrationWaitersRef.current.set(key, {
          timer,
          resolve: () => {
            clearTimeout(timer)
            groupMigrationWaitersRef.current.delete(key)
            resolve(true)
          },
        })
      }),
    [],
  )

  const clearHeartbeatTimers = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current)
      heartbeatTimerRef.current = null
    }
    if (heartbeatWatchRef.current) {
      clearInterval(heartbeatWatchRef.current)
      heartbeatWatchRef.current = null
    }
    for (const timer of consentBackstopTimersRef.current) clearTimeout(timer)
    consentBackstopTimersRef.current = []
  }, [])

  const clearDisconnectGrace = useCallback(() => {
    if (disconnectGraceRef.current) {
      clearTimeout(disconnectGraceRef.current)
      disconnectGraceRef.current = null
    }
  }, [])

  const clearRemoteLeftGrace = useCallback(() => {
    if (remoteLeftGraceRef.current) {
      clearTimeout(remoteLeftGraceRef.current)
      remoteLeftGraceRef.current = null
    }
  }, [])

  const clearMediaSwitchTimer = useCallback(() => {
    if (mediaSwitchTimerRef.current) {
      clearTimeout(mediaSwitchTimerRef.current)
      mediaSwitchTimerRef.current = null
    }
  }, [])

  /** Show a transient toast-style notice in the call overlay, auto-clearing. */
  const showNotice = useCallback((notice: string, ttlMs: number = NOTICE_TTL_MS) => {
    patchVoiceCallSession({ notice })
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
    noticeTimerRef.current = setTimeout(() => {
      noticeTimerRef.current = null
      const current = getVoiceCallSession()
      if (current?.notice === notice) patchVoiceCallSession({ notice: undefined })
    }, ttlMs)
  }, [])

  /**
   * ensureCallPermissionsForCall, but VISIBLE when the browser permission
   * prompt is what's blocking. On web, getUserMedia does not resolve until the
   * user acts on the prompt — during that time an outgoing call sits in
   * `outgoing` playing ringback with the INVITE never sent (the peer is not
   * being rung at all), and an answered call hangs before mic start. Both
   * looked like dead calls in the 2026-08-18 test session (Ania's first two
   * calls on a brand-new profile). Surface a notice after a short delay so the
   * user finds the prompt instead of waiting on a phantom ring.
   */
  const ensurePermissionsWithPromptNotice = useCallback(
    async (callId: string, options?: { video?: boolean }) => {
      const nudgeTimer = window.setTimeout(() => {
        const live = getVoiceCallSession()
        if (live?.callId !== callId || isTerminalCallPhase(live.phase)) return
        recordCallFlow(
          'ctrl:permissions:promptPending',
          { video: Boolean(options?.video) },
          { callId },
        )
        showNotice(PERMISSION_PROMPT_NOTICE)
      }, PERMISSION_PROMPT_NOTICE_DELAY_MS)
      try {
        return await ensureCallPermissionsForCall(options)
      } finally {
        window.clearTimeout(nudgeTimer)
        const live = getVoiceCallSession()
        if (live?.callId === callId && live.notice === PERMISSION_PROMPT_NOTICE) {
          patchVoiceCallSession({ notice: undefined })
        }
      }
    },
    [showNotice],
  )

  callLifecycleCleanup = useCallback(() => {
    clearRingTimer()
    clearHeartbeatTimers()
    clearDisconnectGrace()
    clearRemoteLeftGrace()
    clearMediaSwitchTimer()
  }, [clearDisconnectGrace, clearHeartbeatTimers, clearMediaSwitchTimer, clearRemoteLeftGrace, clearRingTimer])

  const shouldAcceptAdapterSessionEvent = useCallback(() => {
    const session = getVoiceCallSession()
    return Boolean(session && !isTerminalCallPhase(session.phase))
  }, [])

  const scheduleDisconnectTerminate = useCallback(() => {
    if (!shouldAcceptAdapterSessionEvent()) return
    const session = getVoiceCallSession()
    if (!session || (session.phase !== 'active' && session.phase !== 'connecting')) return
    clearDisconnectGrace()
    disconnectGraceRef.current = setTimeout(() => {
      disconnectGraceRef.current = null
      const current = getVoiceCallSession()
      if (
        !current ||
        isTerminalCallPhase(current.phase) ||
        (current.phase !== 'active' && current.phase !== 'connecting')
      ) {
        return
      }
      void terminateCallRef.current(
        'interrupted',
        'Connection interrupted',
        'Connection interrupted',
        { endPayloadReason: 'INTERRUPTED' },
      )
    }, CHIME_DISCONNECT_GRACE_MS)
  }, [clearDisconnectGrace, shouldAcceptAdapterSessionEvent])

  // Keep the call alive as soon as LOCAL media is up: without an ongoing-call
  // foreground service + wake locks, Android pauses the WebView + cuts the mic
  // on screen-off, dropping the call. `callId` also lets iOS anchor the call in
  // CallKit (system green pill + background audio) and moves the self-managed
  // Telecom connection to active so the OS shows the status-bar call chip.
  // Safe to call more than once.
  const startCallKeepAlive = useCallback(() => {
    if (!isNativeApp()) return
    const live = getVoiceCallSession()
    if (!live || isTerminalCallPhase(live.phase)) return
    const callId = live.callId
    const isGroup = Boolean(live.isGroup)
    // NEVER RESURRECT: a call this device already ended (car / Telecom hang-up
    // the WebView heard late) is not re-anchored — END it instead.
    if (shouldEndInsteadOfKeepAlive({ callId, isGroup, isTombstoned: isCallTombstoned })) {
      recordCallFlow('ctrl:keepAlive:refused', { reason: 'tombstoned', by: 'js' }, { callId })
      void hangUpRef.current({ endSource: 'native-teardown' })
      return
    }
    const peerName = resolveTelecomPeerName({
      displayName: live.peer?.displayName,
      isGroup: Boolean(live.isGroup),
      directoryName: live.peer?.userId
        ? getCachedDirectoryUser(live.peer.userId)?.displayName
        : null,
      isPlaceholder: isPlaceholderCallerName,
    })
    void startNativeOngoingCall({
      video: live.mediaType === 'video',
      peerName,
      callId,
      peerId: live.peer?.userId,
      group: isGroup,
      // Answered/connecting call — represent as ACTIVE in Telecom. Never
      // `dialing`: that requested system ringback on the answered side
      // (callee dial-tone-after-answer, 2026-09-16).
      dialing: false,
    }).then((result) => {
      if (
        !shouldEndInsteadOfKeepAlive({
          callId,
          isGroup,
          isTombstoned: () => false,
          nativeRefusal: result,
        })
      ) {
        return
      }
      // Native already ended this call (Telecom abort / disconnect the
      // WebView missed): finish it here and tell the peer.
      recordCallFlow('ctrl:keepAlive:refused', { reason: result.reason ?? null, by: 'native' }, { callId })
      const current = getVoiceCallSession()
      if (current?.callId === callId && !isTerminalCallPhase(current.phase)) {
        void hangUpRef.current({ endSource: 'native-teardown' })
      }
    })
    void setNativeCallActive(callId)
  }, [])

  // CANONICAL CONNECT — media is up. This stamps `connectedAt` (overlay timer
  // + recorded duration; see terminateCall → END payload). Idempotent: a call
  // already 'active' KEEPS its original connectedAt, so a mid-call reconnect
  // never restarts the timer.
  //
  // 1:1 used to wait solely for remote-present. That stranded Ania on
  // "Connecting…" for 32s after audioVideoDidStart (call 55573680, Android 105)
  // — the 30s stall watchdog was the only promote path. Local media start now
  // promotes too; group calls promote from local media start.
  const promoteCallToActive = useCallback(() => {
    if (!shouldAcceptAdapterSessionEvent()) return
    clearDisconnectGrace()
    // Remote is (back) in the meeting — cancel any pending remote-left end.
    clearRemoteLeftGrace()
    const live = getVoiceCallSession()
    if (!live || isTerminalCallPhase(live.phase)) return
    callAudioRouteArbiter.setPhase('connected')
    patchVoiceCallSession({
      phase: 'active',
      // Preserve the FIRST canonical connect instant across reconnects.
      connectedAt: live.connectedAt ?? Date.now(),
      errorMessage: undefined,
      lastPeerHeartbeatAt: Date.now(),
      // Media is (re)established — clear any "Reconnecting…" banner.
      reconnecting: false,
      // The rehydrate/rejoin flow shows a persistent "Reconnecting you to your
      // call…" notice with no auto-clear timer; now that media connected, clear
      // it so it doesn't linger for the rest of the call (or into teardown).
      ...(live.notice === RECONNECTING_NOTICE ? { notice: undefined } : {}),
    })
    clearRingTimer()
    startCallKeepAlive()
  }, [clearDisconnectGrace, clearRemoteLeftGrace, clearRingTimer, shouldAcceptAdapterSessionEvent, startCallKeepAlive])

  /** Set below after disposeAdapter is defined. */
  const disposeAdapterRef = useRef<() => Promise<void>>(async () => {})

  /** Call id whose remote attendee has been seen in the Chime roster (two-way media). */
  const remotePresentCallIdRef = useRef<string | null>(null)

  // LOCAL media engine up (Chime audioVideoDidStart / native audioSessionDidStart).
  //  - GROUP call: this IS the canonical connect (a host can be alone in the
  //    room waiting for guests), so go active now.
  //  - 1:1 call: go active now too (see below); remote-present also promotes.
  //  - Multi-device: claim SESSION ownership here so other devices show soft
  //    presence instead of auto-joining (Q1C). Switch uses takeover + media_started.
  const onAdapterConnected = useCallback(() => {
    if (!shouldAcceptAdapterSessionEvent()) return
    clearDisconnectGrace()
    const live = getVoiceCallSession()
    if (!live || isTerminalCallPhase(live.phase) || live.phase === 'elsewhere') return
    // Keep the call alive the instant local media is up, regardless of party
    // count (screen-off survival + OS call chip) — unchanged from before.
    startCallKeepAlive()
    const deviceId = getDeviceId()
    if (deviceId && live.callerId && live.calleeId) {
      const action = switchingDeviceRef.current ? 'media_started' : 'claim'
      switchingDeviceRef.current = false
      void publishDeviceClaim({
        callId: live.callId,
        callerId: live.callerId,
        calleeId: live.calleeId,
        conversationId: live.conversationId,
        action,
        platform: getNativePlatform(),
        deviceId,
        caps: advertisedDeviceClaimCaps({
          platform: getNativePlatform(),
          nativeMediaCall: nativeMediaActiveRef.current === live.callId,
        }),
      }).catch((err) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (!/active on another device/i.test(msg)) return
        // Advisory only. A sibling of THIS user holds a fresh claim — log it
        // and keep THIS device's live media. Disposing here was the
        // crash-after-prompt path (Ania's false "2 devices" teardown). Rehydrate
        // already shows the Switch banner BEFORE join when the claim is
        // genuinely live elsewhere; Switch itself uses takeover, not this path.
        recordCallFlow(
          'ctrl:claim:ownedByOther',
          { action, advisory: true },
          { callId: live.callId },
        )
      })
    }
    if (live.isGroup) {
      promoteCallToActive()
      return
    }
    // 1:1: go active as soon as LOCAL media is up. Waiting solely for
    // remote-present stranded Ania on "Connecting…" for 32s after
    // audioVideoDidStart (call 55573680, Android 105) — the 30s stall watchdog
    // was the only promote path. Local-up ≠ two-way though (CarPlay peer stuck
    // joining, 2026-11-02): until the remote attendee is actually in the
    // roster, say so instead of letting this side talk into the void.
    if (live.phase === 'connecting' && !live.transferRoom && !live.transfer?.onHold) {
      promoteCallToActive()
      if (remotePresentCallIdRef.current !== live.callId) {
        const after = getVoiceCallSession()
        if (after && after.callId === live.callId && after.phase === 'active' && !after.notice) {
          patchVoiceCallSession({ notice: WAITING_FOR_PEER_NOTICE })
        }
      }
      return
    }
    // 1:1: if LOCAL media re-established on an already-active call (a mid-call
    // reconnect completed), clear the reconnecting banner WITHOUT touching the
    // canonical connectedAt.
    if (live.phase === 'active') {
      patchVoiceCallSession({
        reconnecting: false,
        ...(live.notice === RECONNECTING_NOTICE ? { notice: undefined } : {}),
      })
    }
  }, [clearDisconnectGrace, promoteCallToActive, shouldAcceptAdapterSessionEvent, startCallKeepAlive])

  // CANONICAL CONNECT for a 1:1 call: the first remote attendee is present
  // (two-way media). Group calls use LOCAL media start as their canonical
  // connect (handled in onAdapterConnected).
  const onAdapterRemotePresent = useCallback(() => {
    if (!shouldAcceptAdapterSessionEvent()) return
    const live = getVoiceCallSession()
    if (!live || live.isGroup) return
    remotePresentCallIdRef.current = live.callId
    promoteCallToActive()
    // Two-way media confirmed — clear the local-up-only banner.
    const after = getVoiceCallSession()
    if (after && after.callId === live.callId && after.notice === WAITING_FOR_PEER_NOTICE) {
      patchVoiceCallSession({ notice: undefined })
    }
  }, [promoteCallToActive, shouldAcceptAdapterSessionEvent])

  const onAdapterRemoteLeft = useCallback(() => {
    if (!shouldAcceptAdapterSessionEvent()) return
    const session = getVoiceCallSession()
    if (session?.phase !== 'active') return
    // Group calls never use the 1:1 "remote left → end" coupling (the adapter
    // doesn't fire this in multiparty mode); guard regardless.
    if (session.isGroup) return
    if (session.transfer?.onHold || isTransferInProgress(session.transfer?.phase)) return
    // Do NOT end immediately: the peer's attendee leaving can be a transient
    // gap (their multi-device Switch re-joins with a new attendeeId, a reload,
    // an ICE flap). A real hangup lands as an END signal (instant teardown);
    // this fallback only fires when the peer is STILL absent after the grace.
    const callId = session.callId
    clearRemoteLeftGrace()
    remoteLeftGraceRef.current = setTimeout(() => {
      remoteLeftGraceRef.current = null
      const current = getVoiceCallSession()
      if (!current || current.callId !== callId || current.phase !== 'active') return
      if (current.isGroup) return
      if (current.transfer?.onHold || isTransferInProgress(current.transfer?.phase)) return
      // Peer returned during the grace (also cancelled via promoteCallToActive).
      if (callAdapterRef.current?.hasRemotePresence()) return
      // The peer vanished WITHOUT an END landing (killed webview, dead radio):
      // publish the END ourselves so the backend SESSION row is settled and
      // deleted. Leaving the row alive made get-my-active-call re-offer the
      // dead call and rehydrate resurrected it — the zombie reconnect loop.
      // Redundant ENDs are de-duped server-side (claimTerminalCall).
      // Not a hang-up: `peer-lost` lets a session call wait for a reconnect.
      void terminateCallRef.current('completed', undefined, undefined, {
        notifyPeer: true,
        endCause: 'peer-lost',
      })
    }, REMOTE_LEFT_GRACE_MS)
  }, [clearRemoteLeftGrace, shouldAcceptAdapterSessionEvent])

  const disposeAdapter = useCallback(async (owner?: NativeCallOwner) => {
    // A scoped teardown (terminateCall, after its END publish) of a call that
    // a NEWER call has since replaced in Chime: the media, timers and native
    // call are the newer call's — only the native side is told (it scopes too).
    const joinedCallId = joinedChimeCallIdRef.current
    if (owner && joinedCallId && joinedCallId !== owner.callId) {
      recordCallFlow('ctrl:dispose:superseded', { joinedCallId }, { callId: owner.callId })
      // Android scopes the stop to the owner; iOS's stop ends every CallKit
      // call, the newer one included.
      if (getNativePlatform() === 'android') void stopNativeOngoingCall(owner)
      return
    }
    // Tear down the native iOS Chime audio session (if this call used it). Safe
    // and idempotent when it didn't (no-op off iOS / no active native session).
    const nativeCallId = nativeMediaActiveRef.current
    nativeMediaActiveRef.current = null
    // Clear the "answered on THIS device" marker on teardown so a later call can
    // never inherit a stale value (it is re-stamped by acceptCall / onAnswer).
    answeredHereCallIdRef.current = null
    if (nativeCallId) void leaveNativeIosMedia(nativeCallId)
    const videoAdapter = callAdapterRef.current
    callAdapterRef.current = null
    handledSignalsRef.current.clear()
    joiningRef.current = false
    joinedChimeCallIdRef.current = null
    // A redial's meeting prewarm is not the ended call's.
    if (!owner || prewarmedJoinRef.current?.callId === owner.callId) prewarmedJoinRef.current = null
    clearDisconnectGrace()
    clearRemoteLeftGrace()
    clearHeartbeatTimers()
    // If the main call tore down while a second call was still waiting, tell that
    // caller we're busy (merge/decline both clear it first, so this only fires on
    // a genuine call end).
    dropWaitingCall('BUSY')
    // Tear down the per-call room channel subscription (group calls).
    if (roomSubRef.current) {
      try {
        roomSubRef.current.unsubscribe()
      } catch {
        /* already closed */
      }
      roomSubRef.current = null
    }
    roomSubCallIdRef.current = null
    if (roomKeepAliveTimerRef.current) {
      clearInterval(roomKeepAliveTimerRef.current)
      roomKeepAliveTimerRef.current = null
    }
    if (rosterReconcileTimerRef.current) {
      clearTimeout(rosterReconcileTimerRef.current)
      rosterReconcileTimerRef.current = null
    }
    holdMusicPlayerRef.current.stop()
    // Tear down the ongoing-call foreground service + wake locks (native).
    if (isNativeApp()) void stopNativeOngoingCall(owner)
    if (videoAdapter) {
      videoAdapter.disposeSync()
      await videoAdapter.dispose()
    }
  }, [clearDisconnectGrace, clearHeartbeatTimers, clearRemoteLeftGrace, dropWaitingCall])
  disposeAdapterRef.current = disposeAdapter

  // Chime kicked THIS device because the same user joined from another device
  // (AudioJoinedFromAnotherDevice — multi-device Switch). Mirror the sibling
  // DEVICE_CLAIM handling: silent local teardown + soft "elsewhere" presence.
  // CRITICAL: never let this take the disconnect-grace path — that broadcasts
  // END/INTERRUPTED and, when the DEVICE_CLAIM signal is delayed or lost, ends
  // the call for every participant while the Switch is still in flight.
  const onAdapterKickedByOtherDevice = useCallback(() => {
    const live = getVoiceCallSession()
    recordCallFlow(
      'ctrl:kickedByOtherDevice',
      { phase: live?.phase ?? null, nativeMedia: nativeMediaActiveRef.current },
      { callId: live?.callId ?? null },
    )
    if (!live || isTerminalCallPhase(live.phase) || live.phase === 'elsewhere') return
    const callId = live.callId
    // SAME-DEVICE handoff, not another device: on iOS the native
    // ChimeMediaController joins with the SAME attendeeId as the JS adapter,
    // so Chime kicks the older (JS) session with AudioJoinedFromAnotherDevice.
    // That is our own native media taking over — dispose the JS adapter
    // quietly and keep the call live (2026-08-17: Olivia's group join showed
    // the false "2 devices" prompt from exactly this path).
    if (nativeMediaActiveRef.current === callId) {
      recordCallFlow('ctrl:kickedByOtherDevice:nativeHandoff', {}, { callId })
      void disposeAdapterRef.current()
      return
    }
    clearHeartbeatTimers()
    void (async () => {
      await disposeAdapterRef.current()
      void stopNativeOngoingCall()
      void setNativeCallDisconnected(callId, 'dismiss')
      patchVoiceCallSession({
        phase: 'elsewhere',
        activeElsewhere: true,
        // Owner device id/platform unknown at the media plane — the sibling's
        // DEVICE_CLAIM (claim/media_started) patch fills these when it lands.
        reconnecting: false,
        userMinimized: false,
        notice: 'Call moved to another device',
      })
    })()
  }, [clearHeartbeatTimers])

  // The meeting itself ended server-side while media was up (MeetingEnded).
  // The call is over for everyone — end locally now (no 5s disconnect grace,
  // no INTERRUPTED). Still publish the END: if no participant's END landed
  // (e.g. both webviews died and Chime reaped the meeting), the SESSION row
  // survives and rehydrate would resurrect the dead call on next foreground.
  // Redundant ENDs are de-duped server-side (claimTerminalCall).
  const onAdapterMeetingEnded = useCallback(() => {
    if (!shouldAcceptAdapterSessionEvent()) return
    const live = getVoiceCallSession()
    recordCallFlow(
      'ctrl:meetingEnded',
      { phase: live?.phase ?? null },
      { callId: live?.callId ?? null },
    )
    if (!live || (live.phase !== 'active' && live.phase !== 'connecting')) return
    void terminateCallRef.current('completed', undefined, undefined, {
      notifyPeer: true,
      endCause: 'peer-lost',
    })
  }, [shouldAcceptAdapterSessionEvent])

  const getCallAdapter = useCallback((): ChimeCallAdapter => {
    if (!callAdapterRef.current) {
      callAdapterRef.current = new ChimeCallAdapter({
        onConnected: onAdapterConnected,
        onRemotePresent: onAdapterRemotePresent,
        onDisconnected: scheduleDisconnectTerminate,
        onConnectFailed: (code) => handleInitialConnectFailedRef.current(code),
        onKickedByOtherDevice: onAdapterKickedByOtherDevice,
        onMeetingEnded: onAdapterMeetingEnded,
        onConnecting: (reconnecting) => {
          if (!shouldAcceptAdapterSessionEvent()) return
          const live = getVoiceCallSession()
          // Only flag/clear a reconnect for an already-connected call.
          if (live?.phase === 'active') patchVoiceCallSession({ reconnecting })
        },
        onRemoteLeft: onAdapterRemoteLeft,
        onLocalContentShareChanged: (active) => {
          if (!shouldAcceptAdapterSessionEvent()) return
          patchVoiceCallSession({ isPresenting: active })
        },
        onRemoteContentShareChanged: (active) => {
          if (!shouldAcceptAdapterSessionEvent()) return
          patchVoiceCallSession({ remotePresenting: active })
        },
        onRemoteVideoActive: () => {
          if (!shouldAcceptAdapterSessionEvent()) return
          // Live remote video trumps a stale "driving" placeholder (the peer's
          // driving:false announcement is fire-and-forget and can be lost).
          const live = getVoiceCallSession()
          if (live?.peerDriving) patchVoiceCallSession({ peerDriving: false })
        },
        onRosterPresenceChanged: (attendee, present) =>
          handleRosterPresenceRef.current(attendee, present),
        onAttendeeAudioStateChanged: (attendee, state) =>
          handleAttendeeAudioRef.current(attendee, state),
        onRemoteTileAdded: (tile) => handleRemoteTileAddedRef.current(tile),
        onRemoteTileRemoved: (tileId) => handleRemoteTileRemovedRef.current(tileId),
        onVideoSendUnavailable: () => handleVideoSendUnavailableRef.current(),
        onError: (message) => {
          const phase = getVoiceCallSession()?.phase
          void terminateCallRef.current('error', message, undefined, {
            endPayloadReason: phase === 'active' ? 'INTERRUPTED' : 'CANCELLED',
          })
        },
      })
    }
    return callAdapterRef.current
  }, [
    onAdapterConnected,
    onAdapterKickedByOtherDevice,
    onAdapterMeetingEnded,
    onAdapterRemotePresent,
    onAdapterRemoteLeft,
    scheduleDisconnectTerminate,
    shouldAcceptAdapterSessionEvent,
  ])

  const ensureFallbackAudioElement = useCallback((): HTMLAudioElement => {
    if (fallbackAudioRef.current) return fallbackAudioRef.current
    const el = createFallbackAudioElement()
    fallbackAudioRef.current = el
    return el
  }, [])

  const waitForRemoteAudioElement = useCallback(async (timeoutMs = 10_000): Promise<HTMLAudioElement> => {
    await yieldToDom()
    if (remoteAudioRef.current) return remoteAudioRef.current

    // Overlay audio mounts after React commit; native Answer-from-notification
    // can reach join before VoiceCallOverlay paints (especially cold start).
    const overlayWaitMs = isNativeApp() ? 1_500 : timeoutMs
    const started = Date.now()
    while (!remoteAudioRef.current && Date.now() - started < overlayWaitMs) {
      await new Promise((r) => setTimeout(r, MEDIA_ELEMENT_SETTLE_MS))
    }
    if (remoteAudioRef.current) return remoteAudioRef.current

    return ensureFallbackAudioElement()
  }, [ensureFallbackAudioElement])

  const ensureFallbackVideoElements = useCallback(() => {
    if (!fallbackLocalVideoRef.current) {
      fallbackLocalVideoRef.current = createFallbackVideoElement()
    }
    if (!fallbackRemoteVideoRef.current) {
      fallbackRemoteVideoRef.current = createFallbackVideoElement()
    }
    return {
      localVideo: fallbackLocalVideoRef.current,
      remoteVideo: fallbackRemoteVideoRef.current,
    }
  }, [])

  const waitForVideoElements = useCallback(
    async (timeoutMs = 10_000) => {
      await yieldToDom()
      // Overlay video mounts after React commit; native Answer-from-notification
      // can reach join before VideoCallOverlay paints (especially cold start).
      const overlayWaitMs = isNativeApp() ? 1_500 : timeoutMs
      const started = Date.now()
      while (
        (!localVideoRef.current || !remoteVideoRef.current) &&
        Date.now() - started < overlayWaitMs
      ) {
        await new Promise((r) => setTimeout(r, MEDIA_ELEMENT_SETTLE_MS))
      }
      if (localVideoRef.current && remoteVideoRef.current) {
        return { localVideo: localVideoRef.current, remoteVideo: remoteVideoRef.current }
      }
      return ensureFallbackVideoElements()
    },
    [ensureFallbackVideoElements],
  )

  // A video call answered on the locked Android keyguard joined audio-only;
  // the device is unlocked now, so the camera and the peer's video start.
  const enableLockedAnswerVideo = useCallback(
    async (callId: string, via: string) => {
      const session = getVoiceCallSession()
      const adapter = callAdapterRef.current
      if (
        !session ||
        session.callId !== callId ||
        isTerminalCallPhase(session.phase) ||
        session.mediaType !== 'video' ||
        !adapter
      ) {
        recordCallFlow('ctrl:lockedVideo:skipped', { via, phase: session?.phase ?? null }, { callId })
        return
      }
      recordCallFlow('ctrl:lockedVideo:unlocked', { via }, { callId })
      // Unlocked while attached to CarPlay / Android Auto (car dock): video
      // stays paused until the car detaches or the driver resumes explicitly.
      if (isCarConnected()) {
        patchVoiceCallSession({ selfDriving: true, isCameraOff: true })
        announceCarModeRef.current(true)
        recordCallFlow('ctrl:lockedVideo:carPaused', { via }, { callId })
        return
      }
      const permissions = await ensureCallPermissionsForCall({ video: true })
      if (permissions.camera === 'denied') {
        adapter.resumeRemoteVideo()
        patchVoiceCallSession({ errorMessage: cameraDeniedMessage() })
        recordCallFlow('ctrl:lockedVideo:cameraDenied', { via }, { callId })
        return
      }
      try {
        const { localVideo, remoteVideo } = await waitForVideoElements()
        await adapter.enableVideo({ localVideo, remoteVideo, contentVideo: contentVideoRef.current })
        if (getVoiceCallSession()?.callId === callId) patchVoiceCallSession({ isCameraOff: false })
        recordCallFlow('ctrl:lockedVideo:started', { via }, { callId })
      } catch (err) {
        adapter.resumeRemoteVideo()
        patchVoiceCallSession({
          errorMessage: 'Your camera could not start — tap the camera button to try again.',
        })
        recordCallFlow(
          'ctrl:lockedVideo:failed',
          { via, message: err instanceof Error ? err.message : String(err) },
          { callId },
        )
      }
    },
    [waitForVideoElements],
  )
  const enableLockedAnswerVideoRef = useRef(enableLockedAnswerVideo)
  enableLockedAnswerVideoRef.current = enableLockedAnswerVideo

  const joinChimeMeeting = useCallback(
    async (
      callId: string,
      displayName?: string,
      mediaType: CallMediaType = 'voice',
      options?: { callKitAnswer?: boolean },
    ) => {
      if (!currentUserId) return
      if (joiningRef.current) return
      if (joinedChimeCallIdRef.current === callId && callAdapterRef.current) {
        recordCallFlow('ctrl:join:dupIgnored', { platform: getNativePlatform() }, { callId })
        return
      }
      if (shouldAbortJoin(callId)) return
      // Claim the join BEFORE the media-element wait: the wait can take up to
      // 1.5s (native) / 10s (web), and an unclaimed window there let a second
      // join (connecting-stall watchdog, duplicate ACCEPT) interleave with
      // this one.
      joiningRef.current = true
      try {
        const isVideo = mediaType === 'video'
        const isGroupJoin = Boolean(getVoiceCallSession()?.isGroup)
        // Android locked-keyguard answer: audio now, video after the unlock.
        const deferVideoForLock =
          isVideo && !isGroupJoin && lockedVideoGateRef.current.deferAtJoin(callId)
        // CarPlay / Android Auto attached: the video call still connects, but
        // this device never starts its camera and keeps the peer's video
        // paused. Resumed when the car detaches or the driver taps
        // "I've stopped — turn video on". Audio-only car calls are untouched.
        const carVideoPause = isVideo && !isGroupJoin && (await refreshCarConnection())
        const deferVideo = deferVideoForLock || carVideoPause

        // Attribute all subsequent call-flow telemetry (adapter join stages,
        // audio-session signals) to this call id.
        setCallFlowCall(callId)
        recordCallFlow('ctrl:join:begin', {
          mediaType,
          platform: getNativePlatform(),
          visibility: typeof document !== 'undefined' ? document.visibilityState : null,
          ...(deferVideoForLock ? { videoAwaitsUnlock: true } : {}),
          ...(carVideoPause ? { carVideoPause: true } : {}),
        }, { callId })

        // Seed the speaker state from the platform/media matrix so the overlay
        // reflects the route the moment controls appear (route is applied to
        // the adapter once the meeting is joined, below).
        callAudioRouteArbiter.setMediaType(mediaType === 'video' ? 'video' : 'voice')
        callAudioRouteArbiter.setPhase('connecting')
        const queuedPick = queuedLockScreenPickRef.current
        if (queuedPick?.callId === callId) {
          queuedLockScreenPickRef.current = null
          callAudioRouteArbiter.userSelect(queuedPick.output)
          recordCallFlow(
            'route:userSelect',
            { kind: queuedPick.output, source: 'lockScreen', queued: true },
            { callId },
          )
        }
        patchVoiceCallSession({
          phase: 'connecting',
          mediaType,
          isSpeakerOn: desiredCallSpeakerOn(),
          ...(deferVideo ? { isCameraOff: true } : {}),
          ...(carVideoPause ? { selfDriving: true } : {}),
        })

        const requestToken = () =>
          chimeService.requestJoinToken({
            externalMeetingId: callId,
            attendeeDisplayName: displayName,
            enableVideo: isVideo,
          })

        // FRESH JoinInfo per callId. Only reuse a prewarmed token if it was
        // requested for THIS callId AND the meeting it resolved to is actually
        // THIS call's meeting (ExternalMeetingId === callId). A prior call's
        // JoinInfo must NEVER be reused — that is the deterministic stale-meeting
        // bug (2026-07-08) where the iOS callee joined a torn-down previous
        // meeting (58c79b0f) instead of the fresh one and got a terminal
        // SignalingBadRequest. On any miss/mismatch we fetch fresh below.
        let credentials: ChimeJoinTokenResponse | null = null
        if (prewarmedJoinRef.current?.callId === callId) {
          const prewarmed = await prewarmedJoinRef.current.promise
          if (shouldAbortJoin(callId)) return
          if (chimeCredentialsMatchCall(prewarmed, callId)) {
            credentials = prewarmed
          }
        }
        // Invalidate the prewarm regardless — it is single-use and must not leak
        // into a later call.
        prewarmedJoinRef.current = null
        if (!credentials) {
          credentials = await requestToken()
        }
        if (shouldAbortJoin(callId)) return

        // ── NATIVE iOS AUDIO PATH ────────────────────────────────────────────
        // iOS VOICE runs AUDIO through the native Amazon Chime SDK (Swift)
        // instead of the WKWebView WebRTC engine, so a locked-screen / background
        // CallKit answer can actually capture the mic (WebKit mutes mic capture
        // in a backgrounded WebView; the WebContent process is suspended on a
        // locked answer). The native attendee joins the SAME meeting with the
        // SAME JoinInfo, so recording/transcription/signaling/20-cap are intact.
        //
        // SCOPE:
        //   • ALL iOS voice joins (incoming CallKit answers AND outgoing /
        //     foreground). Outgoing previously stayed on the JS adapter "until
        //     device-validated"; 2026-08-19 Andre iOS → Zach Android showed
        //     that path is one-way-audio (WebView + Bluetooth + interruptions,
        //     no CallKit session, HTML downlink paused). Native start() without
        //     CallKit already exists for this case (startImmediately).
        //   • VIDEO stays on the JS adapter; WEB + ANDROID never reach here.
        // DOUBLE-JOIN GUARD (build 17 crash fix): runs for EVERY iOS voice join,
        // NOT only CallKit answers. The native layer may ALREADY own this meeting
        // — it minted JoinInfo + joined while the phone was locked
        // (AscendCallManager.maybeStartNativeMediaMint) before any JS ran. The
        // critical caller here is the FOREGROUND `rehydrateActiveCallFromBackend`
        // path, which does NOT pass `callKitAnswer`: in build 16 it therefore
        // skipped this guard entirely and joined through the WKWebView adapter,
        // producing a DOUBLE-JOIN (native Chime engine + WebView Chime adapter in
        // the same meeting). That double-membership churned the media engine
        // (join→stop→join) and destabilized the app on foreground-after-locked-
        // answer — the "crash" in the device test. Adopting the live native
        // session (and never touching the JS adapter) removes the double-join.
        // Also flips the overlay off "Connecting…" for an already-connected
        // native call (the post-unlock regression).
        if (!isVideo && isNativeIosAudioEnabled()) {
          const nativeActive = await getNativeIosMediaActiveCall()
          if (shouldAbortJoin(callId)) return
          if (nativeActive && nativeActive.callId === callId) {
            nativeMediaActiveRef.current = callId
            recordCallFlow(
              'ctrl:nativeMedia:adoptExisting',
              {
                connected: nativeActive.connected,
                callKitAnswer: Boolean(options?.callKitAnswer),
              },
              { callId },
            )
            const live = getVoiceCallSession()
            if (live?.isMuted) void setNativeIosMediaMuted(true)
            const speakerOn = desiredCallSpeakerOn()
            void setNativeIosMediaSpeaker(speakerOn)
            patchVoiceCallSession({ isSpeakerOn: speakerOn })
            // Foreground-adopt of a native session that's already running (e.g.
            // the locked-answer mint connected while the WebView was suspended).
            // Local audio up → keep the call alive + hold "Connecting…"; if the
            // native roster ALREADY shows a remote peer (a call that's been
            // talking while locked) that is the canonical connect → go active
            // now. The retained nativeRosterChanged event promotes otherwise.
            if (nativeActive.connected) {
              const s = getVoiceCallSession()
              if (s && s.callId === callId && !isTerminalCallPhase(s.phase)) {
                onAdapterConnected()
                if (nativeActive.remotePresent) onAdapterRemotePresent()
              }
            }
            return
          }
        }

        // NATIVE iOS VOICE PATH — initiate a native join for every iOS voice
        // call (CallKit answer AND outgoing/foreground). `callKitAnswer` only
        // chooses start timing (didActivate vs startImmediately).
        if (
          shouldInitiateNativeIosVoiceJoin({
            isVideo,
            nativeAudioEnabled: isNativeIosAudioEnabled(),
          })
        ) {
          // Never join a mismatched (stale) meeting natively either.
          if (!chimeCredentialsMatchCall(credentials, callId)) {
            credentials = await requestToken()
            if (shouldAbortJoin(callId)) return
          }
          const nativeAvailable = await isNativeIosMediaAvailable()
          if (shouldAbortJoin(callId)) return
          if (nativeAvailable) {
            const callKitAnswer = Boolean(options?.callKitAnswer)
            recordCallFlow(
              'ctrl:nativeMedia:joinBegin',
              { callKitAnswer, visibility: typeof document !== 'undefined' ? document.visibilityState : null },
              { callId },
            )
            try {
              await joinNativeIosMedia({
                callId,
                credentials,
                callKitAnswer,
                group: Boolean(getVoiceCallSession()?.isGroup),
              })
              nativeMediaActiveRef.current = callId
              // Seed mute/speaker the user may have set while connecting. The
              // native controller applies these once audio starts.
              const live = getVoiceCallSession()
              if (live?.isMuted) void setNativeIosMediaMuted(true)
              const speakerOn = desiredCallSpeakerOn()
              void setNativeIosMediaSpeaker(speakerOn)
              patchVoiceCallSession({ isSpeakerOn: speakerOn })
              recordCallFlow('ctrl:nativeMedia:joinRequested', { speakerOn }, { callId })
              // Native `nativeMediaConnected` drives phase → active. Done — do NOT
              // create/join the JS adapter (that would double-join the meeting).
              return
            } catch (err) {
              // Native join REJECTED at the bridge — fall through to the JS
              // adapter so the call still connects (e.g. foreground/unlocked),
              // just without the background-mic capability. Telemetry captures it.
              nativeMediaActiveRef.current = null
              recordCallFlow(
                'ctrl:nativeMedia:joinRejected',
                { error: err instanceof Error ? err.message : String(err) },
                { callId },
              )
            }
          }
        }
        // ─────────────────────────────────────────────────────────────────────

        // JS adapter path (video, native-unavailable fallback, web, Android).
        // iOS WebKit mutes getUserMedia in a backgrounded WKWebView — a join
        // started while hidden hangs until the connecting-stall watchdog
        // INTERRUPTs (Jaiden 2026-08-18 video, +30.7s, no chime:mic:started).
        if (getNativePlatform() === 'ios' && isDocumentHidden()) {
          recordCallFlow(
            'ctrl:join:waitVisible',
            { mediaType, visibility: 'hidden' },
            { callId },
          )
          const waited = await waitUntilDocumentVisible(() => shouldAbortJoin(callId))
          if (waited === 'aborted' || shouldAbortJoin(callId)) return
        }

        const audioEl = await waitForRemoteAudioElement()
        if (shouldAbortJoin(callId)) return

        for (let attempt = 0; attempt < 3; attempt++) {
          if (shouldAbortJoin(callId)) return
          // Hard guard: never hand the adapter JoinInfo for a different meeting.
          // If a stale cache (or the backend) ever yields a mismatched meeting,
          // discard it and refetch FRESH for this callId rather than joining a
          // dead/previous meeting.
          if (!chimeCredentialsMatchCall(credentials, callId)) {
            await disposeAdapter()
            credentials = await requestToken()
            if (shouldAbortJoin(callId)) return
          }
          try {
            if (isVideo && !deferVideo) {
              const { localVideo, remoteVideo } = await waitForVideoElements()
              if (shouldAbortJoin(callId)) return
              // videoOptional: a camera that can't start yet (CallKit answer
              // while the WebView is backgrounded) must NOT kill the call —
              // join audio-only and retry the camera on app foreground.
              await getCallAdapter().join(
                credentials,
                currentUserId,
                { audio: audioEl, localVideo, remoteVideo },
                {
                  enableVideo: true,
                  speakerOn: desiredCallSpeakerOn(),
                  videoOptional: true,
                },
              )
              if (!(callAdapterRef.current?.isVideoStarted() ?? false)) {
                patchVoiceCallSession({
                  errorMessage:
                    'Your camera could not start yet — it will retry automatically.',
                })
              }
            } else {
              // Voice call: join audio-only on the video-capable adapter so a
              // later upgrade just turns the camera on (no re-join). Video
              // elements aren't mounted yet — they bind on upgrade. A deferred
              // video call joins the same way, the peer's video held paused.
              await getCallAdapter().join(
                credentials,
                currentUserId,
                {
                  audio: audioEl,
                  localVideo: localVideoRef.current,
                  remoteVideo: remoteVideoRef.current,
                },
                {
                  enableVideo: false,
                  speakerOn: desiredCallSpeakerOn(),
                  ...(deferVideo ? { deferRemoteVideo: true } : {}),
                },
              )
            }
            // Apply mute/camera toggles the user may have set while connecting.
            const live = getVoiceCallSession()
            if (live?.isMuted) {
              callAdapterRef.current?.setMuted(true)
            }
            if (live?.isCameraOff && isVideo && !deferVideo) {
              await callAdapterRef.current?.setCameraEnabled(false)
            }
            // Apply the default audio route for this platform/media context.
            // On native this routes earpiece vs loud speaker via AudioManager;
            // on web the browser already plays through the default output.
            if (isNativeApp()) {
              const speakerOn = desiredCallSpeakerOn()
              const ok = (await callAdapterRef.current?.setSpeakerphone(speakerOn)) ?? false
              if (ok) patchVoiceCallSession({ isSpeakerOn: speakerOn })
            }
            joinedChimeCallIdRef.current = callId
            break
          } catch (err) {
            if (shouldAbortJoin(callId)) return
            const message = err instanceof Error ? err.message : ''
            const retriable =
              message.includes('audioHostUrl') ||
              message.includes('incomplete meeting') ||
              message.includes('incomplete meeting credentials')
            if (!retriable || attempt === 2) throw err
            await disposeAdapter()
            await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
            credentials = await requestToken()
          }
        }
        // The unlock landed while the audio-only join was still running.
        if (deferVideoForLock && lockedVideoGateRef.current.noteJoined(callId)) {
          void enableLockedAnswerVideoRef.current(callId, 'joinedAfterUnlock')
        }
        // The car may have attached or detached while the token fetch + media
        // connect were in flight (a realistic get-in-the-car window). The
        // mid-call effect can't repair that on its own: adapter.join() resets
        // the remote-video defer, and pauseVideoForCar no-ops once selfDriving
        // is set — so re-check the LIVE car state now that the join settled.
        const carNow = isVideo && !isGroupJoin ? await refreshCarConnection() : false
        if (carVideoPause && carNow) {
          // Still in the car: joined video-deferred as planned — tell the peer.
          recordCallFlow('ctrl:carVideo:joinPaused', {}, { callId })
          announceCarModeRef.current(true)
        } else if (carVideoPause && !carNow) {
          // Undocked mid-join: bring video up; announces driving:false itself
          // (never the stale driving:true the join was seeded with).
          recordCallFlow('ctrl:carVideo:carDetachedDuringJoin', {}, { callId })
          void resumeVideoAfterDrivingRef.current('carDetachedDuringJoin')
        } else if (!carVideoPause && carNow) {
          // Docked mid-join: this join started the camera. The racing mid-call
          // effect may already have marked selfDriving (its adapter work was
          // wiped by the join) — clear the flag so pauseVideoForCar runs
          // end-to-end against the settled adapter.
          recordCallFlow('ctrl:carVideo:carAttachedDuringJoin', {}, { callId })
          if (getVoiceCallSession()?.selfDriving) {
            // Also clear isCameraOff: this join demonstrably started the
            // camera, so a racing pause's `isCameraOff: true` must not be
            // memoized as a deliberate user choice (it would stop undocking
            // from switching the camera back on).
            patchVoiceCallSession({ selfDriving: false, isCameraOff: false })
          }
          void pauseVideoForCarRef.current('carAttachedDuringJoin')
        }
      } finally {
        joiningRef.current = false
      }
    },
    [
      currentUserId,
      disposeAdapter,
      getCallAdapter,
      waitForRemoteAudioElement,
      waitForVideoElements,
      onAdapterConnected,
      onAdapterRemotePresent,
    ],
  )

  const sendEnd = useCallback(
    async (
      callId: string,
      callerId: string,
      calleeId: string,
      conversationId?: string | null,
      reason = 'COMPLETED',
      startedAt?: number,
      detail?: string,
      extra?: Pick<CallEndPayload, 'callPurpose' | 'urgentLocation' | 'mediaType'>,
      roomInvite?: { joinCallId: string },
    ) => {
      try {
        await publishVoiceCallSignal({
          callId,
          type: 'END',
          callerId,
          calleeId,
          conversationId,
          payload: buildCallEndPayload(reason, startedAt, detail, extra),
          ...(roomInvite ? { roomInvite: true, joinCallId: roomInvite.joinCallId } : {}),
        })
      } catch {
        /* best effort */
      }
    },
    [],
  )

  const terminateCall = useCallback(
    async (
      reason: VoiceCallEndReason,
      errorMessage?: string,
      disconnectDetail?: string,
      options?: { notifyPeer?: boolean; endPayloadReason?: string; endCause?: VoiceCallEndCause },
    ) => {
      const session = getVoiceCallSession()
      if (!session || isTerminalCallPhase(session.phase)) {
        await disposeAdapter()
        return
      }

      leadershipRingRef.current = null
      clearCallNotification(session.callId)
      const phaseAtEnd = session.phase
      finishCall(reason, errorMessage, disconnectDetail, options?.endCause)
      const endDetail =
        options?.endCause === 'peer-lost' ? PEER_LOST_END_DETAIL : (disconnectDetail ?? errorMessage)

      if (options?.notifyPeer === false) {
        // No END will be published, so the SESSION row (and this device's
        // claim on it) may outlive us. Gracefully release the claim so no
        // sibling device is ever prompted to "switch" to a finished call —
        // the freshness window is only the backstop for crashes.
        const releaseDeviceId = getDeviceId()
        if (releaseDeviceId && (phaseAtEnd === 'active' || phaseAtEnd === 'connecting')) {
          void publishDeviceClaim({
            callId: session.callId,
            callerId: session.callerId,
            calleeId: session.calleeId,
            conversationId: session.conversationId,
            action: 'release',
            platform: getNativePlatform(),
            deviceId: releaseDeviceId,
          }).catch(() => {})
        }
      }

      if (options?.notifyPeer !== false) {
        if (phaseAtEnd === 'incoming') {
          await publishVoiceCallSignal({
            callId: session.callId,
            type: 'REJECT',
            callerId: session.callerId,
            calleeId: session.calleeId,
            conversationId: session.conversationId,
          }).catch(() => {})
          relayTerminalToSiblingDevices(
            {
              callId: session.callId,
              type: 'REJECT',
              callerId: session.callerId,
              calleeId: session.calleeId,
              conversationId: session.conversationId,
              payload: null,
            },
            session.calleeId,
          )
        } else {
          const payloadReason =
            options?.endPayloadReason ?? endPayloadReasonFor(reason, phaseAtEnd)
          // Recorded/stored duration = endedAt − (canonical connect). Anchor it
          // to `connectedAt` (the two-way-media moment) so the stored duration
          // matches the in-app overlay timer + the CallKit talk time. Falls back
          // to `startedAt` (ring start) for a call that never connected (MISSED /
          // CANCELLED), where `connectedAt` is undefined and duration isn't shown.
          // The backend records the END payload's `startedAt` verbatim, so this
          // is a pure client-side alignment — no backend change required.
          const durationAnchor = session.connectedAt ?? session.startedAt
          const endPayload = buildCallEndPayload(
            payloadReason,
            durationAnchor,
            endDetail,
            {
              callPurpose: session.callPurpose,
              urgentLocation: session.urgentLocation,
              mediaType: session.mediaType,
            },
          )
          await sendEnd(
            session.callId,
            session.callerId,
            session.calleeId,
            session.conversationId,
            payloadReason,
            durationAnchor,
            endDetail,
            {
              callPurpose: session.callPurpose,
              urgentLocation: session.urgentLocation,
              mediaType: session.mediaType,
            },
          )
          // Fan out cancel/end to our other devices (web + mobile same user).
          if (currentUserId) {
            relayTerminalToSiblingDevices(
              {
                callId: session.callId,
                type: 'END',
                callerId: session.callerId,
                calleeId: session.calleeId,
                conversationId: session.conversationId,
                payload: endPayload,
              },
              currentUserId,
            )
          }
        }
      }

      // After the END publish (seconds on a cold Lambda) a car / phone redial
      // may already be live: scope the teardown to the call that just ended.
      await disposeAdapter({ callId: session.callId, carTapAt: session.carTapAt })
    },
    [currentUserId, disposeAdapter, sendEnd],
  )

  terminateCallRef.current = terminateCall

  // The server's 30-second recording-consent window ended this call. Reaches
  // the heartbeat sender as its mutation reply and everyone else through the
  // subscription; idempotent, so receiving both copies is harmless.
  const handleConsentWindowEnd = useCallback(
    async (signal: VoiceCallSignal, notice: ConsentCallEndedNotice) => {
      recordCallFlow('ctrl:consentWindow:ended', {
        self: notice.unconsentedUserId === currentUserId,
      }, { callId: signal.callId })
      showConsentCallEndedNotice(notice)
      dismissIncomingRingSurface(signal.callId)
      const live = getVoiceCallSession()
      if (live?.callId === signal.callId && !isTerminalCallPhase(live.phase)) {
        clearHeartbeatTimers()
        clearRingTimer()
        finishCall('consent-missing')
        await disposeAdapter()
      } else if (shouldReclassifyAsConsentEnd(live, signal.callId, Date.now())) {
        // Chime's MeetingEnded beat this END and the call already ended as a
        // lost peer: it was the consent window (a session call must take back
        // its start, not wait for a reconnect).
        patchVoiceCallSession({ endReason: 'consent-missing', endCause: undefined })
      }
      emitConsentWindowEnded(signal.callId)
    },
    [clearHeartbeatTimers, clearRingTimer, currentUserId, disposeAdapter],
  )

  // The server's 30-second recording-consent window lapsed: the call goes on
  // but is not recorded. Both parties get a passing notice, once per call. The
  // post-connect sheet is withdrawn: consenting now would not record this
  // call, and the member's account stays undecided so the next call asks again.
  const notRecordedNoticeShownForRef = useRef<string | null>(null)
  const handleConsentNotRecorded = useCallback(
    (notice: ConsentNotRecordedNotice) => {
      const plan = planConsentNotRecordedNotice({
        notice,
        live: getVoiceCallSession(),
        viewerUserId: currentUserId,
        alreadyShownForCallId: notRecordedNoticeShownForRef.current,
        isTerminalPhase: isTerminalCallPhase,
      })
      if (!plan) return
      notRecordedNoticeShownForRef.current = notice.callId
      recordCallFlow('ctrl:consentWindow:notRecorded', {
        self: plan.isUnconsentedViewer,
      }, { callId: notice.callId })
      patchVoiceCallSession({
        callNotRecorded: true,
        ...(plan.withdrawPostConnectSheet ? { recordingConsentOwedPostConnect: false } : {}),
      })
      showNotice(plan.message, CONSENT_NOT_RECORDED_NOTICE_TTL_MS)
    },
    [currentUserId, showNotice],
  )

  const sendHeartbeat = useCallback(
    async (session: NonNullable<ReturnType<typeof getVoiceCallSession>>) => {
      let reply: VoiceCallSignal
      try {
        reply = await publishVoiceCallSignal({
          callId: session.callId,
          type: 'HEARTBEAT',
          callerId: session.callerId,
          calleeId: session.calleeId,
          conversationId: session.conversationId,
        })
      } catch {
        return /* best effort */
      }
      await routeHeartbeatReply(reply, handleConsentWindowEnd, handleConsentNotRecorded)
    },
    [handleConsentNotRecorded, handleConsentWindowEnd],
  )

  const startHeartbeatLoop = useCallback(() => {
    clearHeartbeatTimers()
    heartbeatTimerRef.current = setInterval(() => {
      const session = getVoiceCallSession()
      if (!session || session.phase !== 'active') return
      void sendHeartbeat(session)
    }, HEARTBEAT_INTERVAL_MS)
    // The server checks the 30-second recording-consent deadline on heartbeats;
    // the regular 20s cadence alone could let a call run ~20s past it.
    consentBackstopTimersRef.current = CONSENT_BACKSTOP_HEARTBEAT_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        const session = getVoiceCallSession()
        if (!session || session.phase !== 'active' || session.isGroup) return
        void sendHeartbeat(session)
      }, delay),
    )

    heartbeatWatchRef.current = setInterval(() => {
      const session = getVoiceCallSession()
      if (!session || session.phase !== 'active') return
      // Cross-check the media plane before killing the call: peer heartbeats
      // ride AppSync, which drops during network blips / backgrounding even
      // while Chime media keeps flowing (or is mid-reconnect). If Chime still
      // has the remote attendee present, the peer is alive — extend the
      // deadline instead of ending a working call with a false INTERRUPTED.
      const adapter = callAdapterRef.current
      const decision = decideOnHeartbeatMiss({
        lastPeerHeartbeatAt: session.lastPeerHeartbeatAt ?? null,
        connectedAt: session.connectedAt ?? null,
        now: Date.now(),
        missMs: HEARTBEAT_MISS_MS,
        mediaConnected: adapter?.isConnected() ?? false,
        remotePresent: adapter?.hasRemotePresence() ?? false,
      })
      if (decision === 'defer') {
        recordCallFlow(
          'ctrl:heartbeatMissDeferred',
          { lastPeerHeartbeatAt: session.lastPeerHeartbeatAt ?? null },
          { callId: session.callId },
        )
        patchVoiceCallSession({ lastPeerHeartbeatAt: Date.now() })
        return
      }
      if (decision === 'end') {
        void sendEnd(
          session.callId,
          session.callerId,
          session.calleeId,
          session.conversationId,
          'INTERRUPTED',
          session.startedAt,
          'Connection interrupted',
          {
            callPurpose: session.callPurpose,
            urgentLocation: session.urgentLocation,
            mediaType: session.mediaType,
          },
        )
        finishCall('interrupted', 'Connection interrupted')
        void disposeAdapter()
      }
    }, HEARTBEAT_INTERVAL_MS)
  }, [clearHeartbeatTimers, disposeAdapter, sendEnd, sendHeartbeat])

  // Chime initial-connect failure (session terminated before media ever
  // started). The SDK never retries terminal signaling codes — notably code 11
  // (SignalingBadRequest), which an abnormal WKWebView signaling-socket close
  // during the CallKit-answer handoff maps to. That socket suspension is
  // transient, so on iOS do a BOUNDED rejoin once the WebView network settles
  // rather than letting the call die ~400ms after it starts connecting. On
  // web/Android (no WKWebView suspension) keep the prior behavior.
  const handleInitialConnectFailed = useCallback(
    (code: number) => {
      if (!shouldAcceptAdapterSessionEvent()) return
      const session = getVoiceCallSession()
      recordCallFlow('ctrl:initialConnectFailed', {
        code,
        phase: session?.phase ?? null,
        isGroup: session?.isGroup ?? null,
        attempts: initialConnectRetryRef.current.attempts,
      }, { callId: session?.callId ?? null })
      if (!session || (session.phase !== 'connecting' && session.phase !== 'active')) return

      const isIos = isNativeApp() && getNativePlatform() === 'ios'
      const retryable = code < 0 || RETRYABLE_INITIAL_CONNECT_CODES.has(code)
      // Not the iOS handoff case (or a non-transient/terminal cause): fall back
      // to the existing grace-then-terminate behavior. Group joins go through a
      // different (room-keyed) path, so don't attempt the 1:1 rejoin for them.
      if (!isIos || !retryable || session.isGroup) {
        scheduleDisconnectTerminate()
        return
      }

      const tracker = initialConnectRetryRef.current
      if (tracker.callId !== session.callId) {
        tracker.callId = session.callId
        tracker.attempts = 0
      }
      if (tracker.attempts >= MAX_INITIAL_CONNECT_REJOINS) {
        tracker.callId = ''
        tracker.attempts = 0
        void terminateCallRef.current(
          'error',
          'Could not connect the call — please try again.',
          undefined,
          { endPayloadReason: 'INTERRUPTED' },
        )
        return
      }
      tracker.attempts += 1
      const callId = session.callId
      const mediaType = session.mediaType ?? 'voice'
      // Show the "Reconnecting…" banner and keep the call on-screen while we
      // dispose the failed session and rejoin. disposeAdapter() clears the
      // prewarmed JoinInfo AND the adapter's meeting/attendee/config, so the
      // rejoin below FETCHES FRESH JoinInfo for the current callId — it never
      // reuses the dead/previous meeting config (2026-07-08 stale-meeting bug).
      patchVoiceCallSession({ phase: 'connecting', reconnecting: true })
      void disposeAdapter()
        .then(() => waitForWebViewForeground(2_000))
        .then(() => {
          const live = getVoiceCallSession()
          if (!live || live.callId !== callId || isTerminalCallPhase(live.phase)) return undefined
          // This bounded rejoin only runs on iOS (see isIos gate above), so keep
          // the answer on the native audio path across the rejoin as well.
          return joinChimeMeeting(callId, localAttendeeDisplayName(currentUserId), mediaType, {
            callKitAnswer: true,
          })
        })
        .then(() => {
          const live = getVoiceCallSession()
          if (live && live.callId === callId && !isTerminalCallPhase(live.phase)) {
            startHeartbeatLoop()
          }
        })
        .catch(() => {
          void terminateCallRef.current(
            'error',
            'Could not connect the call — please try again.',
            undefined,
            { endPayloadReason: 'INTERRUPTED' },
          )
        })
    },
    [
      currentUserId,
      disposeAdapter,
      joinChimeMeeting,
      scheduleDisconnectTerminate,
      shouldAcceptAdapterSessionEvent,
      startHeartbeatLoop,
    ],
  )
  handleInitialConnectFailedRef.current = handleInitialConnectFailed

  const hangUp = useCallback(async (options?: { endSource?: CallEndSource }) => {
    const session = getVoiceCallSession()
    if (!session || isTerminalCallPhase(session.phase)) return
    const endSource: CallEndSource = isCallEndSource(options?.endSource) ? options.endSource : 'in-app'
    recordCallEndSource(session.callId, endSource, session.phase)
    // Group call: hanging up = leaving for THIS participant only. The call
    // continues for everyone else (group-end semantics). Declining an unanswered
    // group invite still uses the normal REJECT path below.
    if (session.isGroup && session.phase === 'prejoin') {
      // Close the never-connected room roster so nothing sticks "connecting"
      // after CANCELLED (Peter add-participant P0).
      void publishVoiceCallSignal({
        callId: session.callId,
        type: 'END',
        callerId: session.hostId ?? currentUserId ?? session.callId,
        calleeId: currentUserId ?? session.callId,
        conversationId: session.conversationId,
        roomCallId: session.callId,
        payload: {
          scope: 'room',
          reason: 'CANCELLED',
          lastParticipant: true,
          mediaType: session.mediaType,
          participantIds: roomEndParticipantIds(session.participants ?? []),
        },
      }).catch(() => {})
      await terminateCall('cancelled', undefined, undefined, { notifyPeer: false })
      return
    }
    if (session.isGroup && (session.phase === 'active' || session.phase === 'connecting')) {
      await leaveRoomRef.current()
      return
    }
    const reason: VoiceCallEndReason =
      session.phase === 'incoming' ? 'declined' : session.phase === 'active' ? 'completed' : 'cancelled'
    await terminateCall(reason)
  }, [currentUserId, terminateCall])
  hangUpRef.current = hangUp

  /**
   * Tear down a local incoming ring on THIS device only. When `notifyPeer` is
   * false (notification swipe / shade dismiss) sibling devices keep ringing so
   * the user can still answer on web. When true (explicit Decline, or replacing
   * a stale ring with a new INVITE) the caller + other devices are notified.
   */
  const abandonLocalIncomingRing = useCallback(
    async (callId: string, options?: { notifyPeer?: boolean }) => {
      const session = getVoiceCallSession()
      const notifyPeer = options?.notifyPeer ?? false

      if (session?.callId === callId && session.phase === 'incoming') {
        clearRingTimer()
        clearCallNotification(callId)
        if (isNativeApp()) void setNativeCallDisconnected(callId, 'canceled')
        if (notifyPeer) {
          await publishVoiceCallSignal({
            callId: session.callId,
            type: 'REJECT',
            callerId: session.callerId,
            calleeId: session.calleeId,
            conversationId: session.conversationId,
          }).catch(() => {})
          void publishVoiceCallSignal({
            callId: session.callId,
            type: 'REJECT',
            callerId: session.callerId,
            calleeId: session.calleeId,
            conversationId: session.conversationId,
            recipientUserId: session.calleeId,
          }).catch(() => {})
          finishCall('declined')
        } else {
          setVoiceCallSession(null)
        }
        await disposeAdapter()
        return
      }

      clearCallNotification(callId)
      if (isNativeApp()) void setNativeCallDisconnected(callId, 'canceled')
    },
    [clearRingTimer, disposeAdapter],
  )

  const handleRemoteSignal = useCallback(
    async (signal: VoiceCallSignal) => {
      const dedupeKey =
        signal.type === 'INVITE'
          ? `${signal.callId}:${signal.type}:${signal.senderId}`
          : `${signal.callId}:${signal.type}:${signal.createdAt}:${signal.senderId}`
      if (handledSignalsRef.current.has(dedupeKey)) return
      handledSignalsRef.current.add(dedupeKey)

      // The server's 30-second recording-consent window ended this call. The
      // server publishes this END once per party (and the heartbeat sender
      // also gets it as its mutation reply). Handled before the own-device /
      // sibling filters: it is server-originated, not a relay.
      const consentEndNotice = consentWindowEndNotice(signal)
      if (consentEndNotice) {
        await handleConsentWindowEnd(signal, consentEndNotice)
        return
      }
      const notRecordedNotice = consentNotRecordedNotice(signal)
      if (notRecordedNotice) {
        handleConsentNotRecorded(notRecordedNotice)
        return
      }

      // Call signals now fan out to every device of BOTH parties so all of a
      // user's devices ring. Never react to a signal this very device sent.
      if (signal.senderDeviceId && signal.senderDeviceId === getDeviceId()) return
      // A signal sent by another device of the *same* signed-in user.
      const isSiblingDevice = Boolean(currentUserId) && signal.senderId === currentUserId

      const session = getVoiceCallSession()

      if (signal.type === 'HEARTBEAT') {
        if (session?.callId === signal.callId && session.phase === 'active') {
          patchVoiceCallSession({ lastPeerHeartbeatAt: Date.now() })
        }
        return
      }

      // Multi-device media ownership (same Cognito user only). Sibling DEVICE_CLAIM
      // means another of our devices took media — leave locally; on iOS keep
      // CallKit until media_started (Q2C / Q3C).
      if (signal.type === 'DEVICE_CLAIM' && isSiblingDevice) {
        // This device already saw this call END — a late/stale claim signal
        // must never seed "active on another device" for a finished call.
        if (isCallTombstoned(signal.callId)) return
        const payload = parseSignalPayload<{
          action?: string
          platform?: string
          deviceId?: string
        }>(signal.payload)
        const action = String(payload?.action ?? 'claim')
        const ownerDeviceId = String(payload?.deviceId ?? signal.senderDeviceId ?? '')
        const ownerPlatform =
          payload?.platform === 'ios' ||
          payload?.platform === 'android' ||
          payload?.platform === 'web'
            ? payload.platform
            : null

        if (action === 'release') {
          // Sibling gracefully released its claim (tab closed / call left).
          // If we were only showing soft presence for that device, clear it.
          const live = getVoiceCallSession()
          if (
            live?.callId === signal.callId &&
            live.phase === 'elsewhere' &&
            (!ownerDeviceId || !live.activeDeviceId || live.activeDeviceId === ownerDeviceId)
          ) {
            setVoiceCallSession(null)
          }
          return
        }

        if (action === 'media_started') {
          // Q2C/Q3C: claim-after-media path — sibling tears down only once the
          // new device reports media is up (not on a speculative pre-join claim).
          const live = getVoiceCallSession()
          if (
            live &&
            live.callId === signal.callId &&
            (live.phase === 'active' ||
              live.phase === 'connecting' ||
              live.phase === 'outgoing')
          ) {
            clearHeartbeatTimers()
            await disposeAdapter()
            void stopNativeOngoingCall()
            void setNativeCallDisconnected(signal.callId, 'dismiss')
            pendingCallKitHandoffRef.current = null
            patchVoiceCallSession({
              phase: 'elsewhere',
              activeElsewhere: true,
              activeDeviceId: ownerDeviceId || null,
              activeDevicePlatform: ownerPlatform,
              reconnecting: false,
              userMinimized: false,
              notice: 'Call moved to another device',
            })
            return
          }
          if (
            pendingCallKitHandoffRef.current === signal.callId &&
            getNativePlatform() === 'ios'
          ) {
            pendingCallKitHandoffRef.current = null
            void setNativeCallDisconnected(signal.callId, 'dismiss')
          }
          if (session?.callId === signal.callId && session.phase === 'elsewhere') {
            patchVoiceCallSession({
              activeDeviceId: ownerDeviceId || session.activeDeviceId,
              activeDevicePlatform: ownerPlatform ?? session.activeDevicePlatform,
            })
          }
          return
        }

        if (action === 'claim' || action === 'takeover') {
          const live = getVoiceCallSession()
          if (
            live &&
            live.callId === signal.callId &&
            (live.phase === 'active' ||
              live.phase === 'connecting' ||
              live.phase === 'outgoing')
          ) {
            if (getNativePlatform() === 'ios') {
              pendingCallKitHandoffRef.current = signal.callId
            }
            clearHeartbeatTimers()
            await disposeAdapter()
            // Leave CallKit up until media_started on iOS (Q3C). Android/web
            // clear the ongoing chip now — media already moved.
            if (getNativePlatform() !== 'ios') {
              void stopNativeOngoingCall()
              void setNativeCallDisconnected(signal.callId, 'dismiss')
            } else {
              void stopNativeOngoingCall()
            }
            patchVoiceCallSession({
              phase: 'elsewhere',
              activeElsewhere: true,
              activeDeviceId: ownerDeviceId || null,
              activeDevicePlatform: ownerPlatform,
              reconnecting: false,
              userMinimized: false,
              notice: 'Call moved to another device',
            })
          } else if (!live || isTerminalCallPhase(live.phase) || live.phase === 'elsewhere') {
            // Soft presence seed when this device wasn't in media.
            if (!live || live.callId !== signal.callId || isTerminalCallPhase(live?.phase ?? 'idle')) {
              // Rehydrate soft presence from signal identity if we have no session.
              // Full peer labels come from a later myActiveCall rehydrate.
            }
            if (live?.callId === signal.callId) {
              patchVoiceCallSession({
                phase: 'elsewhere',
                activeElsewhere: true,
                activeDeviceId: ownerDeviceId || null,
                activeDevicePlatform: ownerPlatform,
              })
            }
          }
        }
        return
      }

      if (signal.type === 'INVITE') {
        // Our own outgoing call, echoed to our other devices — don't ring ourselves.
        if (signal.callerId === currentUserId) return
        noteIncomingInviteReceived(signal.callId)
        // Already ringing or connected on this call — ignore duplicate INVITEs
        // (subscription reconnect / multi-path delivery).
        if (
          session?.callId === signal.callId &&
          !isTerminalCallPhase(session.phase)
        ) {
          return
        }

        if (
          session &&
          session.phase !== 'ended' &&
          session.phase !== 'idle' &&
          session.callId !== signal.callId
        ) {
          // A prior incoming ring the user dismissed (notification swipe /
          // shade clear) can leave a stale 'incoming' session and a live
          // Telecom/CallKit connection. That blocked every subsequent call
          // with BUSY. Abandon the abandoned ring locally + tell the old
          // caller we declined; only truly-busy phases (outgoing/active/…) still
          // reject the new invite.
          if (session.phase === 'incoming') {
            await abandonLocalIncomingRing(session.callId, { notifyPeer: true })
          } else if (
            session.phase === 'active' &&
            !session.isGroup &&
            signal.roomInvite !== true &&
            !signal.roomCallId &&
            !getWaitingCall()
          ) {
            // Call-waiting: a second 1:1 call arrived while we're in an active
            // call. Stash it and let the user MERGE (add caller to this call) or
            // DECLINE — instead of auto-rejecting with BUSY.
            const invite = extractCallerInvitePayload(signal.payload)
            const waitingPeer = await resolveIncomingCallerPeer(
              signal.callerId,
              signal.calleeId,
              currentUserId,
              signal.payload,
            )
            setWaitingCall({
              callId: signal.callId,
              callerId: signal.callerId,
              calleeId: signal.calleeId,
              conversationId: signal.conversationId,
              peer: waitingPeer,
              mediaType: invite?.mediaType === 'video' ? 'video' : 'voice',
              startedAt: Date.now(),
              sessionUuid: invite?.sessionUuid ?? null,
            })
            showNotice(`${waitingPeer.displayName} is calling…`)
            clearWaitingRingTimer()
            waitingRingTimerRef.current = setTimeout(() => {
              if (getWaitingCall()?.callId === signal.callId) dropWaitingCall('MISSED')
            }, RING_TIMEOUT_MS)
            return
          } else {
            await publishVoiceCallSignal({
              callId: signal.callId,
              type: 'BUSY',
              callerId: signal.callerId,
              calleeId: signal.calleeId,
              conversationId: signal.conversationId,
              // Busy on a room invite only declines this invitee; the server
              // must not end the room (callId is the room id).
              ...(signal.roomInvite
                ? { roomInvite: true, joinCallId: signal.joinCallId || signal.callId }
                : {}),
            }).catch(() => {})
            return
          }
        }

        const invite = extractCallerInvitePayload(signal.payload)
        const mediaType: CallMediaType = invite?.mediaType === 'video' ? 'video' : 'voice'
        const isRoomInvite = signal.roomInvite === true
        const roomInvitePayload = isRoomInvite
          ? parseSignalPayload<{
              groupTitle?: string
              transfer?: boolean
              originalCallerId?: string
              originalCallerDisplayName?: string
              originalCallerAvatarUrl?: string | null
            }>(signal.payload)
          : null
        const roomGroupTitle = roomInvitePayload?.groupTitle?.trim() || undefined
        // A transfer invite joins the same room mechanics but renders the 1:1
        // overlay on the invitee (the call is being forwarded TO them).
        const isTransferRoomInvite = isRoomInvite && roomInvitePayload?.transfer === true
        const transferOriginalCallerId = roomInvitePayload?.originalCallerId?.trim()
        const forwardedCallerId = transferOriginalCallerId || undefined
        const peer = isTransferRoomInvite && forwardedCallerId
          ? await resolvePeerByUserId(forwardedCallerId, {
              displayName: roomInvitePayload?.originalCallerDisplayName,
              avatarUrl: roomInvitePayload?.originalCallerAvatarUrl,
            })
          : await resolveIncomingCallerPeer(
              signal.callerId,
              signal.calleeId,
              currentUserId,
              signal.payload,
            )
        const sessionCallerId =
          isTransferRoomInvite && forwardedCallerId
            ? forwardedCallerId
            : signal.callerId
        const hostParticipantId = sessionCallerId
        // iOS: CallKit (rung by the VoIP push) is the ONE canonical incoming
        // ring surface — the in-app ring overlay stands down while it shows.
        // The VoIP push usually lands before this AppSync INVITE, so the flag
        // is normally known here; the nativeIncomingRing listener also patches
        // it live for the reverse ordering. Android keeps its existing split
        // (JS overlay in foreground / Telecom when hidden).
        const iosCallKitRing =
          isNativeApp() &&
          getNativePlatform() === 'ios' &&
          isNativeIncomingRingActive(signal.callId)

        callAudioRouteArbiter.setNative(isNativeApp())
        callAudioRouteArbiter.begin(mediaType === 'video' ? 'video' : 'voice', 'incoming-ringtone')
        setVoiceCallSession({
          callId: signal.callId,
          callerId: sessionCallerId,
          calleeId: signal.calleeId,
          conversationId: signal.conversationId,
          localRole: 'callee',
          peer,
          phase: 'incoming',
          mediaType,
          osRingActive: iosCallKitRing,
          isMuted: false,
          isSpeakerOn: callAudioRouteArbiter.desiredSpeakerOn(),
          // 1:1 video answers with the camera ON; group video keeps each
          // participant's camera OFF until they consent (tap to enable).
          isCameraOff: isRoomInvite,
          startedAt: Date.now(),
          callPurpose: invite?.callPurpose ?? 'normal',
          urgentLocation: invite?.urgentLocation ?? null,
          sessionUuid: isRoomInvite ? null : (invite?.sessionUuid ?? null),
          ...(isRoomInvite
            ? {
                isGroup: true,
                ...(isTransferRoomInvite ? { transferRoom: true } : {}),
                isHost: false,
                hostId: signal.callerId,
                maxParticipants: MAX_CALL_PARTICIPANTS,
                groupTitle: roomGroupTitle,
                participants: [
                  {
                    userId: hostParticipantId,
                    displayName: peer.displayName,
                    avatarUrl: peer.avatarUrl,
                    role: 'host' as const,
                    state: 'active' as const,
                  },
                  ...(currentUserId
                    ? [
                        {
                          userId: currentUserId,
                          displayName:
                            localAttendeeDisplayName(currentUserId) ?? 'You',
                          role: 'guest' as const,
                          state: 'ringing' as const,
                        },
                      ]
                    : []),
                ],
              }
            : {}),
        })

        // Prewarm the callee's Chime join token WHILE the call is still ringing
        // so tapping Accept connects immediately instead of paying for the
        // meeting-token round-trip (CreateMeeting/GetMeeting + CreateAttendee)
        // only after the user answers. This is the single biggest answer-side
        // latency win — especially noticeable connecting TO this device. 1:1
        // only: group/room invitees join the host's existing meeting through
        // joinGroupMeeting, which has its own credential path. The request is
        // best-effort (.catch → null) so a failure never blocks the ring, and
        // joinChimeMeeting falls back to a fresh request if the prewarm missed.
        if (!isRoomInvite && currentUserId) {
          prewarmedJoinRef.current = {
            callId: signal.callId,
            promise: chimeService
              .requestJoinToken({
                externalMeetingId: signal.callId,
                attendeeDisplayName:
                  localAttendeeDisplayName(currentUserId) ?? peer.displayName,
                enableVideo: mediaType === 'video',
                // This ring-time prewarm can CREATE the meeting (racing the
                // caller's dial-time prewarm) while the local user — the
                // callee — hasn't seen the recording notice yet, so recording
                // must stay deferred until consent resolves.
                deferRecordingCapture: shouldDeferRecordingCapture(
                  getCachedDirectoryUser(signal.callerId),
                ),
              })
              .catch(() => null),
          }
        }

        // Foreground AppSync INVITE:
        // - Android: JS overlay owns the ring; dismiss any native Telecom ring
        //   seeded by FCM so we don't double-ring (unchanged behavior).
        // - iOS: CallKit owns ringing (standard iOS pattern) — do NOT cancel
        //   the CallKit call here. The old cancel raced the VoIP push (a push
        //   landing after this cancel re-rang CallKit anyway), which is how the
        //   banner + in-app ring ended up visible together.
        if (
          isNativeApp() &&
          getNativePlatform() !== 'ios' &&
          document.visibilityState === 'visible' &&
          !isRoomInvite
        ) {
          void cancelNativeIncomingCall(signal.callId)
        }

        // App alive but backgrounded / locked → show a phone-style incoming
        // call. Group invites use the standard high-priority notification +
        // in-app ring (NOT the self-managed Telecom / CallStyle path, which
        // rings only ONE call), so skip the native full-screen ring for them.
        if (isNativeApp() && document.visibilityState === 'hidden' && !isRoomInvite) {
          void (async () => {
            const shown = await showNativeIncomingCall({
              callId: signal.callId,
              callerName: peer.displayName,
              video: mediaType === 'video',
              callerId: signal.callerId,
              calleeId: signal.calleeId,
              callPurpose: invite?.callPurpose ?? 'normal',
            })
            if (shown) {
              // The OS (Telecom/CallStyle) ring is now live. Mirror it onto the
              // session so the JS ringtone effect re-runs and stands down — the
              // effect started ringing on phase→incoming BEFORE this async
              // native call resolved, which double-rang backgrounded Android
              // (OS channel ringtone + WebView ringtone simultaneously).
              const current = getVoiceCallSession()
              if (
                current?.callId === signal.callId &&
                current.phase === 'incoming' &&
                !current.osRingActive
              ) {
                patchVoiceCallSession({ osRingActive: true })
              }
            } else {
              void showIncomingCallActionNotification({
                callId: signal.callId,
                callerName: peer.displayName,
              })
            }
          })()
        }

        clearRingTimer()
        ringTimerRef.current = setTimeout(() => {
          const current = getVoiceCallSession()
          if (current?.callId === signal.callId && current.phase === 'incoming') {
            dismissIncomingRingSurface(signal.callId)
            const endReason = resolveUnansweredEndReason(current.startedAt, new Date(), 'MISSED')
            // A room invite's ring timing out is this invitee's no-answer, not
            // the end of the room (callId is the room id).
            void sendEnd(
              current.callId,
              current.callerId,
              current.calleeId,
              current.conversationId,
              endReason,
              current.startedAt,
              undefined,
              undefined,
              current.isGroup && !current.isHost ? { joinCallId: current.callId } : undefined,
            )
            if (currentUserId) {
              relayTerminalToSiblingDevices(
                {
                  callId: current.callId,
                  type: 'END',
                  callerId: current.callerId,
                  calleeId: current.calleeId,
                  conversationId: current.conversationId,
                  payload: buildCallEndPayload(endReason, current.startedAt),
                },
                currentUserId,
              )
            }
            finishCall(endReason === 'MISSED' ? 'missed' : 'cancelled')
            void disposeAdapter()
          }
        }, RING_TIMEOUT_MS)
        return
      }

      // A waiting (call-waiting) caller hung up / cancelled before we merged or
      // declined — drop the prompt without touching the active call.
      if (
        getWaitingCall()?.callId === signal.callId &&
        (signal.type === 'END' || signal.type === 'REJECT' || signal.type === 'BUSY')
      ) {
        clearWaitingRingTimer()
        clearWaitingCall()
        dismissIncomingRingSurface(signal.callId)
        return
      }

      if (
        signal.type === 'ACCEPT' &&
        !isSiblingDevice &&
        currentUserId &&
        signal.callerId === currentUserId &&
        isCallTombstoned(signal.callId) &&
        shouldEndLateAccept({
          tombstoneReason: tombstonedCallReason(signal.callId),
          roomInvite: signal.roomInvite,
          roomCallId: signal.roomCallId,
          joinCallId: signal.joinCallId,
        }) &&
        (!session || session.callId !== signal.callId || isTerminalCallPhase(session.phase))
      ) {
        // NEVER RESURRECT: the callee answered a 1:1 dial this device already
        // abandoned (car / Telecom hang-up before connect). Send END so they
        // are not left in an empty meeting, and never join. A room ACCEPT is
        // never answered with END — that would close a live group room.
        recordCallFlow('ctrl:accept:endedCall', { phase: session?.phase ?? null }, { callId: signal.callId })
        void sendEnd(
          signal.callId,
          signal.callerId,
          signal.calleeId,
          signal.conversationId ?? null,
          'CANCELLED',
          Date.now(),
        )
        return
      }

      if (!session || session.callId !== signal.callId) {
        // Caller cancelled / declined / busy while we ring on native push only, or
        // with a stale session — still tear down OS-level ringing.
        if (
          signal.type === 'END' ||
          signal.type === 'REJECT' ||
          signal.type === 'BUSY'
        ) {
          dismissIncomingRingSurface(signal.callId)
          if (
            session?.callId === signal.callId &&
            session.phase === 'incoming'
          ) {
            clearRingTimer()
            finishCall(endReasonFromTerminalSignal(signal.type, signal.payload))
            await disposeAdapter()
          }
          // NEVER re-relay a signal that is itself a sibling relay — the
          // subscription broadcasts relays to BOTH parties, so re-relaying
          // creates an infinite cross-user END ping-pong (2026-08-17 storm).
          if (
            currentUserId &&
            !isSiblingDevice &&
            !isSiblingRelaySignal(signal) &&
            shouldRelayPeerTerminal(signal.type)
          ) {
            relayTerminalToSiblingDevices(signal, currentUserId)
          }
        }
        if (leadershipRingRef.current && (signal.type === 'REJECT' || signal.type === 'BUSY')) {
          const ring = leadershipRingRef.current
          if (ring.invites.some((i) => i.callId === signal.callId)) {
            ring.responded.add(signal.callId)
            if (
              ring.responded.size >= ring.invites.length &&
              getVoiceCallSession()?.phase === 'outgoing'
            ) {
              leadershipRingRef.current = null
              clearRingTimer()
              finishCall('busy')
              await disposeAdapter()
            }
            return
          }
        }
        if (leadershipRingRef.current && signal.type === 'ACCEPT') {
          const ring = leadershipRingRef.current
          const match = ring.invites.find((i) => i.callId === signal.callId)
          const live = getVoiceCallSession()
          if (match && live?.phase === 'outgoing') {
            for (const inv of ring.invites) {
              if (inv.callId === signal.callId) continue
              void publishVoiceCallSignal({
                callId: inv.callId,
                type: 'BUSY',
                callerId: signal.callerId,
                calleeId: inv.peerUserId,
                conversationId: ring.conversationId,
              }).catch(() => {})
            }
            leadershipRingRef.current = null
            clearRingTimer()
            patchVoiceCallSession({
              callId: signal.callId,
              calleeId: match.peerUserId,
              // Someone answered: from here the UI shows WHO picked up —
              // drop the "Leadership team" ring label + the dialling notice.
              peer: match.peer,
              ringGroupName: null,
              notice: undefined,
              phase: 'connecting',
            })
            try {
              await joinChimeMeeting(
                signal.callId,
                localAttendeeDisplayName(currentUserId),
                live.mediaType ?? 'voice',
              )
              startHeartbeatLoop()
            } catch (err) {
              void terminateCall(
                'error',
                err instanceof Error ? err.message : 'Failed to join call',
                undefined,
                { endPayloadReason: 'CANCELLED' },
              )
            }
          }
        }
        return
      }

      // A terminal/answer signal whose sender is our OWN user id — either a
      // sibling device (web ↔ mobile, same account) handled the ring, or it's
      // the echo of a relay THIS device just sent to silence its siblings.
      //
      // Such a signal must NEVER drive this device's call forward or tear down a
      // call this device is actively in. In particular, when we accept a call we
      // relay ACCEPT to our own user id; that echo comes straight back here, and
      // acting on it (the pre-fix behavior) immediately disposed the freshly
      // connected call. So: only use a sibling signal to silently drop a
      // *redundant incoming ring* on this device, and always return without
      // falling through to the peer-signal switch below.
      if (
        isSiblingDevice &&
        (signal.type === 'ACCEPT' ||
          signal.type === 'REJECT' ||
          signal.type === 'END' ||
          signal.type === 'BUSY')
      ) {
        // …EXCEPT our own answer echoing back through the native-ack relay. A
        // locked CallKit answer publishes its ACCEPT server-side, and that ACCEPT
        // is fanned back to this very device with our own senderId. While the
        // suspended WebView is still 'incoming' (accept hasn't run yet) the block
        // below would "silently drop the redundant ring" — disposing the native
        // media session that just joined the meeting and stranding the caller on
        // "Connecting…". A REAL sibling device's ACCEPT carries that device's own
        // id and is unaffected, so answering elsewhere still hands off.
        if (
          shouldIgnoreOwnAnswerEcho({
            signalType: signal.type,
            signalCallId: signal.callId,
            senderDeviceId: signal.senderDeviceId,
            answeredHereCallId: answeredHereCallIdRef.current,
            nativeMediaCallId: nativeMediaActiveRef.current,
            session,
          })
        ) {
          recordCallFlow(
            'ctrl:signal:ownAnswerEchoIgnored',
            { phase: session?.phase ?? null, senderDeviceId: signal.senderDeviceId ?? null },
            { callId: signal.callId },
          )
          return
        }
        if (session?.callId === signal.callId && session.phase === 'incoming') {
          clearRingTimer()
          dismissIncomingRingSurface(signal.callId)
          // Another of our devices handled this ring — drop it silently (no
          // banner, no missed-call history, no extra signal to the peer).
          setVoiceCallSession(null)
          await disposeAdapter()
        }
        // Owning device hung up — clear soft presence on siblings.
        if (
          signal.type === 'END' &&
          session?.callId === signal.callId &&
          session.phase === 'elsewhere'
        ) {
          pendingCallKitHandoffRef.current = null
          setVoiceCallSession(null)
        }
        return
      }

      // Group call: per-invitee invite-phase signals (onVoiceCallSignal) update
      // the roster only. A single REJECT / END / BUSY from one invitee must NOT
      // tear down the whole call (group-end semantics live on the room channel /
      // Chime presence). In-room state runs via onCallRoomSignal.
      if (session.isGroup) {
        switch (signal.type) {
          case 'ACCEPT': {
            upsertGroupParticipant(signal.senderId, { state: 'active' })
            return
          }
          case 'REJECT':
          case 'BUSY':
            upsertGroupParticipant(signal.senderId, { state: 'declined' })
            return
          case 'END':
            upsertGroupParticipant(signal.senderId, { state: 'left' })
            return
          default:
            return
        }
      }

      switch (signal.type) {
        case 'CONSENT':
          // Mid-call recording-consent bookkeeping from the peer's post-connect
          // sheet — server-side only (consent record + deferred capture start).
          // Nothing for the receiving client to do.
          break
        case 'MEDIA_SWITCH': {
          const payload = parseSignalPayload<MediaSwitchPayload | CarModeSignalPayload>(
            signal.payload,
          )
          // Car-mode driving state rides MEDIA_SWITCH (no new signal type —
          // older builds' handler only reacts to `action`/`to` and ignores it).
          if (payload && 'kind' in payload && payload.kind === 'car-mode') {
            handleCarModeSignalRef.current(payload.driving === true)
            break
          }
          // Consent-based voice↔video switching (1:1).
          const switchPayload = payload as MediaSwitchPayload | null
          if (
            switchPayload?.action &&
            (switchPayload.to === 'video' || switchPayload.to === 'voice')
          ) {
            handleMediaSwitchRef.current(switchPayload)
          }
          break
        }
        case 'TRANSFER': {
          const payload = parseSignalPayload<TransferSignalPayload>(signal.payload)
          if (payload?.action) {
            await handleTransferSignalRef.current(payload, signal.senderId)
          }
          break
        }
        case 'HOLD': {
          // Peer put us on hold — mute the meeting audio and loop hold music
          // locally so the held party doesn't sit in dead silence.
          if (session.phase !== 'active') break
          patchVoiceCallSession({ peerHold: true })
          holdMusicPlayerRef.current.start(
            resolveMediaUrl(getTransferEligibilityConfig().holdMusicUrl),
            remoteAudioRef.current,
          )
          break
        }
        case 'UNHOLD': {
          holdMusicPlayerRef.current.stop()
          patchVoiceCallSession({ peerHold: false })
          break
        }
        case 'BUSY':
          recordCallEndSource(signal.callId, 'remote', session.phase, { signal: 'BUSY' })
          clearRingTimer()
          finishCall('busy')
          if (currentUserId && !isSiblingDevice && !isSiblingRelaySignal(signal)) {
            relayTerminalToSiblingDevices(signal, currentUserId)
          }
          await disposeAdapter()
          break
        case 'REJECT':
          recordCallEndSource(signal.callId, 'remote', session.phase, { signal: 'REJECT' })
          clearRingTimer()
          finishCall('declined')
          if (currentUserId && !isSiblingDevice && !isSiblingRelaySignal(signal)) {
            relayTerminalToSiblingDevices(signal, currentUserId)
          }
          await disposeAdapter()
          break
        case 'ACCEPT':
          if (session.phase === 'active') return
          if (session.phase !== 'outgoing' && session.phase !== 'connecting') return
          if (isCallTombstoned(session.callId)) {
            recordCallFlow('ctrl:accept:endedCall', { phase: session.phase }, { callId: session.callId })
            void terminateCall('cancelled', undefined, undefined, { endPayloadReason: 'CANCELLED' })
            return
          }
          // Ring-timeout hygiene: the callee answered — cancel the caller-side
          // 45s MISSED timer explicitly (the phase change below also disarms
          // its phase guard, but never leave a live END(MISSED) timer racing an
          // answered call — that race is what killed the 2026-07-06 calls).
          clearRingTimer()
          // Urgent leadership ring answered by the PRIMARY invitee (same
          // callId as the session, so the fan-out ACCEPT branch above never
          // ran): drop the "Leadership team" label + dialling notice so the
          // connected call shows who picked up, and release the other invites.
          if (session.ringGroupName || leadershipRingRef.current) {
            const ring = leadershipRingRef.current
            if (ring) {
              leadershipRingRef.current = null
              for (const inv of ring.invites) {
                if (inv.callId === signal.callId) continue
                void publishVoiceCallSignal({
                  callId: inv.callId,
                  type: 'BUSY',
                  callerId: session.callerId,
                  calleeId: inv.peerUserId,
                  conversationId: session.conversationId,
                }).catch(() => {})
              }
            }
            patchVoiceCallSession({ ringGroupName: null, notice: undefined })
          }
          patchVoiceCallSession({ phase: 'connecting' })
          // Answered: the native DIALING stall backstop must not end it while media connects.
          void noteNativeOutgoingAnswered(session.callId)
          try {
            await joinChimeMeeting(
              session.callId,
              localAttendeeDisplayName(currentUserId),
              session.mediaType ?? 'voice',
            )
            startHeartbeatLoop()
          } catch (err) {
            void terminateCall(
              'error',
              err instanceof Error ? err.message : 'Failed to join call',
              undefined,
              { endPayloadReason: 'CANCELLED' },
            )
          }
          break
        case 'END': {
          recordCallEndSource(signal.callId, 'remote', session.phase, { signal: 'END' })
          dismissIncomingRingSurface(signal.callId)
          const phaseAtEnd = session.phase
          const endPayload = parseSignalPayload<{
            reason?: string
            detail?: string
          }>(signal.payload)
          const reason = (endPayload?.reason ?? '').toUpperCase()
          if (reason === 'INTERRUPTED') {
            finishCall(
              'interrupted',
              endPayload?.detail ?? 'Connection interrupted',
              endPayload?.detail ?? 'User closed the window',
            )
          } else if (reason === 'MISSED') {
            finishCall('missed')
          } else if (reason === 'CANCELLED') {
            finishCall('cancelled')
          } else {
            // The peer's client ended it because WE vanished (peer-lost END):
            // completed for the record, but not a hang-up.
            finishCall(
              phaseAtEnd === 'active' ? 'completed' : 'cancelled',
              undefined,
              undefined,
              endPayload?.detail === PEER_LOST_END_DETAIL ? 'peer-lost' : undefined,
            )
          }
          if (
            currentUserId &&
            !isSiblingDevice &&
            !isSiblingRelaySignal(signal) &&
            shouldRelayPeerTerminal(signal.type)
          ) {
            relayTerminalToSiblingDevices(signal, currentUserId)
          }
          await disposeAdapter()
          break
        }
        default:
          break
      }
    },
    [
      abandonLocalIncomingRing,
      clearHeartbeatTimers,
      clearRingTimer,
      currentUserId,
      disposeAdapter,
      handleConsentNotRecorded,
      handleConsentWindowEnd,
      joinChimeMeeting,
      sendEnd,
      showNotice,
      startHeartbeatLoop,
      terminateCall,
    ],
  )

  // Incoming-call signaling subscription. Amplify's AppSync subscription can
  // silently drop (network change, long background, idle WebSocket) and does
  // NOT always re-establish on its own — when that happens the device simply
  // stops ringing. We add explicit reconnect-with-backoff plus an immediate
  // re-subscribe on app foreground / network online so web→app rings keep
  // arriving reliably.
  useEffect(() => {
    if (!currentUserId) return

    let cancelled = false
    let sub: SubscriptionHandle | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0

    const clearReconnect = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
    }

    const scheduleReconnect = () => {
      if (cancelled || reconnectTimer) return
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5))
      attempt += 1
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        connect()
      }, delay)
    }

    const connect = () => {
      if (cancelled) return
      try {
        sub?.unsubscribe()
      } catch {
        /* already closed */
      }
      const obs = graphqlSubscription(onVoiceCallSignalSubscription, {
        recipientUserId: currentUserId,
      }) as VoiceCallSubscription

      sub = obs.subscribe({
        next: (event) => {
          if (cancelled) return
          attempt = 0 // healthy delivery resets backoff
          const signal = event.data?.onVoiceCallSignal
          if (signal) void handleRemoteSignal(signal)
        },
        error: (err) => {
          if (cancelled) return
          console.warn('[voice-call] subscription error — reconnecting', err)
          scheduleReconnect()
        },
      })
    }

    // Force an immediate reconnect when the app returns to the foreground or
    // the network comes back, instead of waiting out the backoff.
    const reconnectNow = () => {
      if (cancelled) return
      clearReconnect()
      attempt = 0
      connect()
    }

    connect()

    const onOnline = () => reconnectNow()
    window.addEventListener('online', onOnline)

    let removeResume: (() => void) | null = null
    void (async () => {
      try {
        const { App } = await import('@capacitor/app')
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) reconnectNow()
        })
        if (cancelled) void handle.remove()
        else removeResume = () => void handle.remove()
      } catch {
        /* @capacitor/app unavailable (web) — `online` + visibility cover it */
      }
    })()

    const onVisible = () => {
      if (document.visibilityState === 'visible') reconnectNow()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      clearReconnect()
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
      removeResume?.()
      try {
        sub?.unsubscribe()
      } catch {
        /* already closed */
      }
    }
  }, [currentUserId, handleRemoteSignal])

  // ACCEPT delivery fallback (belt-and-braces): while WE are the caller of an
  // outgoing 1:1 ring, poll the backend's durable call state every few seconds.
  // The primary answer path is the onVoiceCallSignal subscription relay of the
  // callee's ACCEPT — but that is a single, unacknowledged WebSocket delivery.
  // If it is lost (dead/reconnecting subscription, transient network, or a
  // publisher problem — the 2026-07-06 incident where the native-ack ACCEPT was
  // published but never delivered), the caller rings blind into the 45s MISSED
  // timeout while the callee sits in a locally-"connected" call. The accept
  // claim row is durable, so polling myActiveCall converts any missed ACCEPT
  // into a normal connect within one poll interval.
  useEffect(() => {
    if (!currentUserId) return
    let polling = false
    const interval = setInterval(async () => {
      if (polling) return
      const session = getVoiceCallSession()
      if (
        !session ||
        session.phase !== 'outgoing' ||
        session.isGroup ||
        session.callerId !== currentUserId ||
        leadershipRingRef.current !== null ||
        // Give the normal subscription path first shot at a fresh ring.
        // (startedAt is set on every outgoing ring; treat a missing value as
        // "old enough to poll".)
        Date.now() - (session.startedAt ?? 0) < 3_000
      ) {
        return
      }
      polling = true
      try {
        const active = await fetchMyActiveCall()
        const live = getVoiceCallSession()
        if (
          !active?.accepted ||
          !live ||
          live.callId !== session.callId ||
          live.phase !== 'outgoing' ||
          active.callId !== live.callId
        ) {
          return
        }
        // The callee answered but the ACCEPT signal never reached us — connect
        // exactly as the ACCEPT handler would.
        clearRingTimer()
        patchVoiceCallSession({ phase: 'connecting' })
        try {
          await joinChimeMeeting(
            live.callId,
            localAttendeeDisplayName(currentUserId),
            live.mediaType ?? 'voice',
          )
          startHeartbeatLoop()
        } catch (err) {
          void terminateCall(
            'error',
            err instanceof Error ? err.message : 'Failed to join call',
            undefined,
            { endPayloadReason: 'CANCELLED' },
          )
        }
      } catch {
        /* poll is best-effort — the subscription and ring timer still govern */
      } finally {
        polling = false
      }
    }, 4_000)
    return () => clearInterval(interval)
  }, [clearRingTimer, currentUserId, joinChimeMeeting, startHeartbeatLoop, terminateCall])

  // Connecting-stall watchdog: 'connecting' has NO timer guarding it — if the
  // Chime join silently aborts or hangs (join-token fetch stall, an abort-guard
  // early return after a rapid dispose→redial like a blind transfer), the user
  // sits on "Connecting…" + dial tone FOREVER while the peer waits alone in the
  // meeting until their heartbeat kills the call (the 2026-07-07 transfer
  // incident). Detect a stall (still 'connecting', no live join in flight, media
  // not connected), retry the join once with fresh credentials, and if it still
  // can't connect end the call with a real error instead of ringing blind.
  useEffect(() => {
    if (!currentUserId) return
    let stalledSinceMs: number | null = null
    let retriedCallId: string | null = null
    let connectingSinceCallId: string | null = null
    let connectingSinceMs: number | null = null
    let nativeCheckInFlight = false
    const interval = setInterval(() => {
      const session = getVoiceCallSession()
      // Group joins go through joinGroupMeeting (room-keyed credentials) — the
      // 1:1 join retry below would request a token for the wrong meeting.
      if (!session || session.phase !== 'connecting' || session.isGroup) {
        stalledSinceMs = null
        connectingSinceMs = null
        connectingSinceCallId = null
        return
      }
      // NATIVE-MEDIA path (iOS locked CallKit answer): this call's audio is served
      // by the native Chime SDK (ChimeMediaController), NOT the JS adapter — so
      // `callAdapterRef.current?.isConnected()` is ALWAYS null here and the JS
      // dispose()+WebView-rejoin below would wrongly tear down a native session
      // that is still legitimately connecting. That is exactly the +15s slow-
      // connect bug (the watchdog fired at +15.4s and rejoined over the WebView).
      // Evaluate the stall from the NATIVE session state instead, and NEVER
      // disposeAdapter / WebView-rejoin on this path.
      if (nativeMediaActiveRef.current === session.callId) {
        if (connectingSinceCallId !== session.callId) {
          connectingSinceCallId = session.callId
          connectingSinceMs = Date.now()
        }
        const connectingForMs =
          connectingSinceMs !== null ? Date.now() - connectingSinceMs : 0
        if (!nativeCheckInFlight) {
          nativeCheckInFlight = true
          void getNativeIosMediaActiveCall()
            .then((native) => {
              nativeCheckInFlight = false
              const live = getVoiceCallSession()
              if (
                !live ||
                live.callId !== session.callId ||
                live.phase !== 'connecting' ||
                live.isGroup
              ) {
                return
              }
              if (native?.remotePresent) {
                // Two-way media is really up on the native path — the canonical
                // connect (roster event was missed / raced). Advance rather than
                // strand the user on "Connecting…".
                recordCallFlow(
                  'ctrl:connectStall:nativePromote',
                  { connected: native.connected, remotePresent: native.remotePresent },
                  { callId: live.callId },
                )
                onAdapterRemotePresent()
                return
              }
              // Absolute cap: 'connecting' must NEVER persist. Native recovery does
              // NOT rejoin over the WebView — either it connected (handled above) or
              // we end with a real error after the cap.
              if (connectingForMs > 30_000) {
                recordCallFlow(
                  'ctrl:connectStall:nativeTimeout',
                  { hasNativeSession: Boolean(native), connected: native?.connected ?? null },
                  { callId: live.callId },
                )
                void terminateCall(
                  'error',
                  'Could not connect the call — please try again.',
                  undefined,
                  { endPayloadReason: 'INTERRUPTED' },
                )
              }
            })
            .catch(() => {
              nativeCheckInFlight = false
            })
        }
        stalledSinceMs = null
        return
      }
      if (connectingSinceCallId !== session.callId) {
        connectingSinceCallId = session.callId
        connectingSinceMs = Date.now()
      }
      const connectingForMs =
        connectingSinceMs !== null ? Date.now() - connectingSinceMs : 0
      const remotePresent = callAdapterRef.current?.hasRemotePresence() ?? false
      const stallAction = decideConnectingStall({
        connectingForMs,
        hardCapMs: 30_000,
        stallForMs: stalledSinceMs === null ? 0 : Date.now() - stalledSinceMs,
        stallRetryAfterMs: 8_000,
        mediaConnected: callAdapterRef.current?.isConnected() ?? false,
        joinInFlight: joiningRef.current,
        alreadyRetried: retriedCallId === session.callId,
        documentHidden: isDocumentHidden(),
        // Caller sitting alone in Chime after a false ACCEPT (wrong-account
        // ack) must not promote to active / COMPLETED with no remote audio.
        requireRemote: session.localRole === 'caller',
        remotePresent,
      })
      if (stallAction === 'wait-visible') {
        // Freeze the cap clock while the WebView cannot capture.
        stalledSinceMs = null
        connectingSinceMs = Date.now()
        return
      }
      if (stallAction === 'promote') {
        recordCallFlow(
          'ctrl:connectStall:promote',
          { connectingForMs },
          { callId: session.callId },
        )
        connectingSinceMs = null
        connectingSinceCallId = null
        stalledSinceMs = null
        promoteCallToActive()
        return
      }
      if (stallAction === 'end') {
        connectingSinceMs = null
        connectingSinceCallId = null
        stalledSinceMs = null
        recordCallFlow(
          'ctrl:connectStall:end',
          {
            connectingForMs,
            localRole: session.localRole,
            remotePresent,
          },
          { callId: session.callId },
        )
        void terminateCall(
          'error',
          'Could not connect the call — please try again.',
          undefined,
          { endPayloadReason: 'INTERRUPTED' },
        )
        return
      }
      if (stallAction === 'keep') {
        if (joiningRef.current || (callAdapterRef.current?.isConnected() ?? false)) {
          stalledSinceMs = null
        } else if (stalledSinceMs === null) {
          stalledSinceMs = Date.now()
        }
        return
      }
      // retry
      retriedCallId = session.callId
      stalledSinceMs = null
      // Dispose first: the stalled attempt may have left a half-initialized
      // adapter (or an unreleased mic stream) that would wedge the retry too.
      void disposeAdapter()
        .then(() =>
          joinChimeMeeting(
            session.callId,
            localAttendeeDisplayName(currentUserId),
            session.mediaType ?? 'voice',
          ),
        )
        .then(() => startHeartbeatLoop())
        .catch((err) => {
          void terminateCall(
            'error',
            err instanceof Error ? err.message : 'Failed to join call',
            undefined,
            { endPayloadReason: 'INTERRUPTED' },
          )
        })
    }, 2_000)
    return () => clearInterval(interval)
  }, [
    currentUserId,
    disposeAdapter,
    joinChimeMeeting,
    promoteCallToActive,
    onAdapterRemotePresent,
    startHeartbeatLoop,
    terminateCall,
  ])

  useEffect(() => {
    const interruptActiveCall = (detail: string) => {
      const session = getVoiceCallSession()
      if (!session || isTerminalCallPhase(session.phase)) return
      if (session.phase === 'incoming' || session.phase === 'outgoing') return
      // Closing a tab that is only showing the Switch banner must NEVER end
      // the live call on the other device.
      if (session.phase === 'elsewhere') return

      void publishVoiceCallEndKeepalive({
        callId: session.callId,
        callerId: session.callerId,
        calleeId: session.calleeId,
        conversationId: session.conversationId,
        // Anchor the stored duration to the canonical connect (talk time), same
        // as terminateCall; falls back to ring start if it never connected.
        payload: buildCallEndPayload(
          'INTERRUPTED',
          session.connectedAt ?? session.startedAt,
          detail,
        ),
      })
      finishCall('interrupted', 'Connection interrupted', detail)
      callAdapterRef.current?.disposeSync()
      void disposeAdapter()
    }

    const onPageHide = () => {
      // On the native app, `pagehide` fires when the screen turns off, the
      // phone is held to the ear (proximity), or the app is backgrounded —
      // none of which should end an in-progress call. The ongoing-call
      // foreground service + wake locks keep the Chime session alive. Only
      // the web (closing the tab/window) should tear the call down here.
      if (isNativeApp()) return
      const session = getVoiceCallSession()
      if (
        session &&
        (session.phase === 'active' || session.phase === 'connecting')
      ) {
        // Primary release: drop THIS device's claim immediately so a sibling
        // cannot prompt "switch" after the tab is gone. END (below) settles
        // the SESSION row; the freshness window is only the crash backstop.
        const deviceId = getDeviceId()
        if (deviceId) {
          void publishDeviceClaimKeepalive({
            callId: session.callId,
            callerId: session.callerId,
            calleeId: session.calleeId,
            conversationId: session.conversationId,
            platform: getNativePlatform(),
            deviceId,
          })
        }
      }
      interruptActiveCall('User left the page')
    }

    const onVisibilityChange = () => {
      const session = getVoiceCallSession()
      if (session?.phase === 'ended') {
        void disposeAdapter()
      }
    }

    // `beforeunload` never fires inside the native WebView; keep it web-only
    // alongside `pagehide` so a real browser unload still cleans up the call.
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('beforeunload', onPageHide)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearRingTimer()
      clearHeartbeatTimers()
      clearDisconnectGrace()
      clearMediaSwitchTimer()
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('beforeunload', onPageHide)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      disposeFallbackAudioElement(fallbackAudioRef.current)
      fallbackAudioRef.current = null
      disposeFallbackVideoElement(fallbackLocalVideoRef.current)
      disposeFallbackVideoElement(fallbackRemoteVideoRef.current)
      fallbackLocalVideoRef.current = null
      fallbackRemoteVideoRef.current = null
      void disposeAdapter()
    }
  }, [clearDisconnectGrace, clearHeartbeatTimers, clearMediaSwitchTimer, clearRingTimer, disposeAdapter])

  const startCallWithMedia = useCallback(
    async (
      peer: ChatUser,
      mediaType: CallMediaType,
      conversationId?: string | null,
      caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
      options?: StartCallOptions,
    ) => {
      if (!currentUserId) throw new Error('Not signed in')
      if (peer.userId === currentUserId) throw new Error('Cannot call yourself')
      // Soft-lock (13B): outbound start blocked until unlock; incoming Answer is
      // acceptCall and is intentionally not gated here. Car template UI must not
      // prompt on the phone.
      if (!options?.fromCar && isAppLocked()) {
        const unlocked = await ensureSoftUnlocked()
        if (!unlocked) throw new Error("Verify it's you before starting a call")
      }
      const existing = getVoiceCallSession()
      if (existing && existing.phase !== 'ended' && existing.phase !== 'idle') {
        throw new Error('Already in a call')
      }
      // Remote decline / busy must never auto-redial a *new* callId (Zach→Olivia
      // 2026-08-19: REJECT then a fresh INVITE 1.3s later). Tombstones only
      // block rehydrate of the ended callId.
      if (isOutboundRedialBlocked(peer.userId)) {
        recordCallFlow('ctrl:startCall:blockedRedial', { peerId: peer.userId, mediaType })
        return
      }

      // Recording-policy gate (Members/Independents): the first OUTGOING call
      // shows the recording notice BEFORE dialing — same one-time acceptance
      // as the incoming path (one consent, either direction). Accept proceeds
      // with THIS call (no redial); decline durably blocks calling both ways
      // with an explicit explanation (also enforced server-side on INVITE).
      if (!options?.fromCar && !options?.bypassRecordingPolicyGate && needsOutgoingRecordingPolicyGate()) {
        setPendingRecordingPolicyGate({
          peer,
          mediaType,
          proceed: () => {
            void startCallWithMediaRef.current?.(peer, mediaType, conversationId, caller, {
              ...options,
              bypassRecordingPolicyGate: true,
            })
          },
        })
        return
      }

      // Status-aware calling: for a normal 1:1 call, check the target's live
      // status. If they're away/offline/DND, steer the caller to messaging
      // (block) or warn them the target may not answer (warn). Urgent / on-call
      // rings always go through. `bypassGate` is set by the dialog's "Call
      // anyway" to re-enter without re-prompting.
      if (!options?.fromCar && !options?.bypassGate && (options?.callPurpose ?? 'normal') === 'normal') {
        const eligibility = evaluateCallEligibility(
          buildCallTargetContext(peer),
          getCallEligibilityConfig(),
        )
        if (eligibility.severity !== 'allow') {
          setPendingCallGate({
            peer,
            mediaType,
            conversationId: conversationId ?? null,
            result: eligibility,
            proceed: () => {
              void startCallWithMediaRef.current?.(peer, mediaType, conversationId, caller, {
                ...options,
                bypassGate: true,
              })
            },
          })
          return
        }
      }

      recordUserActivity({ force: true })

      lastRecallPeerRef.current = peer
      lastRecallConversationRef.current = conversationId ?? null
      lastRecallMediaRef.current = mediaType

      const callId = createCallId()
      const carTapAt = options?.fromCar ? (options.carTapAt ?? Date.now()) : null
      callAudioRouteArbiter.setNative(isNativeApp())
      callAudioRouteArbiter.begin(mediaType === 'video' ? 'video' : 'voice', 'outgoing-ringback')
      // This dial is reported to Telecom below: its ringback waits for that.
      expectNativeDialReport(callId)
      setVoiceCallSession({
        callId,
        callerId: currentUserId,
        calleeId: peer.userId,
        conversationId,
        localRole: 'caller',
        peer: peerFromUser(peer),
        phase: 'outgoing',
        mediaType,
        isMuted: false,
        isCameraOff: false,
        isSpeakerOn: callAudioRouteArbiter.desiredSpeakerOn(),
        startedAt: Date.now(),
        callPurpose: options?.callPurpose ?? 'normal',
        urgentLocation: options?.urgentLocation ?? null,
        sessionUuid: options?.sessionUuid ?? null,
        carTapAt,
      })
      carDialRef.current = carTapAt != null ? { callId, tapAt: carTapAt } : null
      setCallFlowCall(callId)
      void recordNativeCarRecent({ peerId: peer.userId, peerName: peer.displayName })
      recordCallFlow(
        'ctrl:startCall',
        { mediaType, peerId: peer.userId, callPurpose: options?.callPurpose ?? 'normal' },
        { callId },
      )

      const permissions = await ensurePermissionsWithPromptNotice(callId, {
        video: mediaType === 'video',
      })
      if (permissions.microphone === 'denied') {
        clearRingTimer()
        finishCall('error', microphoneDeniedMessage())
        await disposeAdapter()
        return
      }

      // Prepare + route the native audio session for dialing and restart the
      // ringback on the correct output. A VOICE call routes to the EARPIECE
      // (defaultSpeakerOn('voice') === false) so the dial tone is held to the ear
      // rather than blasted through the loud speaker (the reported bug); a video
      // call routes to the speaker. We do NOT use the boosted in-call voice path
      // for a media/speaker ring — see prepareNativeRingback. The real in-call
      // route is applied when Chime joins (chime-call-adapter).
      //
      // Sequenced + re-kicked: the getUserMedia permission probe above flipped
      // the native session, which silently killed/rerouted the dial tone the
      // ringtone hook already started on the phase→outgoing change. Re-prep with
      // the desired route and restart HTML5 only when native ringback is NOT
      // already playing — since #359/#367 the ringtone hook owns outgoing
      // ringback via playRingbackTone, and an unconditional HTML5 re-kick
      // overlaps it whenever the OS does not mute WebView audio.
      const dialPlan = await prepareOutgoingDialPlan(mediaType === 'video' ? 'video' : 'voice', {
        callId,
      })
      // LIVENESS RE-CHECK: the awaits above can outlast a hang-up (car End,
      // Telecom abort, in-app End while the WebView was busy). Only the
      // still-live dial may create the native ongoing call — otherwise the
      // ended call was resurrected in Telecom / on the car (2026-10-19).
      // A car End tapped before this dial had a callId cancels it here, before
      // the native report and before any INVITE is published.
      const carCancelled = Boolean(
        options?.fromCar && isNativeCarStartCancelledSince(options.carTapAt),
      )
      if (carCancelled || !isOutgoingDialStillLive(getVoiceCallSession(), callId, isCallTombstoned)) {
        const live = getVoiceCallSession()
        recordCallFlow(
          'ctrl:startCall:endedDuringPrep',
          { phase: live?.callId === callId ? live.phase : null, carCancelled },
          { callId },
        )
        clearRingTimer()
        if (live?.callId === callId && !isTerminalCallPhase(live.phase)) {
          finishCall('cancelled')
          await disposeAdapter()
        }
        void endNativeOutgoingCall(callId, 'canceled', carTapAt)
        return
      }
      if (isNativeApp()) {
        const ringbackSpeakerOn = desiredCallSpeakerOn()
        // Android: no pre-dial route prep / HTML5 re-kick — the ringtone hook
        // starts the native ringback once Telecom has the call; a prep here
        // set the audio mode before placeCall (2026-10-20 car dial tone).
        if (getNativePlatform() !== 'android') {
          void prepareNativeRingback(ringbackSpeakerOn).then(() => {
            const live = getVoiceCallSession()
            if (live?.callId === callId && live.phase === 'outgoing') {
              const plan = getActiveOutgoingDialPlan()
              rekickHtml5OutgoingDialTone({
                callId,
                playUrl: plan?.playUrl,
                repeatCount: plan?.repeatCount,
              })
            }
          })
        }
        patchVoiceCallSession({ isSpeakerOn: ringbackSpeakerOn })
        void startNativeOngoingCall({
          video: mediaType === 'video',
          peerName: resolveTelecomPeerName({
            displayName: peer.displayName,
            directoryName: getCachedDirectoryUser(peer.userId)?.displayName,
            isPlaceholder: isPlaceholderCallerName,
          }),
          callId,
          peerId: peer.userId,
          group: false,
          // Real outgoing dial: Telecom owns ringback (DIALING) until the org
          // tone starts, then playRingbackTone hands off (single tone owner).
          dialing: true,
          fromCar: Boolean(options?.fromCar),
        }).then((result) => {
          const reason = result?.refused ? result.reason : undefined
          if (reason !== 'tombstoned' && reason !== 'car-ended') return
          // Native already ended this dial (or the driver tapped End on the
          // car before it had a callId) — hang up so the peer gets CANCELLED.
          recordCallFlow('ctrl:startCall:nativeRefused', { reason }, { callId })
          const current = getVoiceCallSession()
          if (current?.callId === callId && !isTerminalCallPhase(current.phase)) {
            void hangUpRef.current({ endSource: reason === 'car-ended' ? 'car' : 'native-teardown' })
          }
        })
      }
      if (mediaType === 'video' && permissions.camera === 'denied') {
        clearRingTimer()
        finishCall('error', cameraDeniedMessage())
        await disposeAdapter()
        return
      }

      // PHANTOM-RING GUARD (call 64b19aeb, 2026-10-07): the awaits above can
      // freeze for MINUTES when the app is backgrounded mid-dial (Android
      // suspended Zach's WebView inside the permission probe for 11 minutes
      // while he drove). On resume this flow continues where it stopped and
      // would publish an INVITE nobody is waiting on — the callee rings for a
      // call the caller has long abandoned. Abort instead of inviting when the
      // dial is older than the ring window or the session has moved on.
      if (
        shouldAbortStaleOutgoingInvite({
          session: getVoiceCallSession(),
          callId,
          now: Date.now(),
        })
      ) {
        const live = getVoiceCallSession()
        recordCallFlow(
          'ctrl:startCall:staleInviteAborted',
          { elapsedMs: Date.now() - (live?.startedAt ?? 0), phase: live?.phase ?? null },
          { callId },
        )
        clearRingTimer()
        // Only tear down when the stale session is still OURS — if another
        // call already started, finishCall/disposeAdapter would kill it.
        if (live?.callId === callId && !isTerminalCallPhase(live.phase)) {
          finishCall('cancelled')
          await disposeAdapter()
        }
        return
      }

      clearRingTimer()
      ringTimerRef.current = setTimeout(() => {
        const current = getVoiceCallSession()
        if (current?.callId === callId && current.phase === 'outgoing') {
          const endReason = resolveUnansweredEndReason(current.startedAt, new Date(), 'MISSED')
          recordCallEndSource(callId, 'timeout', current.phase)
          void sendEnd(callId, currentUserId, peer.userId, conversationId, endReason, current.startedAt)
          finishCall(endReason === 'MISSED' ? 'missed' : 'cancelled')
          void disposeAdapter()
        }
      }, dialPlan.hangupTimeoutMs)

      // Kick off Chime meeting creation + caller attendee IMMEDIATELY, before
      // (and concurrently with) the INVITE round-trip. Meeting creation is the
      // long pole (CreateMeeting + media-capture pipeline start, ~1s) and the
      // INVITE handler itself awaits push delivery, so overlapping them means
      // the meeting is already live by the time the callee answers — the callee
      // joins an existing meeting instead of racing its creation. Best-effort
      // (.catch → null); consumed by joinChimeMeeting on ACCEPT.
      prewarmedJoinRef.current = {
        callId,
        promise: chimeService
          .requestJoinToken({
            externalMeetingId: callId,
            attendeeDisplayName: localAttendeeDisplayName(currentUserId) ?? peer.displayName,
            enableVideo: mediaType === 'video',
            // The callee hasn't seen the recording notice yet — recording must
            // not start with this (meeting-creating) prewarm.
            deferRecordingCapture: shouldDeferRecordingCapture({ roleType: peer.roleType ?? null }),
          })
          .catch(() => null),
      }

      try {
        await publishVoiceCallSignal({
          callId,
          type: 'INVITE',
          callerId: currentUserId,
          calleeId: peer.userId,
          conversationId,
          payload: {
            // Car-originated dials pass no `caller` profile — fall back to the
            // cached signed-in user before the generic label, so the callee is
            // never shown "Someone" for an identified caller. The server also
            // stamps the roster displayName over placeholders on relay.
            callerDisplayName:
              caller?.displayName?.trim() ||
              [caller?.givenName, caller?.familyName].filter(Boolean).join(' ') ||
              cachedSelfChatUser(currentUserId)?.displayName?.trim() ||
              'Someone',
            callerAvatarUrl: caller?.avatarUrl ?? null,
            mediaType,
            callPurpose: options?.callPurpose ?? 'normal',
            urgentLocation: options?.urgentLocation ?? null,
            // Car-originated dial (CarPlay / Android Auto tap-to-call): the car
            // templates skip the client recording-policy gate, so the server
            // INVITE gate blocks UNDECIDED callers on this flag (#342).
            ...(options?.fromCar ? { fromCar: true } : {}),
            // Telehealth session call: the server validates the session link.
            ...(options?.sessionUuid ? { sessionUuid: options.sessionUuid } : {}),
          },
        })
      } catch (err) {
        // A redial may own the prewarm / media by now — only this dial's are dropped.
        if (prewarmedJoinRef.current?.callId === callId) prewarmedJoinRef.current = null
        const live = getVoiceCallSession()
        const endedLocally = !live || live.callId !== callId || isTerminalCallPhase(live.phase)
        if (!live || live.callId === callId) {
          void disposeAdapter({ callId, carTapAt })
          setVoiceCallSession(null)
        }
        // The DIALING Telecom connection would otherwise sit until the 118 s
        // stall teardown (car shows a dial that no longer exists).
        void endNativeOutgoingCall(callId, 'canceled', carTapAt)
        // Hung up here (car End, in-app End) while the INVITE was in flight
        // and the server already holds the end: nothing failed.
        if (endedLocally && isCallAlreadyEndedError(err)) return
        // Consent BEFORE connect: the server refused to place the call because
        // a member party hasn't accepted call recording.
        const rejection = recordingConsentRejectionFromError(err)
        if (rejection?.code === RECORDING_CONSENT_REQUIRED_SELF && !options?.fromCar) {
          if (options?.recordingConsentRetried) {
            throw new Error(recordingConsentRequiredSelfMessage())
          }
          // Our own consent is missing (race, withdrawn on another device, or
          // a stale local copy): re-read the account, then show the same
          // pre-dial prompt and place THIS call only after they accept.
          recordCallFlow('ctrl:startCall:consentRequiredSelf', { peerId: peer.userId })
          await loadRecordingPolicyConsent().catch(() => undefined)
          setPendingRecordingPolicyGate({
            peer,
            mediaType,
            proceed: () => {
              void startCallWithMediaRef.current?.(peer, mediaType, conversationId, caller, {
                ...options,
                bypassRecordingPolicyGate: true,
                recordingConsentRetried: true,
              })
            },
          })
          return
        }
        if (rejection?.code === RECORDING_CONSENT_REQUIRED_PEER) {
          // The callee hasn't accepted: nothing rang. Tell the caller and offer
          // a message. Still throw so a caller that tracks failed attempts
          // (telehealth "Start call") sees a failed attempt.
          const firstName = rejection.firstName || consentFirstName(peer)
          recordCallFlow('ctrl:startCall:consentRequiredPeer', { peerId: peer.userId })
          showRecordingConsentPeerNotice({
            kind: 'peer',
            people: [{ userId: peer.userId, firstName }],
          })
          throw new PeerRecordingConsentRequiredError(firstName)
        }
        throw err
      }
    },
    [clearRingTimer, currentUserId, disposeAdapter, ensurePermissionsWithPromptNotice, sendEnd],
  )
  startCallWithMediaRef.current = startCallWithMedia

  const startCall = useCallback(
    async (
      peer: ChatUser,
      conversationId?: string | null,
      caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
      options?: StartCallOptions,
    ) => startCallWithMedia(peer, 'voice', conversationId, caller, options),
    [startCallWithMedia],
  )

  const startVideoCall = useCallback(
    async (
      peer: ChatUser,
      conversationId?: string | null,
      caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
      options?: StartCallOptions,
    ) => startCallWithMedia(peer, 'video', conversationId, caller, options),
    [startCallWithMedia],
  )

  /** Ring all leadership providers at once; first to accept wins. */
  const startLeadershipUrgentRing = useCallback(
    async (
      peers: ChatUser[],
      conversationId?: string | null,
      caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
      options?: StartCallOptions,
    ) => {
      if (!currentUserId) throw new Error('Not signed in')
      if (peers.length === 0) throw new Error('No leadership contacts available')
      if (isAppLocked()) {
        const unlocked = await ensureSoftUnlocked()
        if (!unlocked) throw new Error("Verify it's you before starting a call")
      }
      const existing = getVoiceCallSession()
      if (existing && existing.phase !== 'ended' && existing.phase !== 'idle') {
        throw new Error('Already in a call')
      }

      // Same pre-dial recording-policy gate as a normal outgoing call — an
      // urgent ring is still an outgoing call the policy covers. Accept
      // proceeds with the SAME ring (no redial).
      if (!options?.fromCar && !options?.bypassRecordingPolicyGate && needsOutgoingRecordingPolicyGate()) {
        setPendingRecordingPolicyGate({
          // The whole leadership team is rung — never name one member in the
          // gate copy ("Before your call to Ania"). Null = generic copy.
          peer: null,
          mediaType: 'voice',
          proceed: () => {
            void startLeadershipUrgentRingRef.current?.(peers, conversationId, caller, {
              ...options,
              bypassRecordingPolicyGate: true,
            })
          },
        })
        return
      }

      recordUserActivity({ force: true })

      const invites = peers.map((peer) => ({
        callId: createCallId(),
        peerUserId: peer.userId,
        peer: peerFromUser(peer),
      }))
      const primary = invites[0]
      const primaryPeer = peers[0]

      leadershipRingRef.current = {
        invites,
        responded: new Set(),
        conversationId,
      }

      setVoiceCallSession({
        callId: primary.callId,
        callerId: currentUserId,
        calleeId: primary.peerUserId,
        conversationId,
        localRole: 'caller',
        // Bookkeeping only (first invite) — the UI shows ringGroupName while
        // the ring is unanswered, never this member's name/avatar.
        peer: primary.peer,
        phase: 'outgoing',
        mediaType: 'voice',
        isMuted: false,
        isCameraOff: false,
        startedAt: Date.now(),
        callPurpose: 'urgent',
        urgentLocation: options?.urgentLocation ?? null,
        ringGroupName: LEADERSHIP_TEAM_DISPLAY_NAME,
        notice: `Calling leadership team (${peers.length})…`,
      })

      const permissions = await ensureCallPermissionsForCall({ video: false })
      if (permissions.microphone === 'denied') {
        leadershipRingRef.current = null
        finishCall('error', microphoneDeniedMessage())
        await disposeAdapter()
        return
      }

      const leadershipDialPlan = await prepareOutgoingDialPlan('voice', {
        callId: primary.callId,
      })
      clearRingTimer()
      ringTimerRef.current = setTimeout(() => {
        const current = getVoiceCallSession()
        if (current?.phase === 'outgoing' && leadershipRingRef.current) {
          for (const inv of leadershipRingRef.current.invites) {
            void publishVoiceCallSignal({
              callId: inv.callId,
              type: 'END',
              callerId: currentUserId,
              calleeId: inv.peerUserId,
              conversationId,
              payload: buildCallEndPayload('MISSED', current.startedAt),
            }).catch(() => {})
          }
          leadershipRingRef.current = null
          finishCall('missed')
          void disposeAdapter()
        }
      }, leadershipDialPlan.hangupTimeoutMs)

      // Same phantom-ring guard as startCallWithMedia: never publish the
      // urgent-ring INVITEs after a long WebView freeze in the awaits above.
      if (
        shouldAbortStaleOutgoingInvite({
          session: getVoiceCallSession(),
          callId: primary.callId,
          now: Date.now(),
        })
      ) {
        const live = getVoiceCallSession()
        recordCallFlow(
          'ctrl:leadershipRing:staleInviteAborted',
          { elapsedMs: Date.now() - (live?.startedAt ?? 0), phase: live?.phase ?? null },
          { callId: primary.callId },
        )
        clearRingTimer()
        leadershipRingRef.current = null
        if (live?.callId === primary.callId && !isTerminalCallPhase(live.phase)) {
          finishCall('cancelled')
          await disposeAdapter()
        }
        return
      }

      const callerName =
        caller?.displayName?.trim() ||
        [caller?.givenName, caller?.familyName].filter(Boolean).join(' ') ||
        cachedSelfChatUser(currentUserId)?.displayName?.trim() ||
        'Someone'

      try {
        await Promise.all(
          invites.map(async (inv) => {
            await publishVoiceCallSignal({
              callId: inv.callId,
              type: 'INVITE',
              callerId: currentUserId,
              calleeId: inv.peerUserId,
              conversationId,
              payload: {
                callerDisplayName: callerName,
                callerAvatarUrl: caller?.avatarUrl ?? null,
                mediaType: 'voice',
                callPurpose: 'urgent',
                urgentLocation: options?.urgentLocation ?? null,
                leadershipRing: true,
              },
            })
          }),
        )
      } catch (err) {
        const rejection = recordingConsentRejectionFromError(err)
        clearRingTimer()
        leadershipRingRef.current = null
        const live = getVoiceCallSession()
        const stillThisRing =
          live?.callId === primary.callId && !isTerminalCallPhase(live.phase)
        if (rejection?.code === RECORDING_CONSENT_REQUIRED_SELF && !options?.fromCar) {
          if (stillThisRing) {
            finishCall(
              options?.recordingConsentRetried ? 'error' : 'cancelled',
              options?.recordingConsentRetried ? recordingConsentRequiredSelfMessage() : undefined,
            )
          }
          await disposeAdapter()
          if (options?.recordingConsentRetried) return
          await loadRecordingPolicyConsent().catch(() => undefined)
          setPendingRecordingPolicyGate({
            peer: peers[0] ?? null,
            mediaType: 'voice',
            proceed: () => {
              void startLeadershipUrgentRingRef.current?.(peers, conversationId, caller, {
                ...options,
                bypassRecordingPolicyGate: true,
                recordingConsentRetried: true,
              })
            },
          })
          return
        }
        if (stillThisRing) {
          finishCall('error', err instanceof Error ? err.message : 'Could not start the call')
        }
        await disposeAdapter()
        return
      }

      prewarmedJoinRef.current = {
        callId: primary.callId,
        promise: chimeService
          .requestJoinToken({
            externalMeetingId: primary.callId,
            attendeeDisplayName: localAttendeeDisplayName(currentUserId) ?? primaryPeer.displayName,
            enableVideo: false,
            // Same consent-deferred capture as a normal 1:1 dial — the
            // answering provider's consent is unresolved at ring time.
            deferRecordingCapture: shouldDeferRecordingCapture({
              roleType: primaryPeer.roleType ?? null,
            }),
          })
          .catch(() => null),
      }
    },
    [clearRingTimer, currentUserId, disposeAdapter],
  )
  startLeadershipUrgentRingRef.current = startLeadershipUrgentRing

  const requestAcceptCall = useCallback((options?: AcceptCallOptions) => {
    void acceptCallRef.current(options)
  }, [])

  const cancelRecordingConsentRequest = useCallback(() => {
    const session = getVoiceCallSession()
    if (!session?.recordingConsentPending) return
    patchVoiceCallSession({ recordingConsentPending: false })
  }, [])

  const acceptCall = useCallback(async (options?: AcceptCallOptions) => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'incoming') return
    // Stamp "answered on THIS device" before any async work so the native-ack
    // ACCEPT echo this answer produces can never be mistaken for a sibling
    // device answering (see answeredHereCallIdRef).
    answeredHereCallIdRef.current = session.callId
    // The ring may have been labeled with a generic placeholder (ring-time
    // directory lookup failed / placeholder payload from an old backend) —
    // retry now so the in-call surfaces (return-to-call pill, overlay,
    // ongoing notification) show the real caller name for the answered call.
    upgradePlaceholderPeerName(session.callId)
    // The consent modal is the ONLY place consent is captured, and its confirm
    // button re-enters this function with the EXPLICIT `consentNoticeConfirmed`
    // option. Never infer confirmation from `recordingConsentPending` being
    // set: that inference let a double-tap on the incoming-screen Accept button
    // (tap 1 raised the modal, tap 2 re-entered milliseconds later) fabricate a
    // MODAL_CONFIRMED consent record — and the durable policy acceptance — for
    // a notice the user never saw.
    const consentNoticeConfirmed = options?.consentNoticeConfirmed === true
    // Durable acceptance ("one consent, either direction"): a user who already
    // confirmed the recording policy — on an earlier call in either direction
    // or from Settings — is not re-asked; their ACCEPT carries the PRIOR
    // consent record instead.
    const durableConsent = await resolveRecordingPolicyConsent(currentUserId)
    const hasDurableAcceptance = durableConsent.accepted
    // The account check may have waited on the server; the ring can end meanwhile.
    {
      const live = getVoiceCallSession()
      if (!live || live.callId !== session.callId || live.phase !== 'incoming') return
    }
    if (
      !consentNoticeConfirmed &&
      !options?.bypassRecordingConsent &&
      !hasDurableAcceptance
    ) {
      const { local, remote } = recordingConsentRolesForSession(session)
      if (requiresRecordingConsent(local, remote)) {
        // Idempotent: a repeat accept (double-tap, duplicated native answer
        // event) while the modal is already up keeps it up — it must never
        // count as the user confirming it.
        if (!session.recordingConsentPending) {
          recordCallFlow('ctrl:consent:noticeShown', {
            mediaType: session.mediaType ?? 'voice',
          }, { callId: session.callId })
        }
        patchVoiceCallSession({ recordingConsentPending: true })
        return
      }
    }
    if (consentNoticeConfirmed) {
      recordCallFlow('ctrl:consent:modalConfirmed', {
        mediaType: session.mediaType ?? 'voice',
      }, { callId: session.callId })
    }
    // A modal-confirmed accept ALSO stamps the durable per-user acceptance so
    // this user is never re-asked (and outgoing calls need no pre-dial gate).
    // Best-effort: the call itself must never hang on this profile write.
    if (consentNoticeConfirmed && !hasDurableAcceptance) {
      const { local } = recordingConsentRolesForSession(session)
      if (requiresRecordingPolicyAcceptance(local)) {
        void acceptRecordingPolicy().catch((err) =>
          console.warn('[recording-policy] durable accept stamp failed', err),
        )
      }
    }

    recordUserActivity({ force: true })

    clearRingTimer()
    // `answered`: clear the ring surfaces but PROMOTE the ringing Telecom
    // connection to ACTIVE (an in-app answer never passes through Telecom's
    // onAnswer, so without this the connection stayed RINGING and the default
    // cancel path destroyed it — then the keep-alive re-reported the call as
    // OUTGOING on the callee, the wrong-direction car/BT bug of 2026-09-16).
    clearCallNotification(session.callId, {
      answered: true,
      peerId: session.peer?.userId,
      peerName: session.peer?.displayName,
    })
    // Consume-side of the "notice owed" marker: a bypassed accept (iOS CallKit
    // lock-screen answer — no notice UI could be presented) joins the call with
    // the consent notice still OWED. Flag the session so the POST-CONNECT
    // consent sheet is shown the moment the call reaches `active`;
    // recording/transcription stay server-side deferred until it is confirmed.
    const owesPostConnectNotice = (() => {
      if (!options?.bypassRecordingConsent) return false
      const { local, remote } = recordingConsentRolesForSession(session)
      return owesPostConnectConsentNotice({
        bypassedNotice: true,
        hasDurableAcceptance,
        localUser: local,
        remoteUser: remote,
      })
    })()
    patchVoiceCallSession({
      phase: 'connecting',
      recordingConsentPending: false,
      ...(owesPostConnectNotice ? { recordingConsentOwedPostConnect: true } : {}),
    })

    // Invalidate any prewarmed JoinInfo that isn't for THIS call before we join.
    // A prior call's prewarm surviving into this accept is the stale-meeting
    // bug (2026-07-08); joinChimeMeeting also re-validates by ExternalMeetingId,
    // this is the belt to that suspenders.
    if (prewarmedJoinRef.current && prewarmedJoinRef.current.callId !== session.callId) {
      prewarmedJoinRef.current = null
    }

    // iOS CallKit lifecycle ping. Native setCallActive for native-media voice
    // answers RETAINS CallKit for the whole call (stay-alive — 2026-08-04
    // locked-answer "Audio ended"). Video / non-native still uses ring-only
    // handoff when Ascend is visible. Android's setCallActive drives the
    // Telecom timer and must stay on media-connected, so this is iOS-only.
    //
    // LOCKED / hidden WebView: skip — native mint/join already owns audio under
    // CallKit; an early bridge call is unnecessary.
    if (
      isNativeApp() &&
      getNativePlatform() === 'ios' &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      void setNativeCallActive(session.callId)
    }

    // Only an accept that went through the consent modal — now or on an
    // earlier call (durable acceptance) — may claim consent. Everything else —
    // the iOS CallKit bypass (no modal can be presented on a locked device)
    // and provider-to-provider calls (requiresRecordingConsent is false, so no
    // notice is shown at all) — sends the bypassed marker instead, so we never
    // assert consent the user was never asked for.
    const consentPayload = !consentNoticeConfirmed && hasDurableAcceptance
      ? buildPriorRecordingConsentAcceptPayload({
          acceptedAt: durableConsent.acceptedAt ?? new Date().toISOString(),
          noticeVersion: durableConsent.noticeVersion,
        })
      : buildRecordingConsentSignalPayload({
          noticeConfirmed: consentNoticeConfirmed,
          noticeSkipReason: options?.bypassRecordingConsent
            ? (() => {
                const { local, remote } = recordingConsentRolesForSession(session)
                return noticeSkipReasonForUnnoticedAnswer(local, remote)
              })()
            : 'NOT_REQUIRED',
        })

    // Group call: join the shared meeting, subscribe to the room channel, and
    // announce ourselves. The host is already in the meeting, so we don't gate
    // on an ACCEPT round-trip — we join directly. No 1:1 heartbeat loop (group
    // liveness comes from Chime attendee presence).
    if (session.isGroup) {
      try {
        if (!options?.skipPermissionPreflight) {
          const permissions = await ensurePermissionsWithPromptNotice(session.callId, {
            video: false,
          })
          if (permissions.microphone === 'denied') {
            await terminateCall('error', microphoneDeniedMessage())
            return
          }
        }
        await joinGroupMeetingRef.current(
          session.callId,
          localAttendeeDisplayName(currentUserId),
          session.mediaType ?? 'voice',
        )
        // Tell the caller's other devices + the host we answered (notif settle).
        await publishVoiceCallSignal({
          callId: session.callId,
          type: 'ACCEPT',
          callerId: session.callerId,
          calleeId: session.calleeId,
          conversationId: session.conversationId,
          payload: consentPayload,
        }).catch((err) => {
          ignoreForeignCallAck(err, session.callId, 'ACCEPT')
        })
        relayTerminalToSiblingDevices(
          {
            callId: session.callId,
            type: 'ACCEPT',
            callerId: session.callerId,
            calleeId: session.calleeId,
            conversationId: session.conversationId,
            payload: consentPayload,
          },
          session.calleeId,
        )
      } catch (err) {
        if (err instanceof CallFullError) {
          await terminateCall('error', err.message, undefined, {
            notifyPeer: false,
          })
          return
        }
        await terminateCall(
          'error',
          err instanceof Error ? err.message : 'Failed to join call',
          undefined,
          { notifyPeer: false },
        )
      }
      return
    }

    try {
      // The camera probe would open the camera on a locked phone (and its
      // prompt cannot show over the keyguard): checked at unlock instead.
      const probeCamera =
        session.mediaType === 'video' && !lockedVideoGateRef.current.isWaiting(session.callId)
      if (!options?.skipPermissionPreflight) {
        const permissions = await ensurePermissionsWithPromptNotice(session.callId, {
          video: probeCamera,
        })
        if (permissions.microphone === 'denied') {
          await terminateCall('error', microphoneDeniedMessage())
          return
        }
        if (probeCamera && permissions.camera === 'denied') {
          await terminateCall('error', cameraDeniedMessage())
          return
        }
      }

      // Signal ACCEPT BEFORE joining Chime: the caller stops ringing (and joins
      // the meeting) immediately, even when this device's own join is slow — on
      // a cold start (CallKit answer waking a killed app) the join can lag many
      // seconds behind the user's answer, during which the caller previously
      // heard endless ringing. If the join below then fails, the catch sends a
      // terminal END so the caller is torn down rather than left connected.
      await publishVoiceCallSignal({
        callId: session.callId,
        type: 'ACCEPT',
        callerId: session.callerId,
        calleeId: session.calleeId,
        conversationId: session.conversationId,
        payload: consentPayload,
      })
      // Tell our OTHER devices we answered so they stop ringing immediately
      // (web ↔ mobile same user). The normal ACCEPT above only reaches the
      // caller; this relay is addressed to our own user id.
      relayTerminalToSiblingDevices(
        {
          callId: session.callId,
          type: 'ACCEPT',
          callerId: session.callerId,
          calleeId: session.calleeId,
          conversationId: session.conversationId,
          payload: consentPayload,
        },
        session.calleeId,
      )
      await joinChimeMeeting(
        session.callId,
        localAttendeeDisplayName(currentUserId),
        session.mediaType ?? 'voice',
        // This is the answer path. On iOS it is a CallKit answer, so the native
        // audio path must start from provider(_:didActivate:) rather than
        // immediately — that is the whole locked-screen fix.
        { callKitAnswer: getNativePlatform() === 'ios' },
      )
      startHeartbeatLoop()
    } catch (err) {
      if (ignoreForeignCallAck(err, session.callId, 'ACCEPT')) {
        // Wrong signed-in account acked a call it is not on. Tear down only
        // THIS device's UI — never END/REJECT the real parties' session.
        const live = getVoiceCallSession()
        if (live?.callId === session.callId) {
          finishCall('cancelled')
          await disposeAdapter()
        }
        return
      }
      await terminateCall(
        'error',
        err instanceof Error ? err.message : 'Failed to accept call',
      )
    }
  }, [clearRingTimer, currentUserId, disposeAdapter, ensurePermissionsWithPromptNotice, joinChimeMeeting, startHeartbeatLoop, terminateCall])
  acceptCallRef.current = acceptCall

  const declineRecordingConsent = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'incoming') return
    clearRingTimer()
    clearCallNotification(session.callId)
    patchVoiceCallSession({ recordingConsentPending: false })
    // Viewing the notice and refusing recording IS the durable decision:
    // calling is blocked in both directions (server-enforced) until the user
    // accepts — from the pre-dial gate or Menu > Account > Call recording
    // policy. Best-effort: the call teardown below must not hang on it.
    {
      const { local } = recordingConsentRolesForSession(session)
      if (requiresRecordingPolicyAcceptance(local)) {
        void declineRecordingPolicy().catch((err) =>
          console.warn('[recording-policy] durable decline stamp failed', err),
        )
      }
    }
    const optOutPayload = buildRecordingConsentOptOutPayload()
    await publishVoiceCallSignal({
      callId: session.callId,
      type: 'REJECT',
      callerId: session.callerId,
      calleeId: session.calleeId,
      conversationId: session.conversationId,
      payload: optOutPayload,
    }).catch(() => {})
    void publishVoiceCallSignal({
      callId: session.callId,
      type: 'REJECT',
      callerId: session.callerId,
      calleeId: session.calleeId,
      conversationId: session.conversationId,
      recipientUserId: session.calleeId,
      payload: optOutPayload,
    }).catch(() => {})
    finishCall('declined')
    await disposeAdapter()
  }, [clearRingTimer, disposeAdapter])

  // --- Post-connect recording-consent sheet --------------------------------
  //
  // Consumer of the "notice owed" marker (recordingConsentOwedPostConnect, set
  // by a bypassed accept): the sheet renders over the live in-call UI, and
  // these three callbacks are its contract. Telemetry mirrors the pre-join
  // modal's events with a `variant: 'postConnect'` marker.

  /** Once-per-call display telemetry, fired by the sheet when it actually renders. */
  const postConnectNoticeShownForRef = useRef<string | null>(null)
  const notePostConnectConsentNoticeShown = useCallback(() => {
    const session = getVoiceCallSession()
    if (!session?.recordingConsentOwedPostConnect) return
    if (postConnectNoticeShownForRef.current === session.callId) return
    postConnectNoticeShownForRef.current = session.callId
    recordCallFlow('ctrl:consent:noticeShown', {
      mediaType: session.mediaType ?? 'voice',
      variant: 'postConnect',
    }, { callId: session.callId })
  }, [])

  const confirmPostConnectRecordingConsent = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session?.recordingConsentOwedPostConnect || isTerminalCallPhase(session.phase)) return
    recordCallFlow('ctrl:consent:modalConfirmed', {
      mediaType: session.mediaType ?? 'voice',
      variant: 'postConnect',
    }, { callId: session.callId })
    // Same durable per-user stamp a modal-confirmed accept makes, so this user
    // is never re-asked. Best-effort — the call must never hang on it.
    {
      const { local } = recordingConsentRolesForSession(session)
      if (requiresRecordingPolicyAcceptance(local)) {
        void acceptRecordingPolicy().catch((err) =>
          console.warn('[recording-policy] durable accept stamp failed', err),
        )
      }
    }
    // The CONSENT signal is what upgrades the server's PENDING consent and
    // RELEASES the deferred recording/transcription start — it must genuinely
    // reach the server. On failure the owed flag stays set (sheet stays up,
    // confirm is re-tappable) rather than pretending consent was recorded.
    try {
      await publishVoiceCallSignal({
        callId: session.callId,
        type: 'CONSENT',
        callerId: session.callerId,
        calleeId: session.calleeId,
        conversationId: session.conversationId,
        payload: buildPostConnectRecordingConsentAcceptPayload(),
      })
    } catch (err) {
      recordCallFlow('ctrl:consent:postConnectSignalFailed', {
        message: err instanceof Error ? err.message : String(err),
      }, { callId: session.callId })
      patchVoiceCallSession({
        notice: 'Could not record your consent — check your connection and try again.',
      })
      return
    }
    patchVoiceCallSession({ recordingConsentOwedPostConnect: false })
  }, [])

  const declinePostConnectRecordingConsent = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session?.recordingConsentOwedPostConnect || isTerminalCallPhase(session.phase)) return
    const { local } = recordingConsentRolesForSession(session)
    const canOptOut = canMemberOptOutOfRecording(local)
    recordCallFlow('ctrl:consent:postConnectDeclined', {
      mediaType: session.mediaType ?? 'voice',
      canOptOut,
    }, { callId: session.callId })
    patchVoiceCallSession({ recordingConsentOwedPostConnect: false })
    // Same durable decision as the pre-join modal's opt-out: viewing the notice
    // and refusing recording blocks calling in both directions until the user
    // accepts (server-enforced). Best-effort, must not hang the teardown.
    if (canOptOut && requiresRecordingPolicyAcceptance(local)) {
      void declineRecordingPolicy().catch((err) =>
        console.warn('[recording-policy] durable decline stamp failed', err),
      )
    }
    // Pre-join decline semantics = the call ends; mid-call the honest
    // equivalent is leaving it. Opt-out-capable users (Members/Independents)
    // mark the terminal RECORDING_OPT_OUT so the refusal lands on the durable
    // call record; provider staff (who can never opt out) just hang up.
    if (session.isGroup) {
      await hangUp()
      return
    }
    if (canOptOut) {
      await terminateCall('completed', undefined, undefined, {
        endPayloadReason: 'RECORDING_OPT_OUT',
      })
      return
    }
    await hangUp()
  }, [hangUp, terminateCall])

  const declineCall = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'incoming') return
    clearRingTimer()
    clearCallNotification(session.callId)
    await publishVoiceCallSignal({
      callId: session.callId,
      type: 'REJECT',
      callerId: session.callerId,
      calleeId: session.calleeId,
      conversationId: session.conversationId,
    }).catch(() => {})
    // Mirror the decline to our other devices so they stop ringing too.
    void publishVoiceCallSignal({
      callId: session.callId,
      type: 'REJECT',
      callerId: session.callerId,
      calleeId: session.calleeId,
      conversationId: session.conversationId,
      recipientUserId: session.calleeId,
    }).catch(() => {})
    finishCall('declined')
    await disposeAdapter()
  }, [clearRingTimer, disposeAdapter])

  const sendMediaSwitch = useCallback(
    (payload: MediaSwitchPayload) => {
      const session = getVoiceCallSession()
      if (!session) return
      void publishVoiceCallSignal({
        callId: session.callId,
        type: 'MEDIA_SWITCH',
        callerId: session.callerId,
        calleeId: session.calleeId,
        conversationId: session.conversationId,
        payload,
      }).catch(() => {})
    },
    [],
  )

  // Publish this device's car-mode driving state to the 1:1 peer. Rides the
  // MEDIA_SWITCH signal type (no schema change); best-effort like all relays.
  const sendCarModeSignal = useCallback((driving: boolean) => {
    const session = getVoiceCallSession()
    if (!session || session.isGroup) return
    const payload: CarModeSignalPayload = { kind: 'car-mode', driving }
    void publishVoiceCallSignal({
      callId: session.callId,
      type: 'MEDIA_SWITCH',
      callerId: session.callerId,
      calleeId: session.calleeId,
      conversationId: session.conversationId,
      payload,
    }).catch(() => {})
  }, [])
  announceCarModeRef.current = sendCarModeSignal

  // The peer reported their car-mode driving state: swap their tile for the
  // "<Name> is driving — video paused" placeholder (and back).
  const handleCarModeSignal = useCallback((driving: boolean) => {
    const session = getVoiceCallSession()
    if (!session || session.isGroup) return
    if (Boolean(session.peerDriving) === driving) return
    recordCallFlow('ctrl:carVideo:peer', { driving }, { callId: session.callId })
    patchVoiceCallSession({ peerDriving: driving })
  }, [])
  handleCarModeSignalRef.current = handleCarModeSignal

  // In-place voice→video swap: the meeting is always created video-capable and
  // the attendee already holds Video:SendReceive, so we simply mount the video
  // overlay and start the local camera tile — NO dispose / re-join, so the call
  // never drops.
  const performLocalVideoSwap = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'active' || session.mediaType === 'video') return
    if (session.upgradingToVideo) return
    // Answered on the locked keyguard: no camera (not even the permission
    // probe) until the device unlocks.
    if (lockedVideoGateRef.current.isWaiting(session.callId)) {
      recordCallFlow('ctrl:lockedVideo:swapBlocked', {}, { callId: session.callId })
      return
    }

    // Attached to CarPlay / Android Auto: switch to video-mode WITHOUT the
    // camera (and without a permission prompt) — the peer renders our driving
    // placeholder, we keep their video paused.
    if (await refreshCarConnection()) {
      recordCallFlow('ctrl:carVideo:swapPaused', {}, { callId: session.callId })
      callAdapterRef.current?.pauseRemoteVideo()
      patchVoiceCallSession({
        mediaType: 'video',
        upgradingToVideo: false,
        isCameraOff: true,
        selfDriving: true,
        videoRequest: undefined,
      })
      announceCarModeRef.current(true)
      return
    }

    const permissions = await ensureCallPermissionsForCall({ video: true })
    if (permissions.camera === 'denied') {
      patchVoiceCallSession({ errorMessage: cameraDeniedMessage(), videoRequest: undefined })
      return
    }

    const adapter = callAdapterRef.current
    if (!adapter) return

    // Switch to the video overlay first so its video elements mount before we
    // bind the local/remote tiles.
    patchVoiceCallSession({
      mediaType: 'video',
      upgradingToVideo: true,
      isCameraOff: false,
      videoRequest: undefined,
    })

    try {
      const { localVideo, remoteVideo } = await waitForVideoElements()
      await adapter.enableVideo({ localVideo, remoteVideo, contentVideo: contentVideoRef.current })
      patchVoiceCallSession({ upgradingToVideo: false })
    } catch (err) {
      // The switch failed but the call is still connected — fall back to voice
      // rather than dropping the call.
      patchVoiceCallSession({
        mediaType: 'voice',
        upgradingToVideo: false,
        errorMessage: err instanceof Error ? err.message : 'Failed to switch to video',
      })
    }
  }, [waitForVideoElements])

  performVideoSwapRef.current = performLocalVideoSwap

  // Initiate a consent-based voice→video switch: confirm our own camera up
  // front, then ASK the peer (camera stays off until they accept). A 15s
  // timeout returns us to voice. The actual swap happens on their acceptance.
  const upgradeToVideo = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'active' || session.mediaType !== 'voice') return
    if (session.upgradingToVideo || session.videoRequest) return

    const permissions = await ensureCallPermissionsForCall({ video: true })
    if (permissions.camera === 'denied') {
      patchVoiceCallSession({ errorMessage: cameraDeniedMessage() })
      return
    }

    patchVoiceCallSession({ videoRequest: 'outgoing', errorMessage: undefined })
    sendMediaSwitch({ action: 'request', to: 'video' })

    clearMediaSwitchTimer()
    mediaSwitchTimerRef.current = setTimeout(() => {
      mediaSwitchTimerRef.current = null
      const current = getVoiceCallSession()
      if (current?.videoRequest === 'outgoing' && current.mediaType === 'voice') {
        patchVoiceCallSession({ videoRequest: undefined })
        // Withdraw the request so the peer's Accept/Decline prompt clears too.
        sendMediaSwitch({ action: 'decline', to: 'video' })
        showNotice('No answer — staying on voice')
      }
    }, MEDIA_SWITCH_TIMEOUT_MS)
  }, [clearMediaSwitchTimer, sendMediaSwitch, showNotice])

  // Accept an incoming video-switch request from the peer.
  const acceptVideoRequest = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'active' || session.videoRequest !== 'incoming') return
    clearMediaSwitchTimer()
    sendMediaSwitch({ action: 'accept', to: 'video' })
    await performLocalVideoSwap()
  }, [clearMediaSwitchTimer, performLocalVideoSwap, sendMediaSwitch])

  // Decline an incoming video-switch request from the peer.
  const declineVideoRequest = useCallback(() => {
    const session = getVoiceCallSession()
    if (!session || session.videoRequest !== 'incoming') return
    clearMediaSwitchTimer()
    patchVoiceCallSession({ videoRequest: undefined })
    sendMediaSwitch({ action: 'decline', to: 'video' })
  }, [clearMediaSwitchTimer, sendMediaSwitch])

  // In-place video→voice swap: stop the local camera/tile but keep the meeting
  // (and audio) connected. Mirror of performLocalVideoSwap — no dispose /
  // re-join, so the call never drops and either party can switch back.
  const performLocalVoiceSwap = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'active' || session.mediaType !== 'video') return
    if (session.downgradingToVoice) return

    const adapter = callAdapterRef.current
    // Back to voice clears the car-mode video pause (the peer's placeholder
    // only renders on the video overlay; a later re-upgrade re-evaluates).
    if (session.selfDriving) announceCarModeRef.current(false)
    patchVoiceCallSession({
      mediaType: 'voice',
      downgradingToVoice: true,
      isCameraOff: false,
      selfDriving: false,
      videoRequest: undefined,
    })

    try {
      await adapter?.disableVideo()
    } catch {
      /* downgrade is best-effort — the audio call stays up regardless */
    } finally {
      patchVoiceCallSession({ downgradingToVoice: false })
    }
  }, [])

  performVoiceSwapRef.current = performLocalVoiceSwap

  // (Guard logic for the two auto-pause triggers below is the unit-tested
  // `shouldAutoPauseVideoForCar` in utils/car-video-pause-guard.)
  //
  // Car pause/resume operations overlap when the car connection flaps (USB
  // wiggle on Android Auto, wireless CarPlay dropout): each op takes a sequence
  // number and only the NEWEST op may patch session state or leave the camera
  // on. Each op reconciles against the live car state when it finishes, so a
  // flip that landed mid-await is never lost.
  const carVideoOpSeqRef = useRef(0)
  // What `isCameraOff` was before the mid-call car pause, so undocking doesn't
  // switch on a camera the user had deliberately turned off. Join-time pauses
  // never set this (video-call join implies camera-on intent).
  const cameraOffBeforeCarPauseRef = useRef<{ callId: string; cameraWasOff: boolean } | null>(null)
  // The driver tapped "I've stopped — turn video on" while the car is still
  // attached (parked on CarPlay / Android Auto — the button's primary case).
  // Their attestation must hold: neither the end-of-resume reconcile nor a
  // re-run of the attach effect may re-pause this call until the car actually
  // detaches (a fresh attach after that is treated as driving again). Guard
  // logic lives in `shouldAutoPauseVideoForCar` (exported, unit-tested).
  const driverVideoOverrideRef = useRef<string | null>(null)

  const pauseVideoForCarRef = useRef<(via: string) => Promise<void>>(() => Promise.resolve())
  const resumeVideoAfterDrivingRef = useRef<(via: string) => Promise<void>>(() => Promise.resolve())

  // CarPlay / Android Auto attached mid-video-call: stop the camera, pause the
  // peer's video, tell them we're driving. 1:1 only (group camera is guarded in
  // toggleCamera); audio is never touched.
  const pauseVideoForCar = useCallback(
    async (via: string) => {
      const session = getVoiceCallSession()
      if (!session || session.isGroup || session.mediaType !== 'video') return
      if (session.selfDriving) return
      if (session.phase !== 'active' && session.phase !== 'connecting') return
      const seq = ++carVideoOpSeqRef.current
      recordCallFlow('ctrl:carVideo:paused', { via }, { callId: session.callId })
      cameraOffBeforeCarPauseRef.current = {
        callId: session.callId,
        cameraWasOff: session.isCameraOff === true,
      }
      const adapter = callAdapterRef.current
      adapter?.pauseRemoteVideo()
      try {
        await adapter?.disableVideo()
      } catch {
        /* camera stop is best-effort — the audio call stays up regardless */
      }
      if (carVideoOpSeqRef.current !== seq) return // superseded by a newer op
      patchVoiceCallSession({ selfDriving: true, isCameraOff: true })
      announceCarModeRef.current(true)
      showNotice('Video paused while driving')
      // The car may have detached while the camera was stopping — reconcile.
      if (!isCarConnected() && carVideoOpSeqRef.current === seq) {
        void resumeVideoAfterDrivingRef.current('carDetachedDuringPause')
      }
    },
    [showNotice],
  )
  pauseVideoForCarRef.current = pauseVideoForCar

  // The car detached, or the driver tapped "I've stopped — turn video on":
  // resume the peer's video and restart the camera.
  const resumeVideoAfterDriving = useCallback(
    async (via: string) => {
      const session = getVoiceCallSession()
      const adapter = callAdapterRef.current
      if (!session || session.mediaType !== 'video' || !session.selfDriving) return
      if (isTerminalCallPhase(session.phase)) return
      const seq = ++carVideoOpSeqRef.current
      // Record the driver's attestation only while a car is actually attached —
      // a tap after an unobserved detach must not suppress the NEXT attach's pause.
      if (via === 'driverTap' && isCarConnected()) {
        driverVideoOverrideRef.current = session.callId
      }
      recordCallFlow('ctrl:carVideo:resume', { via }, { callId: session.callId })
      patchVoiceCallSession({ selfDriving: false })
      announceCarModeRef.current(false)
      if (!adapter) return // car detached before the join finished — state cleared above
      adapter.resumeRemoteVideo()
      const memo = cameraOffBeforeCarPauseRef.current
      if (via !== 'driverTap' && memo?.callId === session.callId && memo.cameraWasOff) {
        // The user had their camera off before docking — leave it off.
        return
      }
      const permissions = await ensureCallPermissionsForCall({ video: true })
      if (permissions.camera === 'denied') {
        patchVoiceCallSession({ errorMessage: cameraDeniedMessage() })
        return
      }
      if (carVideoOpSeqRef.current !== seq) return // superseded by a newer op
      try {
        const { localVideo, remoteVideo } = await waitForVideoElements()
        await adapter.enableVideo({ localVideo, remoteVideo, contentVideo: contentVideoRef.current })
        if (carVideoOpSeqRef.current !== seq) {
          // The car re-attached while the camera was starting; the newer pause
          // already patched selfDriving — make sure the camera is OFF again.
          try {
            await adapter.disableVideo()
          } catch {
            /* best-effort */
          }
          return
        }
        if (getVoiceCallSession()?.callId === session.callId) {
          patchVoiceCallSession({ isCameraOff: false })
        }
      } catch (err) {
        recordCallFlow(
          'ctrl:carVideo:resumeFailed',
          { via, message: err instanceof Error ? err.message : String(err) },
          { callId: session.callId },
        )
        patchVoiceCallSession({
          errorMessage: 'Your camera could not start — tap the camera button to try again.',
        })
      }
      // The car may have re-attached while the camera was starting — reconcile.
      // A driver override (explicit "I've stopped" tap) wins over a still-
      // attached car; without this exception the button re-paused immediately
      // whenever the parked phone stayed on CarPlay / Android Auto (#546 bug).
      if (
        carVideoOpSeqRef.current === seq &&
        shouldAutoPauseVideoForCar({
          carConnected: isCarConnected(),
          selfDriving: false,
          overrideCallId: driverVideoOverrideRef.current,
          callId: session.callId,
        })
      ) {
        void pauseVideoForCarRef.current('carAttachedDuringResume')
      }
    },
    [waitForVideoElements],
  )
  resumeVideoAfterDrivingRef.current = resumeVideoAfterDriving

  // The driver's explicit "I've stopped — turn video on" control.
  const resumeDrivingVideo = useCallback(() => {
    void resumeVideoAfterDriving('driverTap')
  }, [resumeVideoAfterDriving])

  // React to the car attaching/detaching while a 1:1 video call is live.
  const carConnected = useCarConnected()
  useEffect(() => {
    // Detach ends any driver override — the next attach means driving again.
    if (!carConnected) driverVideoOverrideRef.current = null
    const session = getVoiceCallSession()
    if (!session || session.isGroup || session.mediaType !== 'video') return
    if (session.phase !== 'active' && session.phase !== 'connecting') return
    if (
      shouldAutoPauseVideoForCar({
        carConnected,
        selfDriving: session.selfDriving === true,
        overrideCallId: driverVideoOverrideRef.current,
        callId: session.callId,
      })
    ) {
      void pauseVideoForCar('carAttached')
    } else if (!carConnected && session.selfDriving) {
      void resumeVideoAfterDriving('carDetached')
    }
  }, [carConnected, pauseVideoForCar, resumeVideoAfterDriving])

  // Switch back to voice — announced (NOT gated): tell the peer, then swap our
  // own side. Either party may trigger this at any time.
  const downgradeToVoice = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'active' || session.mediaType !== 'video') return
    if (session.downgradingToVoice) return

    sendMediaSwitch({ action: 'switch', to: 'voice' })
    showNotice('Switching back to voice')
    await performLocalVoiceSwap()
  }, [performLocalVoiceSwap, sendMediaSwitch, showNotice])

  // Handle an inbound MEDIA_SWITCH signal from the peer.
  const handleMediaSwitch = useCallback(
    (payload: MediaSwitchPayload) => {
      const session = getVoiceCallSession()
      if (!session || session.phase !== 'active') return

      if (payload.to === 'voice' && payload.action === 'switch') {
        if (session.mediaType !== 'video') return
        clearMediaSwitchTimer()
        showNotice('Switching back to voice')
        void performLocalVoiceSwap()
        return
      }

      if (payload.to === 'video') {
        switch (payload.action) {
          case 'request':
            // Peer wants to switch to video — surface Accept / Decline.
            if (session.mediaType === 'voice' && !session.videoRequest) {
              patchVoiceCallSession({ videoRequest: 'incoming' })
            }
            break
          case 'accept':
            // Peer accepted OUR request — perform the swap on both sides.
            if (session.videoRequest === 'outgoing') {
              clearMediaSwitchTimer()
              void performLocalVideoSwap()
            }
            break
          case 'decline':
            clearMediaSwitchTimer()
            if (session.videoRequest === 'outgoing') {
              // The peer declined our request.
              patchVoiceCallSession({ videoRequest: undefined })
              showNotice(`${session.peer?.displayName ?? 'They'} declined video`)
            } else if (session.videoRequest === 'incoming') {
              // The requester withdrew (declined / timed out) — clear our prompt.
              patchVoiceCallSession({ videoRequest: undefined })
            }
            break
          case 'switch':
            // Non-gated video switch (defensive) — mirror locally.
            if (session.mediaType === 'voice') void performLocalVideoSwap()
            break
        }
      }
    },
    [clearMediaSwitchTimer, performLocalVideoSwap, performLocalVoiceSwap, showNotice],
  )

  handleMediaSwitchRef.current = handleMediaSwitch

  // Rebuild an incoming-call session from a payload the native FCM service
  // seeded while the app was KILLED (no AppSync subscription ran, so JS never
  // saw the INVITE). Without this, a cold-start Answer has no session to join.
  // Idempotent: returns the existing session for the same call, or null when
  // the device is already busy on a different call.
  // A rejected rebuild is remembered per call so a native Answer for it (which
  // may arrive later, via the pending-action drain) settles the call instead of
  // leaving it answered with nothing behind it.
  const rebuildIncomingSessionFromNativeInvite = useCallback(
    async (invite: NativePendingIncomingCall, options?: { osAnswered?: boolean }): Promise<boolean> => {
      const fail = (reason: NativeAnswerAbortReason): false => {
        const failures = nativeRebuildFailuresRef.current
        failures.delete(invite.callId)
        failures.set(invite.callId, { invite, reason })
        if (failures.size > 20) failures.delete(failures.keys().next().value as string)
        return false
      }
      const existing = getVoiceCallSession()
      if (existing?.callId === invite.callId) return existing.phase === 'incoming'
      if (existing && existing.phase !== 'ended' && existing.phase !== 'idle') return fail('busy')
      if (!invite.callerId || !invite.calleeId) return fail('missingIds')
      if (invite.callerId === currentUserId) return fail('selfCaller')
      if (currentUserId && invite.calleeId !== currentUserId) {
        recordCallFlow('ctrl:invite:notCallee', {}, { callId: invite.callId })
        return fail('notCallee')
      }

      let peer: VoiceCallPeer
      try {
        peer = await resolveIncomingCallerPeer(
          invite.callerId,
          invite.calleeId,
          currentUserId,
          invite.callerName
            ? {
                callerDisplayName: invite.callerName,
                mediaType: invite.mediaType,
                callPurpose: invite.callPurpose,
              }
            : null,
        )
      } catch {
        return fail('rebuildFailed')
      }
      // Re-check after the await — the AppSync INVITE may have built it meanwhile.
      const live = getVoiceCallSession()
      if (live?.callId === invite.callId) return live.phase === 'incoming'
      if (live && live.phase !== 'ended' && live.phase !== 'idle') return fail('busy')
      nativeRebuildFailuresRef.current.delete(invite.callId)

      // A room invite (added to an existing group call) must be rebuilt as a
      // GROUP session so Answer joins the host's existing meeting via
      // joinGroupMeeting — otherwise the added user rebuilds a 1:1 session and
      // can't actually join (the "added user can't answer" bug). For an add, the
      // signal callId IS the room/meeting id (callId === roomCallId === joinCallId).
      const isRoomInvite = invite.roomInvite === true
      // iOS: the CallKit ring that seeded this invite may still be presenting —
      // it stays the canonical ring surface; suppress the in-app ring overlay.
      const iosCallKitRing =
        getNativePlatform() === 'ios' &&
        isNativeIncomingRingActive(invite.joinCallId || invite.callId)
      callAudioRouteArbiter.setNative(isNativeApp())
      callAudioRouteArbiter.begin(
        invite.mediaType === 'video' ? 'video' : 'voice',
        'incoming-ringtone',
      )
      setVoiceCallSession({
        callId: invite.joinCallId || invite.callId,
        callerId: invite.callerId,
        calleeId: invite.calleeId,
        conversationId: invite.conversationId ?? null,
        localRole: 'callee',
        peer,
        phase: 'incoming',
        mediaType: invite.mediaType,
        osRingActive: iosCallKitRing,
        ...(options?.osAnswered ? { osAnswered: true } : {}),
        isMuted: false,
        isCameraOff: isRoomInvite,
        isSpeakerOn: callAudioRouteArbiter.desiredSpeakerOn(),
        startedAt: Date.now(),
        callPurpose: invite.callPurpose ?? 'normal',
        ...(isRoomInvite
          ? {
              isGroup: true as const,
              isHost: false,
              hostId: invite.callerId,
              maxParticipants: MAX_CALL_PARTICIPANTS,
              participants: [
                {
                  userId: invite.callerId,
                  displayName: peer.displayName,
                  avatarUrl: peer.avatarUrl,
                  role: 'host' as const,
                  state: 'active' as const,
                },
                ...(currentUserId
                  ? [
                      {
                        userId: currentUserId,
                        displayName: localAttendeeDisplayName(currentUserId) ?? 'You',
                        role: 'guest' as const,
                        state: 'ringing' as const,
                      },
                    ]
                  : []),
              ],
            }
          : {}),
      })
      void recordNativeCarRecent({
        peerId: invite.callerId,
        peerName: peer.displayName,
      })

      // Foreground rebuild — Android: JS overlay owns the ring; stop native
      // Telecom. iOS: CallKit stays the canonical ring surface (cancelling it
      // here would silently kill the ring the user is looking at).
      if (
        isNativeApp() &&
        getNativePlatform() !== 'ios' &&
        document.visibilityState === 'visible'
      ) {
        void cancelNativeIncomingCall(invite.callId)
      }

      clearRingTimer()
      ringTimerRef.current = setTimeout(() => {
        const current = getVoiceCallSession()
        if (current?.callId === invite.callId && current.phase === 'incoming') {
          dismissIncomingRingSurface(invite.callId)
          const endReason = resolveUnansweredEndReason(current.startedAt, new Date(), 'MISSED')
          void sendEnd(
            current.callId,
            current.callerId,
            current.calleeId,
            current.conversationId,
            endReason,
            current.startedAt,
          )
          if (currentUserId) {
            relayTerminalToSiblingDevices(
              {
                callId: current.callId,
                type: 'END',
                callerId: current.callerId,
                calleeId: current.calleeId,
                conversationId: current.conversationId,
                payload: buildCallEndPayload(endReason, current.startedAt),
              },
              currentUserId,
            )
          }
          finishCall(endReason === 'MISSED' ? 'missed' : 'cancelled')
          void disposeAdapter()
        }
      }, RING_TIMEOUT_MS)
      return true
    },
    [clearRingTimer, currentUserId, disposeAdapter, sendEnd],
  )

  /**
   * Durable return-to-call (Sprint 8 hardening): on app foreground/cold-open,
   * ask the BACKEND whether this user is in a live accepted call. If another
   * of this user's devices owns media (activeDeviceId), show soft presence
   * only — do NOT auto-join Chime (Q1C). Same-device ownership (or no owner
   * yet) still re-joins for return-to-call.
   */
  const rehydratingRef = useRef(false)
  const rehydrateActiveCallFromBackend = useCallback(async () => {
    if (!currentUserId || rehydratingRef.current) return
    const existing = getVoiceCallSession()
    // Soft presence (elsewhere) may refresh ownership; other live phases skip.
    if (existing && !isTerminalCallPhase(existing.phase) && existing.phase !== 'elsewhere') return
    rehydratingRef.current = true
    try {
      const [active, nativeEndedCallIds] = await Promise.all([
        fetchMyActiveCall(),
        getNativeEndedCallIds(),
      ])
      const decision = decideRehydrateAction({
        active: active
          ? {
              callId: active.callId,
              callerId: active.callerId,
              calleeId: active.calleeId,
              accepted: active.accepted,
              activeDeviceId: active.activeDeviceId ?? null,
            }
          : null,
        currentUserId,
        thisDeviceId: getDeviceId(),
        existingPhase: existing?.phase ?? null,
        // The native layer tombstones a callId when the server's END/CANCEL
        // push lands — even while JS was dead (Back-killed WebView, relaunch).
        // That is the server saying "ended": never rejoin it (dcfc5c9a).
        isTombstoned: (callId) => isCallTombstoned(callId) || nativeEndedCallIds.has(callId),
      })
      if (decision === 'none') return
      if (decision === 'clear-elsewhere') {
        if (getVoiceCallSession()?.phase === 'elsewhere') setVoiceCallSession(null)
        return
      }
      if (!active) return
      if (decision === 'self-heal-end') {
        // The user already ended this call HERE, but the backend row survived
        // (our/peer END never settled it). Never resurrect it — instead settle
        // the row with a best-effort END so no device gets re-offered the
        // zombie call. De-duped server-side if history was already written.
        recordCallFlow('ctrl:rehydrate:tombstoned', {}, { callId: active.callId })
        const reason = (tombstonedCallReason(active.callId) ?? 'ended').toUpperCase()
        const payloadReason =
          reason === 'COMPLETED' || reason === 'MISSED' || reason === 'DECLINED' ||
          reason === 'BUSY' || reason === 'INTERRUPTED'
            ? reason
            : 'CANCELLED'
        void publishVoiceCallSignal({
          callId: active.callId,
          type: 'END',
          callerId: active.callerId,
          calleeId: active.calleeId,
          conversationId: active.conversationId ?? null,
          payload: buildCallEndPayload(
            payloadReason,
            Date.parse(active.startedAt) || undefined,
          ),
        }).catch(() => {})
        if (getVoiceCallSession()?.phase === 'elsewhere') setVoiceCallSession(null)
        return
      }
      // Re-check after the await — a signal/invite may have rebuilt state.
      const live = getVoiceCallSession()
      if (
        live &&
        !isTerminalCallPhase(live.phase) &&
        live.phase !== 'elsewhere' &&
        live.callId === active.callId
      ) {
        return
      }

      const peerId = active.callerId === currentUserId ? active.calleeId : active.callerId
      let peer: VoiceCallPeer = { userId: peerId, displayName: 'Call in progress' }
      try {
        const user = await resolveDirectoryUser(peerId)
        if (user?.displayName?.trim()) {
          peer = { userId: peerId, displayName: user.displayName.trim(), avatarUrl: user.avatarUrl }
        }
      } catch {
        /* directory lookup is best-effort — generic label is fine */
      }

      const mediaType = active.mediaType === 'video' ? 'video' : 'voice'
      const startedAtMs = Date.parse(active.startedAt)

      // 'elsewhere' now requires a FRESH per-user claim: the backend only
      // returns activeDeviceId when THIS user's own claim is heartbeat-fresh,
      // so a dead tab / crashed app can no longer produce the switch prompt.
      if (decision === 'elsewhere') {
        setVoiceCallSession({
          callId: active.callId,
          callerId: active.callerId,
          calleeId: active.calleeId,
          conversationId: active.conversationId ?? null,
          localRole: active.callerId === currentUserId ? 'caller' : 'callee',
          peer,
          phase: 'elsewhere',
          activeElsewhere: true,
          activeDeviceId: active.activeDeviceId,
          activeDevicePlatform: active.activeDevicePlatform,
          mediaType,
          isMuted: false,
          isCameraOff: mediaType !== 'video',
          startedAt: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
          callPurpose:
            active.callPurpose === 'on-call' || active.callPurpose === 'urgent'
              ? active.callPurpose
              : 'normal',
          notice: "You're already in a call on another device",
        })
        return
      }

      setVoiceCallSession({
        callId: active.callId,
        callerId: active.callerId,
        calleeId: active.calleeId,
        conversationId: active.conversationId ?? null,
        localRole: active.callerId === currentUserId ? 'caller' : 'callee',
        peer,
        phase: 'connecting',
        mediaType,
        isMuted: false,
        isCameraOff: mediaType !== 'video',
        startedAt: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
        callPurpose:
          active.callPurpose === 'on-call' || active.callPurpose === 'urgent'
            ? active.callPurpose
            : 'normal',
        notice: RECONNECTING_NOTICE,
        activeDeviceId: active.activeDeviceId,
        activeDevicePlatform: active.activeDevicePlatform,
      })
      try {
        await joinChimeMeeting(
          active.callId,
          localAttendeeDisplayName(currentUserId),
          mediaType,
        )
      } catch (err) {
        if (err instanceof CallEndedError) {
          // The call ended between the active-call read and the join (the
          // server refuses the token). End locally — finishCall tombstones the
          // callId so no later foreground/relaunch tries again.
          recordCallFlow('ctrl:rehydrate:callEnded', {}, { callId: active.callId })
          if (getVoiceCallSession()?.callId === active.callId) {
            finishCall('completed')
            await disposeAdapter()
          }
          return
        }
        throw err
      }
      // The callee's media joined WITHOUT going through acceptCall (a car /
      // Bluetooth / notification answer whose JS session was never rebuilt,
      // or a relaunch mid-call), so no consent travelled with it and the
      // server's consent-deferred capture would never start. Resolve it under
      // the same rules as every other unnoticed answer.
      if (active.calleeId === currentUserId) {
        const durable = await resolveRecordingPolicyConsent(currentUserId)
        const roles = await loadRecordingConsentRoles(active.callerId)
        const plan = planRejoinedCalleeConsent({
          hasDurableAcceptance: durable.accepted,
          durableAcceptedAt: durable.acceptedAt,
          durableNoticeVersion: durable.noticeVersion,
          localUser: roles.local,
          remoteUser: roles.remote,
        })
        recordCallFlow('ctrl:rehydrate:consent', {
          skipReason:
            'recordingConsentSkipReason' in plan.payload
              ? plan.payload.recordingConsentSkipReason
              : 'PRIOR_ACCEPTANCE',
          owesPostConnectNotice: plan.owesPostConnectNotice,
        }, { callId: active.callId })
        if (plan.owesPostConnectNotice && getVoiceCallSession()?.callId === active.callId) {
          patchVoiceCallSession({ recordingConsentOwedPostConnect: true })
        }
        void publishVoiceCallSignal({
          callId: active.callId,
          type: 'CONSENT',
          callerId: active.callerId,
          calleeId: active.calleeId,
          conversationId: active.conversationId ?? null,
          payload: plan.payload,
        }).catch((err) => {
          recordCallFlow('ctrl:consent:rehydrateSignalFailed', {
            message: err instanceof Error ? err.message : String(err),
          }, { callId: active.callId })
        })
      }
    } catch {
      // Recovery path only: never surface an error for a call the user may
      // not even remember — if the rejoin failed the session ends normally.
    } finally {
      rehydratingRef.current = false
    }
  }, [currentUserId, disposeAdapter, joinChimeMeeting])

  /**
   * Switch (Q2C/Q3C): join media on THIS device first, then claim ownership via
   * `media_started` (see onAdapterConnected). Prefer claim-after-media so a
   * failed join does not dispose the peer device. On failure after a claim,
   * publish a takeover restoring the previous owner device id.
   */
  const switchToThisDevice = useCallback(async () => {
    if (!currentUserId) return
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'elsewhere') return
    // Never act on a call the user already ended — a stale banner must not be
    // able to rejoin (and thus resurrect) a finished call.
    if (isCallTombstoned(session.callId)) {
      setVoiceCallSession(null)
      return
    }
    const deviceId = getDeviceId()
    if (!deviceId) {
      showNotice('Could not switch — device id unavailable')
      return
    }
    const previousDeviceId = session.activeDeviceId?.trim() || null
    const previousPlatform =
      session.activeDevicePlatform === 'ios' ||
      session.activeDevicePlatform === 'android' ||
      session.activeDevicePlatform === 'web'
        ? session.activeDevicePlatform
        : 'web'
    switchingDeviceRef.current = true
    patchVoiceCallSession({
      phase: 'connecting',
      activeElsewhere: false,
      notice: 'Switching call to this device…',
      reconnecting: true,
      errorMessage: undefined,
    })
    try {
      // Join before takeover so a failed join leaves the owning device intact.
      // onAdapterConnected publishes media_started (hard claim) once local media is up.
      await joinChimeMeeting(
        session.callId,
        localAttendeeDisplayName(currentUserId),
        session.mediaType ?? 'voice',
      )
      // Explicit takeover covers siblings that only listen for claim/takeover, and
      // refreshes SESSION ownership if media_started raced ahead of this line.
      await publishDeviceClaim({
        callId: session.callId,
        callerId: session.callerId,
        calleeId: session.calleeId,
        conversationId: session.conversationId,
        action: 'takeover',
        platform: getNativePlatform(),
        deviceId,
        caps: advertisedDeviceClaimCaps({
          platform: getNativePlatform(),
          nativeMediaCall: nativeMediaActiveRef.current === session.callId,
        }),
      })
      startHeartbeatLoop()
      showNotice('Call switched to this device')
    } catch (err) {
      switchingDeviceRef.current = false
      // Restore previous owner if media_started/takeover already moved SESSION.
      // Harmless when no claim was published (overwrites back to the same device).
      if (previousDeviceId && previousDeviceId !== deviceId) {
        await publishDeviceClaim({
          callId: session.callId,
          callerId: session.callerId,
          calleeId: session.calleeId,
          conversationId: session.conversationId,
          action: 'takeover',
          platform: previousPlatform,
          deviceId: previousDeviceId,
          senderDeviceId: deviceId,
        }).catch(() => {})
      }
      const message = err instanceof Error ? err.message : 'Failed to switch call'
      patchVoiceCallSession({
        phase: 'elsewhere',
        activeElsewhere: true,
        activeDeviceId: previousDeviceId,
        activeDevicePlatform: previousPlatform,
        reconnecting: false,
        notice: undefined,
        errorMessage: message,
      })
      showNotice(`Switch failed — ${message}`)
    }
  }, [currentUserId, joinChimeMeeting, showNotice, startHeartbeatLoop])

  /** Dismiss soft-presence banner without ending the call on the other device. */
  const dismissActiveElsewhere = useCallback(() => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'elsewhere') return
    pendingCallKitHandoffRef.current = null
    setVoiceCallSession(null)
  }, [])

  // Mirror the signed-in user into native (Android + iOS): a different account
  // than the last one wipes the previous account's native call state (ack
  // credentials, invite seed, rings, Telecom / CallKit calls); native invites,
  // acks and joins addressed to anyone else are refused. A same-user restore
  // changes nothing.
  useEffect(() => {
    if (!currentUserId || !isNativeApp()) return
    void setNativeSignedInUser(currentUserId).then((transition) => {
      if (transition && transition !== 'restore') {
        recordCallFlow('ctrl:nativeUser:mirrored', { transition })
      }
    })
  }, [currentUserId])

  // Answer/Decline from the incoming-call notifications (Android). Sources, all
  // funneling into the same guarded handlers:
  //   1. the native full-screen CallStyle plugin (live "callAction" event),
  //   2. the Capacitor local-notification fallback,
  //   3. a pending action persisted natively and consumed on resume / cold
  //      start — the reliable path when the WebView was paused while the user
  //      tapped Answer (the live event is dropped by a paused WebView, which is
  //      why answering previously dismissed the call without connecting),
  //   4. a KILLED-app FCM invite the native service seeded — JS rebuilds the
  //      session here on cold start so Answer actually joins / shows the overlay.
  useEffect(() => {
    if (!currentUserId || !isNativeApp()) return
    const removers: Array<() => void> = []
    let cancelled = false

    const onAnswer = async (callId: string, meta?: { lockedAnswer?: boolean }) => {
      // Before ANY async work: this device is the one answering. The native-ack
      // ACCEPT relayed by a locked/background CallKit answer echoes back here
      // with our own senderId, and must not tear down the call we just answered.
      answeredHereCallIdRef.current = callId
      nativeAnswerRef.current = { callId, atMs: Date.now() }
      const lockedAnswer = meta?.lockedAnswer === true && getNativePlatform() === 'android'
      if (lockedAnswer) lockedVideoGateRef.current.noteLockedAnswer(callId)
      let session = getVoiceCallSession()
      let rebuiltNow = false
      recordCallFlow('ctrl:nativeAnswer', {
        platform: getNativePlatform(),
        hadSession: Boolean(session),
        phase: session?.phase ?? null,
        visibility: typeof document !== 'undefined' ? document.visibilityState : null,
        ...(lockedAnswer ? { lockedAnswer: true } : {}),
      }, { callId })
      // The OS answered: the in-app ring must not start (or keep going) for
      // the 'incoming' session that only waits for accept.
      if (session?.callId === callId && session.phase === 'incoming' && !session.osAnswered) {
        patchVoiceCallSession({ osAnswered: true })
      }
      if (!session || session.callId !== callId) {
        // Cold start / FCM-only ring: rebuild from the native invite seed before
        // accept so mediaType (video vs voice) and peer ids are correct.
        const invite = await consumeNativePendingIncomingCall()
        if (invite?.callId === callId) {
          await rebuildIncomingSessionFromNativeInvite(invite, { osAnswered: true })
          rebuiltNow = true
        }
        session = getVoiceCallSession()
      }
      let rebuildFailure = nativeRebuildFailuresRef.current.get(callId)
      if (
        rebuildFailure &&
        !rebuiltNow &&
        session?.callId !== callId &&
        getWaitingCall()?.callId !== callId
      ) {
        // The failure is from an earlier attempt (e.g. 'busy' on a call that
        // has since ended): retry it now and settle only if it fails again.
        await rebuildIncomingSessionFromNativeInvite(rebuildFailure.invite)
        session = getVoiceCallSession()
        rebuildFailure = nativeRebuildFailuresRef.current.get(callId)
      }
      if (
        rebuildFailure &&
        session?.callId !== callId &&
        getWaitingCall()?.callId !== callId
      ) {
        // Answered at the OS level but unjoinable here (wrong account, busy,
        // bad invite): the native ack may already have ACCEPTed, so end it on
        // the server and drop the Telecom/CallKit connection rather than
        // leave the caller connected to silence.
        nativeRebuildFailuresRef.current.delete(callId)
        recordCallFlow('ctrl:nativeAnswer:abort', { reason: rebuildFailure.reason }, { callId })
        await abortUnbuildableNativeAnswer(rebuildFailure.invite, rebuildFailure.reason, {
          publish: publishVoiceCallSignal,
          disconnectNative: (id) => setNativeCallDisconnected(id, 'canceled'),
          clearNotification: (id) => clearCallNotification(id),
        })
        return
      }
      if (session?.callId === callId && session.phase === 'incoming') {
        // Yield only when the WebView is actually on screen. While it is hidden
        // (lock-screen / background CallKit answer) the frame loop is paused, so
        // waiting for it delays — historically indefinitely — the accept that
        // must run NOW. yieldToDom() is itself rAF-independent when hidden; this
        // keeps the critical answer path off the frame loop entirely.
        if (!isDocumentHidden()) await yieldToDom()
        // An OS-call-UI answer (CallKit, or Telecom from Android Auto /
        // Bluetooth / lock screen / notification) joins audio NOW, warm or
        // cold: a blocking consent modal there sat unseen while the call stayed
        // unconnected (dcfc5c9a). Consent follows as the post-connect sheet.
        requestAcceptCall(nativeAnswerAcceptOptions(getNativePlatform()))
      }
    }
    const onDecline = (callId: string) => {
      const session = getVoiceCallSession()
      if (session?.callId === callId && session.phase === 'incoming') {
        void declineCall()
      }
    }
    // Notification swipe / shade clear / Telecom onAbort — user dismissed the
    // ring without tapping Answer or Decline. Tear down native Telecom on this
    // device only; sibling devices (e.g. web) keep ringing until answered.
    const onDismiss = (callId: string, source?: string) => {
      const session = getVoiceCallSession()
      const route = routeNativeDismiss(session, callId, currentUserId)
      if (route === 'hang-up') {
        // A dismiss that reached a live outgoing / connecting / active call
        // (Telecom onAbort on an older shell, a stray swipe) is a hang-up:
        // END/CANCELLED must reach the peer, never a silent local teardown.
        recordCallFlow('ctrl:nativeDismiss:hangUp', { phase: session?.phase ?? null, source: source ?? null }, { callId })
        onEnd(callId, source)
        return
      }
      void abandonLocalIncomingRing(callId, { notifyPeer: false })
    }
    // Telecom "End call" (status-bar chip / Bluetooth) on a connected call, or a
    // hang-up routed through the self-managed connection.
    const onEnd = (callId: string, source?: string) => {
      const session = getVoiceCallSession()
      const route = routeNativeEndAction({ session, callId, source })
      if (route === 'no-session') {
        // The user hung up from the car / chip while JS had no session for
        // this call (still rebuilding after a native answer, or relaunching).
        // Tombstone it so the backend rehydrate settles it with an END
        // instead of rejoining a call the user just ended.
        if (!session || isTerminalCallPhase(session.phase)) {
          recordCallFlow('ctrl:nativeEnd:noSession', { source: source ?? null }, { callId })
          recordCallEndedTombstone(callId, 'completed')
        }
        return
      }
      if (route === 'ignore') return
      if (route === 'finish-remote') {
        recordCallEndSource(callId, 'remote', session?.phase, { via: 'native', nativeSource: source ?? null })
        void terminateCallRef.current(
          session?.phase === 'active' ? 'completed' : 'cancelled',
          undefined,
          undefined,
          { notifyPeer: false },
        )
        return
      }
      if (route === 'finish-peer-lost') {
        recordCallEndSource(callId, 'remote', session?.phase, { via: 'native', nativeSource: source ?? null })
        void terminateCallRef.current('completed', undefined, undefined, {
          notifyPeer: true,
          endCause: 'peer-lost',
        })
        return
      }
      if (route === 'decline') void declineCall()
      else void hangUp({ endSource: endSourceFromNativeAction(source) })
    }
    // A Telecom connection was destroyed natively. A live JS call for it must
    // not keep running: hang up (END to the peer) unless the teardown was
    // JS-initiated / housekeeping; a native remote END finishes locally.
    const onConnectionDestroyed = (callId: string, source: string) => {
      const session = getVoiceCallSession()
      const decision = decideOnNativeConnectionDestroyed({ session, callId, source })
      if (decision === 'ignore') return
      recordCallFlow('ctrl:nativeTeardown', { source, decision, phase: session?.phase ?? null }, { callId })
      if (decision === 'finish-remote') {
        recordCallEndSource(callId, 'remote', session?.phase, { via: 'native' })
        void terminateCallRef.current('completed', undefined, undefined, { notifyPeer: false })
        return
      }
      void hangUp({ endSource: 'native-teardown' })
    }
    // Keep the speaker toggle in sync with the OS-selected audio route (e.g. a
    // Bluetooth headset connecting mid-call) reported by the Telecom connection.
    const onAudioRoute = (
      callId: string,
      route: 'earpiece' | 'speaker' | 'bluetooth' | 'headset',
      meta?: { userPick?: boolean },
    ) => {
      const session = getVoiceCallSession()
      if (meta?.userPick) {
        // Picked on the lock-screen call screen (native already applied it):
        // the user's choice, so no default re-asserts the old route. Speaker
        // off with a car / headset connected hands the call back to it.
        const output = route === 'speaker' || route === 'earpiece' ? route : 'external'
        if (!session || session.callId !== callId) {
          // Cold boot: the session is rebuilt (which resets the arbiter) and
          // joined after this — the join applies the pick.
          queuedLockScreenPickRef.current = { callId, output }
          recordCallFlow('route:userSelect:queued', { kind: route, source: 'lockScreen' }, { callId })
          return
        }
        callAudioRouteArbiter.userSelect(output)
        patchVoiceCallSession({ isSpeakerOn: output === 'speaker' })
        recordCallFlow('route:userSelect', { kind: route, source: 'lockScreen' }, { callId })
        return
      }
      if (!session || session.callId !== callId) return
      const kind = route === 'headset' ? 'wired' : route
      // Keep the JS route cache fresh (external-precedence guard + chip UI).
      noteHardwareRoute(kind)
      if (kind === 'bluetooth' || kind === 'wired') {
        // Android Auto / the system output switcher / a headset connect put
        // the call on an external device: that supersedes an earlier in-app
        // earpiece/speaker pick, so nothing re-applies the builtin over it.
        const userOutput = callAudioRouteArbiter.snapshot().userOutput
        if (callAudioRouteArbiter.observeSystemExternalSelect()) {
          recordCallFlow('route:systemSelect', { route: kind, previousUserOutput: userOutput }, { callId })
        }
      }
      const decision = callAudioRouteArbiter.observeHardware(kind, 'telecom', {
        availableExternal: kind === 'bluetooth' || kind === 'wired',
      })
      if (kind === 'bluetooth' || kind === 'wired') {
        patchVoiceCallSession({ isSpeakerOn: false })
      }
      if (decision.action !== 'correct') return
      recordCallFlow(
        'route:corrected',
        {
          from: decision.from,
          to: decision.to,
          phase: decision.phase,
          trigger: decision.trigger,
          reason: decision.reason,
        },
        { callId },
      )
      // Native Android already re-asserts from onTelecomRouteChanged. Applying
      // again here re-armed the burst and was the JS half of the 103 oscillation.
      if (getNativePlatform() === 'android') return
      void applyDesiredNativeSpeakerphone()
      void setNativeIosMediaSpeaker(desiredCallSpeakerOn())
    }
    // Android locked-keyguard answer: the unlock turns video on.
    const onDeviceUnlocked = (callId: string, via: string) => {
      if (lockedVideoGateRef.current.noteUnlocked(callId)) {
        void enableLockedAnswerVideoRef.current(callId, via)
      }
    }
    // iOS CallKit activated the shared AVAudioSession (after the user answered a
    // CallKit/VoIP call). Re-assert the call audio route so Chime's WebRTC audio
    // unit is bound to the now-live .playAndRecord session — fixes connected-but-
    // silent CallKit calls where Chime started a beat before CallKit activation.
    const onAudioSessionActivated = () => {
      const session = getVoiceCallSession()
      recordCallFlow('ctrl:callkit:audioSessionActivated', {
        phase: session?.phase ?? null,
        mediaType: session?.mediaType ?? null,
        isSpeakerOn: session?.isSpeakerOn ?? null,
      }, { callId: session?.callId ?? null })
      if (!session || isTerminalCallPhase(session.phase)) return
      // iOS BACKGROUND / lock-screen answer: CallKit just activated AND
      // configured its .playAndRecord/.voiceChat session (AscendCallManager
      // didActivate). This is the canonical "start call audio only after
      // didActivate" moment for CallKit + WebRTC. If we have NOT joined yet
      // (still 'incoming' — the WebView resumed under the CallKit-owned session),
      // kick the accept/join NOW so Chime's mic capture binds to the live
      // recording session rather than starting against no valid session and
      // going dead (the caller-hears-silence locked-answer bug). Mirror the
      // native-answer accept options so a backgrounded WebView doesn't hang on a
      // consent modal or a mis-reporting permission pre-flight.
      if (session.phase === 'incoming') {
        requestAcceptCall(
          getNativePlatform() === 'ios'
            ? { bypassRecordingConsent: true, skipPermissionPreflight: true }
            : undefined,
        )
        return
      }
      const speakerOn = desiredCallSpeakerOn()
      void callAdapterRef.current?.setSpeakerphone(speakerOn)
      void setNativeIosMediaSpeaker(speakerOn)
      // If Chime's audio unit started BEFORE CallKit activated the session
      // (cold-start answer ordering), the mic capture it bound is dead even
      // though the call looks connected — restart the audio input on the live
      // session now that the session is genuinely active.
      void callAdapterRef.current?.restartAudioInput()
    }
    // iOS ring-only handoff completed: CallKit released the audio session (the
    // deferred lock-screen handoff ends the CallKit call only once Chime
    // connects). Any audio bound while CallKit owned the session is starved —
    // restart input + re-assert the route now that the WebView owns audio.
    const onAudioSessionDeactivated = () => {
      const session = getVoiceCallSession()
      recordCallFlow('ctrl:callkit:audioSessionDeactivated', {
        phase: session?.phase ?? null,
      }, { callId: session?.callId ?? null })
      if (!session || isTerminalCallPhase(session.phase)) return
      const speakerOn = desiredCallSpeakerOn()
      void callAdapterRef.current?.setSpeakerphone(speakerOn)
      void setNativeIosMediaSpeaker(speakerOn)
      void callAdapterRef.current?.restartAudioInput()
    }

    // iOS: CallKit started/stopped presenting the incoming ring for a call.
    // While active it is the ONE canonical ring surface — mirror it onto the
    // session so the in-app ring overlay + JS ringtone stand down; when CallKit
    // releases the call with the session still ringing (e.g. CallKit ring
    // timeout beat our 40s SLA), hand the ring back to the in-app overlay.
    const onNativeIncomingRing = (callId: string, active: boolean) => {
      const session = getVoiceCallSession()
      if (!session || session.callId !== callId) return
      if (session.phase === 'incoming' && session.osRingActive !== active) {
        patchVoiceCallSession({ osRingActive: active })
      }
    }

    // Returning to the app with a live call must always show the call UI:
    // clear any minimized overlay (iOS CallKit green-pill / Dynamic Island tap,
    // Android ongoing-call notification tap, or plain app switch all land here).
    //
    // COALESCED: mount + visibilitychange + document 'resume' + deep link all
    // land within the same foreground transition — build 108 telemetry showed
    // 8 invocations in ~200ms, each re-asserting the native route (the
    // incoming-represent half of the earpiece↔speaker flip). One represent per
    // window per call is enough.
    let lastRepresentedCallId: string | null = null
    let lastRepresentedAt = 0
    const representCallOnForeground = () => {
      const live = getVoiceCallSession()
      if (live && !isTerminalCallPhase(live.phase)) {
        const now = Date.now()
        if (
          live.callId === lastRepresentedCallId &&
          now - lastRepresentedAt < REPRESENT_CALL_COALESCE_MS
        ) {
          return
        }
        lastRepresentedCallId = live.callId
        lastRepresentedAt = now
      }
      // Traceability for the blank-screen-after-answer fix: record every time we
      // (re)present a live call on foreground, and whether it was hidden by an
      // explicit user-minimize (the only thing that can hide it now).
      if (live && !isTerminalCallPhase(live.phase)) {
        recordCallFlow(
          'ctrl:foreground:representCall',
          { phase: live.phase, wasUserMinimized: Boolean(live.userMinimized) },
          { callId: live.callId },
        )
        // Backstop for a missed deviceUnlocked: the app is only visible unlocked.
        if (getNativePlatform() === 'android' && lockedVideoGateRef.current.isWaiting(live.callId)) {
          const waitingCallId = live.callId
          void getNativeKeyguardLocked().then((locked) => {
            if (locked !== true) onDeviceUnlocked(waitingCallId, 'appVisible')
          })
        }
        // Never assert a route while the call is still INCOMING: the arbiter's
        // ringtone default is speaker, and asserting it here flipped an
        // earpiece/Bluetooth route Telecom had already picked (route
        // oscillation on incoming represent while Android Auto was connected,
        // build 108). Ring routing belongs to Telecom/prepareForRinging; the
        // in-call route is asserted on join and on the connecting/active paths.
        if (live.phase !== 'incoming') {
          void applyDesiredNativeSpeakerphone()
          void setNativeIosMediaSpeaker(desiredCallSpeakerOn())
        }
      }
      if (live && !isTerminalCallPhase(live.phase) && live.userMinimized) {
        patchVoiceCallSession({ userMinimized: false })
      }
      // Video recovery: a CallKit-answered video call may have joined
      // audio-only (camera blocked while the WebView was backgrounded — see
      // videoOptional). Now that the app is visible, getUserMedia works again:
      // start the camera and rebind tiles. setCameraEnabled() runs the full
      // camera start when video input never began. Skipped when the user
      // intentionally turned their camera off.
      const adapter = callAdapterRef.current
      if (
        live &&
        live.phase === 'active' &&
        live.mediaType === 'video' &&
        !live.isCameraOff &&
        adapter &&
        !adapter.isVideoStarted()
      ) {
        void adapter
          .setCameraEnabled(true)
          .then(() => {
            const current = getVoiceCallSession()
            if (
              current?.callId === live.callId &&
              current.errorMessage?.startsWith('Your camera could not start')
            ) {
              patchVoiceCallSession({ errorMessage: undefined })
            }
          })
          .catch(() => {
            /* camera still unavailable — user can retry via the toggle */
          })
      }
    }

    // Adopt a native Chime session that stayed alive under CallKit while the
    // WebView was suspended — flips "Connecting…" → Active Call when the
    // roster already has the peer (Dynamic Island / deep-link return).
    const syncNativeMediaOnForeground = async (preferredCallId?: string) => {
      const native = await getNativeIosMediaActiveCall()
      if (cancelled || !native) return
      const live = getVoiceCallSession()
      if (!live || isTerminalCallPhase(live.phase)) return
      // Room invites keep CallKit/native on the invite id while the in-app
      // session may use joinCallId — match either side.
      const callKit = await getActiveCallKitCall()
      if (cancelled) return
      const inviteId = native.callId
      const joinId = callKit?.joinCallId
      const matches =
        live.callId === inviteId ||
        live.callId === preferredCallId ||
        (joinId != null && live.callId === joinId) ||
        (preferredCallId != null && inviteId === preferredCallId)
      if (!matches) return
      nativeMediaActiveRef.current = inviteId
      if (live.userMinimized) patchVoiceCallSession({ userMinimized: false })
      if (native.connected) {
        onAdapterConnected()
        if (native.remotePresent) onAdapterRemotePresent()
      }
      recordCallFlow(
        'ctrl:foreground:syncNativeMedia',
        {
          connected: native.connected,
          remotePresent: native.remotePresent,
          phase: getVoiceCallSession()?.phase ?? null,
          inviteId,
          joinId: joinId ?? null,
        },
        { callId: live.callId },
      )
    }

    // Ascend Messenger / DI return: CallKit + native media are often already
    // connected while the WebView has NO session (pending invite was consumed,
    // or the content process reloaded). Seed the overlay IMMEDIATELY from the
    // enriched CallKit snapshot so unlock never lands on a blank black shell.
    const ensureSessionFromCallKit = async (preferredCallId?: string): Promise<boolean> => {
      if (!currentUserId) return false
      const [callKit, native] = await Promise.all([
        getActiveCallKitCall(),
        getNativeIosMediaActiveCall(),
      ])
      if (cancelled) return false
      const callId = preferredCallId || callKit?.callId || native?.callId
      if (!callId) return false

      const live = getVoiceCallSession()
      if (live && live.callId === callId && !isTerminalCallPhase(live.phase)) {
        if (live.userMinimized) patchVoiceCallSession({ userMinimized: false })
        if (native?.callId === callId) {
          nativeMediaActiveRef.current = callId
          if (native.connected) {
            onAdapterConnected()
            if (native.remotePresent) onAdapterRemotePresent()
          }
        }
        return true
      }
      // Also treat an existing session seeded on joinCallId as a match.
      if (
        live &&
        !isTerminalCallPhase(live.phase) &&
        (live.callId === callKit?.joinCallId || live.callId === callKit?.callId)
      ) {
        if (live.userMinimized) patchVoiceCallSession({ userMinimized: false })
        if (native?.callId) {
          nativeMediaActiveRef.current = native.callId
          if (native.connected) {
            onAdapterConnected()
            if (native.remotePresent) onAdapterRemotePresent()
          }
        }
        return true
      }
      if (live && !isTerminalCallPhase(live.phase)) return false

      // Prefer answered stay-alive CallKit / live native media. A ringing-only
      // snapshot without answer should still go through drainPending.
      if (!callKit?.answered && !native) return false

      const peerId =
        callKit?.callerId && callKit.callerId !== currentUserId
          ? callKit.callerId
          : callKit?.calleeId && callKit.calleeId !== currentUserId
            ? callKit.calleeId
            : undefined
      // The snapshot's callerName may itself be a generic placeholder (e.g. a
      // 'Someone' relayed by an older backend). Prefer a real snapshot name,
      // then the synchronously-available directory cache (seeding must stay
      // immediate — no await before the session exists), then the neutral
      // label; upgradePlaceholderPeerName below repairs it asynchronously.
      const rawPeerName = callKit?.callerName?.trim() || ''
      const cachedPeerUser = peerId ? getCachedDirectoryUser(peerId) : undefined
      const peerName =
        (!isPlaceholderCallerName(rawPeerName) ? rawPeerName : '') ||
        cachedPeerUser?.displayName?.trim() ||
        rawPeerName ||
        'Call in progress'
      const mediaType = callKit?.mediaType === 'video' ? 'video' : 'voice'
      const sessionCallId = callKit?.joinCallId || callId
      const isGroup = Boolean(callKit?.roomInvite)
      // A CallKit / native snapshot that outlived a call this device already
      // ended (remote hang-up racing the foreground) must not re-seed it.
      // Room ids are reused on re-add, so only 1:1 calls are checked.
      if (!isGroup && isCallTombstoned(sessionCallId)) {
        recordCallFlow('ctrl:foreground:seedSkipped', { reason: 'tombstoned' }, { callId: sessionCallId })
        return false
      }
      const goActive = Boolean(native?.connected && native.remotePresent)

      recordCallFlow(
        'ctrl:foreground:seedFromCallKit',
        {
          answered: Boolean(callKit?.answered),
          nativeMedia: Boolean(callKit?.nativeMedia || native),
          nativeConnected: Boolean(native?.connected),
          remotePresent: Boolean(native?.remotePresent),
          hasPeerId: Boolean(peerId),
          goActive,
          isGroup,
        },
        { callId: sessionCallId },
      )

      setVoiceCallSession({
        callId: sessionCallId,
        callerId: callKit?.callerId || peerId || currentUserId,
        calleeId: callKit?.calleeId || currentUserId,
        conversationId: callKit?.conversationId ?? null,
        localRole: 'callee',
        peer: {
          userId: peerId || 'unknown',
          displayName: peerName,
        },
        phase: goActive ? 'active' : 'connecting',
        mediaType,
        isMuted: false,
        isCameraOff: mediaType !== 'video',
        startedAt: Date.now(),
        ...(goActive ? { connectedAt: Date.now() } : {}),
        callPurpose: callKit?.callPurpose ?? 'normal',
        userMinimized: false,
        notice: goActive ? undefined : RECONNECTING_NOTICE,
        ...(isGroup
          ? {
              isGroup: true as const,
              isHost: false,
              hostId: callKit?.callerId || peerId,
              maxParticipants: MAX_CALL_PARTICIPANTS,
              participants: [
                ...(peerId
                  ? [
                      {
                        userId: peerId,
                        displayName: peerName,
                        role: 'host' as const,
                        state: 'active' as const,
                      },
                    ]
                  : []),
                ...(currentUserId
                  ? [
                      {
                        userId: currentUserId,
                        displayName: localAttendeeDisplayName(currentUserId) ?? 'You',
                        role: 'guest' as const,
                        state: goActive ? ('active' as const) : ('ringing' as const),
                      },
                    ]
                  : []),
              ],
            }
          : {}),
      })
      // Async repair of a placeholder-seeded peer label (directory lookup) —
      // never blocks the immediate overlay seeding above.
      upgradePlaceholderPeerName(sessionCallId)

      if (native?.callId) {
        nativeMediaActiveRef.current = native.callId
        if (native.connected) {
          onAdapterConnected()
          if (native.remotePresent) onAdapterRemotePresent()
        }
      } else if (callKit?.nativeMedia || callKit?.answered) {
        // Native join may still be catching up — keep overlay up; sync/rehydrate
        // below adopts media when ready.
        nativeMediaActiveRef.current = sessionCallId
      }
      return true
    }

    const onCarStartCall = (peerId: string, peerName: string, tapTs?: number) => {
      if (!peerId) return
      const peer: ChatUser = {
        userId: peerId,
        email: '',
        displayName: peerName || ONE_TO_ONE_TELECOM_FALLBACK_NAME,
      }
      // Pass the signed-in user as the caller profile: without it the INVITE
      // payload's callerDisplayName fell back to 'Someone', which the callee
      // rendered verbatim on the ring + in-call UI (2026-10-07 field bug).
      void startCallWithMediaRef.current?.(peer, 'voice', null, cachedSelfChatUser(currentUserId), {
        fromCar: true,
        carTapAt: tapTs ?? Date.now(),
        bypassGate: true,
        bypassRecordingPolicyGate: true,
      }).catch((err) => {
        const alreadyOnCall = /already in a call/i.test(err instanceof Error ? err.message : String(err))
        // A re-delivery of the live car dial (its saved copy drained on
        // foreground): no alert — it surfaced after the driver hung up.
        if (alreadyOnCall && isDuplicateOfLiveCarDial(getVoiceCallSession(), peerId, tapTs)) {
          recordCallFlow('ctrl:carStart:duplicateOfLive', { peerId, tapTs: tapTs ?? null })
          return
        }
        void notifyNativeCarStartFailed({ message: resolveCarStartFailureAlert(err), alreadyOnCall })
      })
    }

    // Car End tapped while the car dial had no callId yet: end the dial now
    // (before its INVITE when the dial is still preparing).
    const onCarStartCancelled = () => {
      const dial = carDialRef.current
      const live = getVoiceCallSession()
      if (!dial || !live || live.callId !== dial.callId || live.phase !== 'outgoing') return
      recordCallFlow('ctrl:carStart:cancelled', {}, { callId: dial.callId })
      void hangUpRef.current({ endSource: 'car' })
      void endNativeOutgoingCall(dial.callId, 'canceled', dial.tapAt)
    }

    // Drain any Answer/Decline the user tapped while the WebView was not
    // running (backgrounded or just cold-started by the Answer/tap), and rebuild
    // the session from a killed-state FCM invite so the action can connect.
    const drainPending = async () => {
      const [invite, pending, carStart] = await Promise.all([
        consumeNativePendingIncomingCall(),
        consumeNativePendingCallAction(),
        consumeNativePendingCarStartCall(),
      ])
      if (cancelled) return

      // Cold start: no live session yet — rebuild it from the seeded invite so
      // Answer can join and Decline can reject. If the user just opened the app
      // (no tapped action), the rebuilt 'incoming' session shows the overlay.
      if (invite) {
        const answered =
          pending?.action === 'answer' &&
          (pending.callId === invite.callId || pending.callId === invite.joinCallId)
        await rebuildIncomingSessionFromNativeInvite(invite, answered ? { osAnswered: true } : undefined)
        if (cancelled) return
      }

      if (!pending) {
        if (carStart?.peerId) onCarStartCall(carStart.peerId, carStart.peerName ?? '', carStart.ts)
        return
      }
      if (pending.action === 'answer') {
        await onAnswer(pending.callId, pending.lockedAnswer ? { lockedAnswer: true } : undefined)
      } else if (pending.action === 'dismiss') onDismiss(pending.callId, pending.source)
      // 'end' = remote hang-up (or CallKit End) persisted while the WebView was
      // suspended — tears down an ACTIVE session (hangUp) as well as declining
      // a still-incoming one. Idempotent: no-ops when the session is gone.
      else if (pending.action === 'end') onEnd(pending.callId, pending.source)
      else onDecline(pending.callId)
    }

    void addIncomingCallActionListener({ onAnswer, onDecline }).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void addNativeCallActionListener({
      onAnswer,
      onDecline,
      onDismiss,
      onEnd,
      // CallKit mute (lock screen / CarPlay / AirPods): apply to whichever
      // media engine owns this call's audio and mirror onto the overlay so the
      // in-app mute button never disagrees with the real mic state.
      onMuteChanged: (callId, muted) => {
        const session = getVoiceCallSession()
        if (!session || session.callId !== callId || isTerminalCallPhase(session.phase)) return
        if (session.isMuted === muted) return
        if (nativeMediaActiveRef.current === session.callId) {
          void setNativeIosMediaMuted(muted)
        } else {
          callAdapterRef.current?.setMuted(muted)
        }
        patchVoiceCallSession({ isMuted: muted })
      },
    }).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void addNativeConnectionDestroyedListener(onConnectionDestroyed).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void addNativeAudioRouteListener(onAudioRoute).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void addNativeDeviceUnlockedListener(onDeviceUnlocked).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    removers.push(startLockScreenCallSync())
    void addNativeAudioSessionActivatedListener(onAudioSessionActivated).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void addNativeAudioSessionDeactivatedListener(onAudioSessionDeactivated).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void addNativeIncomingRingListener(onNativeIncomingRing).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void addNativeCarStartCallListener(onCarStartCall, onCarStartCancelled).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })

    // Mirror the native-iOS-media kill switch into native UserDefaults so the
    // LOCKED-screen mint path (which runs with this WebView suspended) honors an
    // explicit on/off override the next time a call is answered while locked.
    void syncNativeIosMediaEnabled()

    // Track CarPlay / Android Auto attach state (drives the in-car video pause).
    initCarConnectionStore()

    // Seed CarPlay / Android Auto recents from the real call history so the
    // car screen mirrors the Calls page (not just calls placed on this build).
    void syncCarRecentsFromHistory()
    void syncCarContactsFromDirectory()

    // NATIVE iOS AUDIO (ChimeMediaController) lifecycle → existing session state.
    // These map the native Chime SDK events onto the SAME patchVoiceCallSession
    // flow the JS adapter drives, so the overlay/roster/timer/mute/speaker UI is
    // identical whether audio ran native or in the WebView.
    void addNativeIosMediaListeners({
      // Native audio connected → treat exactly like an adapter connect (phase →
      // active, clear reconnecting, start ongoing-call/Telecom anchors).
      onConnected: (callId) => {
        const session = getVoiceCallSession()
        recordCallFlow('ctrl:nativeMedia:connected', { phase: session?.phase ?? null }, { callId })
        if (!session || session.callId !== callId || isTerminalCallPhase(session.phase)) return
        // Record native ownership even when no JS adopt path ran (locked-answer
        // mint) — the kick handler uses this to tell a SAME-DEVICE native
        // handoff apart from a genuine other-device takeover (2026-08-17).
        nativeMediaActiveRef.current = callId
        onAdapterConnected()
      },
      // Native connect/start failed → the same bounded-rejoin / grace-terminate
      // handling as a JS initial-connect failure (iOS rejoins the native path).
      onFailed: (callId, code, message) => {
        const session = getVoiceCallSession()
        recordCallFlow('ctrl:nativeMedia:failed', { code, message, phase: session?.phase ?? null }, { callId })
        if (!session || session.callId !== callId || isTerminalCallPhase(session.phase)) return
        // Locked / background CallKit answer: native start can fail transiently
        // (MediaError while CallKit still owns the session). Do NOT clear the
        // native-active marker or dispose→WebView-rejoin while the WebView is
        // hidden — that races stay-alive CallKit audio and was a crash /
        // zombie-ringtone surface (2026-08-04). Leave recovery to native
        // retryPendingStartIfNeeded / didActivate; foreground failures still
        // take the bounded-rejoin path below.
        const hidden =
          typeof document !== 'undefined' &&
          (document.visibilityState === 'hidden' || document.hidden)
        if (hidden) {
          recordCallFlow(
            'ctrl:nativeMedia:failedDeferred',
            { code, message, phase: session.phase },
            { callId },
          )
          return
        }
        nativeMediaActiveRef.current = null
        handleInitialConnectFailedRef.current(code)
      },
      // First remote attendee present in the NATIVE Chime roster = two-way media
      // established = the canonical connect on the iOS native-media path. This
      // mirrors onAdapterRemotePresent for the JS adapter: it flips the held
      // "Connecting…" state to a running timer at the SAME instant both parties
      // are in the meeting (nativeMediaConnected alone = LOCAL audio up, which
      // can precede the peer). Native audio is voice/1:1 first; group presence
      // stays on the JS adapter.
      onRosterChanged: (callId, remoteCount) => {
        const session = getVoiceCallSession()
        recordCallFlow(
          'ctrl:nativeMedia:roster',
          { remoteCount, phase: session?.phase ?? null },
          { callId },
        )
        if (!session || session.callId !== callId || isTerminalCallPhase(session.phase)) return
        if (remoteCount >= 1) onAdapterRemotePresent()
      },
      // Remote peer left the native meeting → same as adapter remote-left
      // (ends a connected 1:1 call).
      onRemoteLeft: (callId) => {
        const session = getVoiceCallSession()
        recordCallFlow('ctrl:nativeMedia:remoteLeft', { phase: session?.phase ?? null }, { callId })
        if (!session || session.callId !== callId) return
        onAdapterRemoteLeft()
      },
    }).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })

    // FOREGROUND-ONTO-CALL: locked CallKit answer + Dynamic Island / green-pill
    // return. `bringAppToForeground` opens ascendcomms://callkit/answered (handled
    // below); DI taps usually only activate the app — drive presentation from
    // visibility/resume + native `callKitReturn`. Register SYNCHRONOUS document
    // listeners (unlike async `appStateChange`, which can lose the first-resume race).
    const onForegroundRepresent = (preferredCallId?: string) => {
      if (cancelled) return
      representCallOnForeground()
      void (async () => {
        // Paint Active Call first (CallKit snapshot + native media), then drain
        // pending answer / backend rehydrate. Ordering fixes blank black screen
        // after Ascend Messenger unlock when invite was already consumed.
        await ensureSessionFromCallKit(preferredCallId)
        if (cancelled) return
        await drainPending()
        if (cancelled) return
        await syncNativeMediaOnForeground(preferredCallId)
        if (cancelled) return
        // If we still have no live session, CallKit may still own a stay-alive
        // call — rehydrate from backend then adopt native media again.
        const live = getVoiceCallSession()
        if (!live || isTerminalCallPhase(live.phase)) {
          const callKit = await getActiveCallKitCall()
          if (cancelled) return
          if (callKit?.callId) {
            recordCallFlow('ctrl:foreground:callKitActive', {
              answered: callKit.answered,
              nativeMedia: callKit.nativeMedia,
            }, { callId: callKit.callId })
          }
          // Last resort: seed again (invite fields) then backend rehydrate.
          await ensureSessionFromCallKit(preferredCallId ?? callKit?.callId)
          if (cancelled) return
          await rehydrateActiveCallFromBackend()
          if (!cancelled) await syncNativeMediaOnForeground(preferredCallId ?? callKit?.callId)
        }
        if (cancelled) return
        const after = getVoiceCallSession()
        void sweepNativeTelecomConnections(
          liveCallIdForNativeSweep(after, nativeAnswerRef.current),
        )
      })()
    }
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        onForegroundRepresent()
      }
    }
    // Capacitor/Cordova fire 'resume' on the document when the native app
    // returns to the foreground — a second, reliable foreground signal.
    // Wrap so the Event arg is not treated as preferredCallId.
    const onResumeEvent = () => {
      onForegroundRepresent()
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange)
      document.addEventListener('resume', onResumeEvent as EventListener)
      removers.push(() => document.removeEventListener('visibilitychange', onVisibilityChange))
      removers.push(() => document.removeEventListener('resume', onResumeEvent as EventListener))
    }
    // Run once at mount: on a locked answer the WebView may (re)mount already
    // foreground with a live call while the appStateChange 'active' fired before
    // this listener attached — represent the call now regardless so the overlay
    // is shown immediately instead of a blank screen.
    representCallOnForeground()

    // Consume on mount, and again every time the app returns to the foreground
    // (the WebView resumes after the Answer tap brought it forward). After the
    // drain, ask the backend whether we're in a live call the in-memory store
    // no longer knows about (WebView died mid-call) and re-hydrate it.
    void drainPending().then(() => {
      if (cancelled) return
      void syncNativeMediaOnForeground().then(() => {
        if (!cancelled) void rehydrateActiveCallFromBackend()
      })
    })
    removers.push(
      addCallKitAnsweredDeepLinkListener((link) => {
        if (cancelled) return
        recordCallFlow(
          'ctrl:deeplink:callkitAnswered',
          { hasCallId: Boolean(link.callId), hasUuid: Boolean(link.uuid) },
          { callId: link.callId ?? null },
        )
        onForegroundRepresent(link.callId)
      }),
    )
    void addNativeCallKitReturnListener((data) => {
      if (cancelled) return
      recordCallFlow(
        'ctrl:callKitReturn',
        { nativeMedia: data.nativeMedia, uuid: data.uuid ?? null },
        { callId: data.callId },
      )
      onForegroundRepresent(data.callId)
    }).then((remove) => {
      if (cancelled) remove()
      else removers.push(remove)
    })
    void (async () => {
      try {
        const { App } = await import('@capacitor/app')
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (cancelled) return
          if (isActive) {
            onForegroundRepresent()
          }
          // NOTE: we deliberately do NOT auto-minimize the overlay on background
          // anymore. Doing so (setting overlayMinimized on !isActive) relied on the
          // foreground event reliably clearing it — but on a locked CallKit answer
          // resume the async appStateChange listener can lose the race, leaving the
          // overlay stuck `invisible` (the blank-screen-after-answer bug). Overlay
          // visibility now keys on the EXPLICIT `userMinimized` flag only, so a
          // backgrounded call always re-presents its full-screen UI on return.
        })
        if (cancelled) void handle.remove()
        else removers.push(() => void handle.remove())
      } catch {
        /* @capacitor/app unavailable — mount-time drain still runs */
      }
    })()

    return () => {
      cancelled = true
      for (const remove of removers) remove()
    }
  }, [currentUserId, acceptCall, declineCall, hangUp, abandonLocalIncomingRing, rebuildIncomingSessionFromNativeInvite, requestAcceptCall, rehydrateActiveCallFromBackend, onAdapterConnected, onAdapterRemotePresent, onAdapterRemoteLeft])

  // Web: a page reload during a live call loses the in-memory session the same
  // way a reclaimed WebView does — rehydrate once auth is ready. (Native runs
  // this from the drain effect above, gated on isNativeApp.)
  useEffect(() => {
    if (!currentUserId || isNativeApp()) return
    void rehydrateActiveCallFromBackend()
  }, [currentUserId, rehydrateActiveCallFromBackend])

  const recallLastCall = useCallback(async () => {
    const peer = lastRecallPeerRef.current
    if (!peer || !currentUserId) return
    const existing = getVoiceCallSession()
    if (existing && existing.phase !== 'ended' && existing.phase !== 'idle') {
      await terminateCall('cancelled', undefined, undefined, { endPayloadReason: 'CANCELLED' })
    } else {
      setVoiceCallSession(null)
    }
    await startCallWithMedia(
      peer,
      lastRecallMediaRef.current,
      lastRecallConversationRef.current,
      undefined,
    )
  }, [currentUserId, startCallWithMedia, terminateCall])

  const toggleMute = useCallback(() => {
    const session = getVoiceCallSession()
    if (!session) return
    const next = !session.isMuted
    // Native iOS audio path: mute the native Chime audio unit (the JS adapter
    // isn't the media engine for this call). Otherwise use the JS adapter.
    if (nativeMediaActiveRef.current && nativeMediaActiveRef.current === session.callId) {
      void setNativeIosMediaMuted(next)
    } else {
      callAdapterRef.current?.setMuted(next)
    }
    patchVoiceCallSession({ isMuted: next })
  }, [])

  /**
   * Tell the peer we put them on / took them off hold (1:1 only). The local
   * mute + playback silencing is handled by the overlay; the peer's device
   * starts/stops hold music on the HOLD/UNHOLD relay.
   */
  const notifyPeerHold = useCallback((onHold: boolean) => {
    const session = getVoiceCallSession()
    if (!session || session.isGroup || session.phase !== 'active') return
    void publishVoiceCallSignal({
      callId: session.callId,
      type: onHold ? 'HOLD' : 'UNHOLD',
      callerId: session.callerId,
      calleeId: session.calleeId,
      conversationId: session.conversationId,
    }).catch(() => {
      /* relay is best-effort — local hold still applies */
    })
  }, [])

  const toggleCamera = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.mediaType !== 'video') return
    // While driving, the camera never starts; the overlay's explicit
    // "I've stopped — turn video on" control is the only way back.
    // NO extra isCarConnected() guard here: once the driver explicitly said
    // "I've stopped" (selfDriving cleared), the camera toggle must work — and
    // car-output heuristics (docks) must never block group-call cameras.
    if (session.selfDriving) return
    const nextOff = !session.isCameraOff
    const adapter = callAdapterRef.current
    if (!adapter) return
    if (!nextOff) {
      // Turning the camera ON. In a group call the meeting joined audio-only
      // (camera consent is per-participant), so the first enable must actually
      // start the camera + video tile, not just re-show an existing tile.
      if (session.isGroup) {
        const permissions = await ensureCallPermissionsForCall({ video: true })
        if (permissions.camera === 'denied') {
          patchVoiceCallSession({ errorMessage: cameraDeniedMessage() })
          return
        }
        try {
          await adapter.enableVideo({
            localVideo: localVideoRef.current,
            remoteVideo: remoteVideoRef.current,
            contentVideo: contentVideoRef.current,
          })
        } catch (err) {
          patchVoiceCallSession({
            errorMessage: err instanceof Error ? err.message : 'Could not start camera',
          })
          return
        }
      } else {
        await adapter.setCameraEnabled(true)
      }
    } else {
      if (session.isGroup) await adapter.disableVideo()
      else await adapter.setCameraEnabled(false)
    }
    patchVoiceCallSession({ isCameraOff: nextOff })
  }, [])

  const toggleSpeaker = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session) return
    const prevDesired = desiredCallSpeakerOn()
    const hadToggle = callAudioRouteArbiter.hasUserToggle()
    const next = !prevDesired
    callAudioRouteArbiter.userToggle(next)
    // Prefer the live adapter (routes via native AudioManager/AVAudioSession and,
    // on web, the Chime output device). While dialing/ringing the meeting isn't
    // joined yet, so fall back to the native audio route directly — on native
    // the route is owned by AudioManager/AVAudioSession, not the Chime meeting.
    const adapter = callAdapterRef.current
    // Native iOS audio path owns routing via the Chime SDK's chooseAudioDevice;
    // the JS adapter is not the media engine for this call.
    const nativeMediaActive = Boolean(
      nativeMediaActiveRef.current && nativeMediaActiveRef.current === session.callId,
    )
    const ok = nativeMediaActive
      ? await setNativeIosMediaSpeaker(next)
      : adapter
        ? ((await adapter.setSpeakerphone(next)) ?? false)
        : await setNativeSpeakerphone(next)

    if (isNativeApp()) {
      // Native routing must actually succeed before we flip the UI, otherwise
      // the button would lie about the active route.
      if (ok) {
        patchVoiceCallSession({ isSpeakerOn: next, errorMessage: undefined })
      } else {
        if (hadToggle) callAudioRouteArbiter.userToggle(prevDesired)
        else callAudioRouteArbiter.clearUserToggle()
        patchVoiceCallSession({
          errorMessage: 'Speaker routing is not supported on this device',
        })
      }
      return
    }

    // Web: reflect the user's intent regardless of whether setSinkId could
    // reach an earpiece device. Mobile browsers cannot truly route to the
    // earpiece, but the toggle should still track what the user selected.
    patchVoiceCallSession({ isSpeakerOn: next, errorMessage: undefined })
  }, [])

  const setRemoteAudioElement = useCallback((el: HTMLAudioElement | null) => {
    remoteAudioRef.current = el
    if (el) {
      callAdapterRef.current?.updateAudioElement(el)
    }
  }, [])

  const setVideoElements = useCallback(
    (local: HTMLVideoElement | null, remote: HTMLVideoElement | null) => {
      localVideoRef.current = local
      remoteVideoRef.current = remote
      if (local && remote) {
        callAdapterRef.current?.updateElements({ localVideo: local, remoteVideo: remote })
      }
    },
    [],
  )

  // The screen-share (content) tile element mounts only while someone presents.
  const setContentVideoElement = useCallback((el: HTMLVideoElement | null) => {
    contentVideoRef.current = el
    if (el) callAdapterRef.current?.updateElements({ contentVideo: el })
  }, [])

  /** Re-bind Chime content tiles after the presenting UI expands (mobile WebView). */
  const refreshContentVideoBinding = useCallback(() => {
    const el = contentVideoRef.current
    if (!el) return
    callAdapterRef.current?.updateElements({ contentVideo: el })
    void el.play().catch(() => {
      /* autoplay may require a user gesture on some browsers */
    })
  }, [])

  // Start sharing the screen (web only — uses getDisplayMedia via Chime content
  // share). Works during both voice and video calls. The browser shows its own
  // source picker; cancelling it is not an error.
  const startScreenShare = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session || session.phase !== 'active') return
    const adapter = callAdapterRef.current
    if (!adapter) return
    try {
      await adapter.startContentShare()
      // isPresenting flips via the adapter's contentShareDidStart event.
    } catch (err) {
      const message = err instanceof Error ? err.message : ''
      // User dismissed the picker → not an error worth surfacing.
      if (/Permission denied|NotAllowed|dismiss|cancel/i.test(message)) return
      showNotice('Could not start screen sharing')
    }
  }, [showNotice])

  const stopScreenShare = useCallback(async () => {
    await callAdapterRef.current?.stopContentShare()
  }, [])

  // Apply a local camera background filter (none / blur / replacement image) via
  // the Chime background processors behind the adapter. The selection is stashed
  // even when the camera is off, so it takes effect the moment video starts.
  const setBackgroundFilter = useCallback(
    async (selection: BackgroundFilterSelection) => {
      const session = getVoiceCallSession()
      if (!session) return
      patchVoiceCallSession({
        backgroundFilterId: selection.id,
        backgroundFilterPending: true,
      })
      try {
        await callAdapterRef.current?.setBackgroundFilter(
          selection.kind === 'blur'
            ? { kind: 'blur' }
            : selection.kind === 'image' && selection.imageUrl
              ? { kind: 'image', imageUrl: selection.imageUrl }
              : { kind: 'none' },
        )
      } catch {
        showNotice('Could not apply that background')
      } finally {
        patchVoiceCallSession({ backgroundFilterPending: false })
      }
    },
    [showNotice],
  )

  // ==========================================================================
  // Multi-party (group) calling
  // ==========================================================================

  /** Publish an in-room signal on the per-call room channel (onCallRoomSignal). */
  const sendRoomSignal = useCallback(
    (callId: string, type: VoiceCallSignal['type'], payload?: RoomSignalPayload) => {
      const session = getVoiceCallSession()
      void publishVoiceCallSignal({
        callId,
        type,
        callerId: session?.hostId ?? currentUserId ?? callId,
        calleeId: currentUserId ?? callId,
        conversationId: session?.conversationId,
        roomCallId: callId,
        payload: payload as Record<string, unknown> | undefined,
      }).catch(() => {})
    },
    [currentUserId],
  )

  /** Self leaves the room (the call continues for everyone else). */
  const leaveRoom = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session?.isGroup) return
    // Reconcile the roster against Chime's authoritative attendee list FIRST so
    // a ghost / stale 'active' entry (e.g. a placeholder seeded during a
    // transfer or merge, or a peer whose PARTICIPANT_LEFT never arrived) can't
    // inflate the count and make us wrongly think the call is still live. A
    // phantom 'active' entry was the root cause of merge/callback calls never
    // producing a CallHistoryRecord: the true last leaver never detected they
    // were last, so the terminal room END was never sent.
    let roster = dedupeParticipants(session.participants ?? [])
    const adapter = callAdapterRef.current
    if (adapter) {
      const present = new Set(adapter.getPresentExternalUserIds())
      roster = reconcileActiveAgainstPresence(roster, present, currentUserId)
    }
    // Are we the last active participant? If so, leaving empties the room, so we
    // must record the group call's terminal history (otherwise a call that ends
    // by everyone simply leaving — rather than the host using "End for everyone"
    // — would never produce a CallHistoryRecord for any participant).
    const isLastParticipant = countActiveRemote(roster, currentUserId) === 0
    if (currentUserId) {
      sendRoomSignal(session.callId, 'PARTICIPANT_LEFT', {
        kind: 'left',
        userId: currentUserId,
      })
    }
    if (isLastParticipant) {
      const participantIds = roomEndParticipantIds(roster)
      void publishVoiceCallSignal({
        callId: session.callId,
        type: 'END',
        callerId: session.hostId ?? currentUserId ?? session.callId,
        calleeId: currentUserId ?? session.callId,
        conversationId: session.conversationId,
        roomCallId: session.callId,
        payload: {
          scope: 'room',
          reason: 'COMPLETED',
          // Invitees may close an empty room (last leaver) without the
          // host-only "end for everyone" gate blocking teardown.
          lastParticipant: true,
          // Only the host knows the true call start; a late joiner omits it so
          // the server uses the authoritative room start (earliest INVITE) and
          // the recorded duration matches the real call length.
          ...(session.isHost && session.startedAt
            ? { startedAt: new Date(session.startedAt).toISOString() }
            : {}),
          endedAt: new Date().toISOString(),
          mediaType: session.mediaType,
          participantIds,
        },
      }).catch(() => {})
    }
    finishCall('completed')
    await disposeAdapter()
  }, [currentUserId, disposeAdapter, sendRoomSignal])
  leaveRoomRef.current = leaveRoom

  /** End the call for EVERYONE (host only). */
  const endCallForEveryone = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session?.isGroup || !session.isHost) return
    const participantIds = roomEndParticipantIds(session.participants ?? [])
    sendRoomSignal(session.callId, 'END', {
      kind: 'left',
      userId: currentUserId ?? '',
    } as RoomSignalPayload)
    // Also send the terminal END with bookkeeping payload (scope:'room').
    void publishVoiceCallSignal({
      callId: session.callId,
      type: 'END',
      callerId: session.hostId ?? currentUserId ?? session.callId,
      calleeId: currentUserId ?? session.callId,
      conversationId: session.conversationId,
      roomCallId: session.callId,
      payload: {
        scope: 'room',
        reason: 'COMPLETED',
        startedAt: session.startedAt ? new Date(session.startedAt).toISOString() : undefined,
        endedAt: new Date().toISOString(),
        mediaType: session.mediaType,
        participantIds,
      },
    }).catch(() => {})
    finishCall('completed')
    await disposeAdapter()
  }, [currentUserId, disposeAdapter, sendRoomSignal])

  /**
   * Reconcile the live roster against Chime's authoritative attendee presence:
   * de-dupe by userId and drop any 'active' participant who is not actually in
   * the meeting (a ghost / stale entry — the phantom-participant bug). Runs only
   * once the call is connected so a pending invitee or a not-yet-present joiner
   * is never dropped.
   */
  const reconcileGroupRoster = useCallback(() => {
    const session = getVoiceCallSession()
    if (!session?.isGroup) return
    const list = session.participants ?? []
    let next = dedupeParticipants(list)
    const adapter = callAdapterRef.current
    if (adapter && session.phase === 'active') {
      const present = new Set(adapter.getPresentExternalUserIds())
      next = reconcileActiveAgainstPresence(next, present, currentUserId)
    }
    if (!rostersEqual(list, next)) {
      patchVoiceCallSession({ participants: next })
    }
  }, [currentUserId])
  const reconcileGroupRosterRef = useRef(reconcileGroupRoster)
  reconcileGroupRosterRef.current = reconcileGroupRoster

  /** Debounced reconcile so a roster broadcast that lands just before a peer's
   *  Chime presence callback isn't mistaken for a ghost. */
  const scheduleRosterReconcile = useCallback(() => {
    if (rosterReconcileTimerRef.current) clearTimeout(rosterReconcileTimerRef.current)
    rosterReconcileTimerRef.current = setTimeout(() => {
      rosterReconcileTimerRef.current = null
      reconcileGroupRosterRef.current()
    }, 1500)
  }, [])
  const scheduleRosterReconcileRef = useRef(scheduleRosterReconcile)
  scheduleRosterReconcileRef.current = scheduleRosterReconcile

  /** Group liveness: when every other participant has left, leave too. */
  const evaluateGroupLiveness = useCallback(() => {
    const session = getVoiceCallSession()
    if (!session?.isGroup || session.phase !== 'active') return
    if (countActiveRemote(session.participants, currentUserId) === 0) {
      void leaveRoom()
    }
  }, [currentUserId, leaveRoom])

  // --- Room signaling channel ---
  const handleRoomSignal = useCallback(
    (signal: VoiceCallSignal) => {
      const dedupeKey = `room:${signal.roomCallId}:${signal.type}:${signal.createdAt}:${signal.senderId}`
      if (handledSignalsRef.current.has(dedupeKey)) return
      handledSignalsRef.current.add(dedupeKey)
      if (signal.senderDeviceId && signal.senderDeviceId === getDeviceId()) return

      const session = getVoiceCallSession()
      if (!session?.isGroup || session.callId !== signal.roomCallId) return

      const payload = parseSignalPayload<{
        kind?: string
        to?: CallMediaType
        userId?: string
        displayName?: string
        avatarUrl?: string | null
        role?: 'host' | 'guest'
        participants?: CallParticipant[]
      }>(signal.payload)
      const kind = payload?.kind

      if (kind === 'roster' && Array.isArray(payload?.participants)) {
        const incoming = payload.participants as CallParticipant[]
        const existing = session.participants ?? []
        const byId = new Map(existing.map((p) => [p.userId, p]))
        for (const p of incoming) {
          const prev = byId.get(p.userId)
          // Keep our locally-observed live state (presence/volume) authoritative.
          byId.set(p.userId, {
            ...p,
            ...(prev
              ? {
                  state: prev.state === 'active' ? 'active' : p.state,
                  attendeeId: prev.attendeeId ?? p.attendeeId,
                  tileId: prev.tileId ?? p.tileId,
                  mutedSelf: prev.mutedSelf,
                  speaking: prev.speaking,
                }
              : {}),
          })
        }
        const merged = dedupeParticipants(Array.from(byId.values()))
        if (!rostersEqual(existing, merged)) {
          patchVoiceCallSession({ participants: merged })
        }
        scheduleRosterReconcileRef.current()
        return
      }

      if (kind === 'join' && payload?.userId) {
        resolvePeerGroupMigration(session.callId, payload.userId)
        upsertGroupParticipant(payload.userId, {
          displayName: payload.displayName ?? 'Participant',
          avatarUrl: payload.avatarUrl,
          role: payload.role ?? 'guest',
          state: 'active',
        })
        // The host re-broadcasts the full roster so late joiners converge.
        if (session.isHost && currentUserId) {
          const live = getVoiceCallSession()
          sendRoomSignal(session.callId, 'ROSTER', {
            kind: 'roster',
            participants: live?.participants ?? [],
          })
        }
        scheduleRosterReconcileRef.current()
        return
      }

      if (kind === 'left' && payload?.userId) {
        upsertGroupParticipant(payload.userId, {
          state: 'left',
          tileId: null,
          attendeeId: null,
          speaking: false,
        })
        evaluateGroupLiveness()
        return
      }

      if (kind === 'remove' && payload?.userId) {
        const targetId = payload.userId
        if (targetId === currentUserId) {
          showNotice('You were removed from the call')
          void leaveRoom()
          return
        }
        upsertGroupParticipant(targetId, { state: 'left', tileId: null, speaking: false })
        return
      }

      if (kind === 'ask-to-mute' && payload?.userId) {
        const targetId = payload.userId
        if (targetId === currentUserId) {
          const live = getVoiceCallSession()
          if (live && !live.isMuted) {
            callAdapterRef.current?.setMuted(true)
            patchVoiceCallSession({ isMuted: true })
            showNotice('You were muted by the host')
          }
        }
        return
      }

      if (kind === 'media' && payload?.to) {
        // Host promoted the call to video / back to voice. Switch the overlay
        // mode for everyone; each participant's camera stays off until they tap.
        patchVoiceCallSession({ mediaType: payload.to })
        return
      }

      // Terminal room END (host ended for everyone).
      if (signal.type === 'END') {
        showNotice('The host ended the call')
        finishCall('completed')
        void disposeAdapter()
      }
    },
    [currentUserId, disposeAdapter, evaluateGroupLiveness, leaveRoom, resolvePeerGroupMigration, sendRoomSignal, showNotice],
  )
  const handleRoomSignalRef = useRef(handleRoomSignal)
  handleRoomSignalRef.current = handleRoomSignal

  const subscribeRoom = useCallback(
    (callId: string) => {
      if (roomSubCallIdRef.current === callId && roomSubRef.current) return
      try {
        roomSubRef.current?.unsubscribe()
      } catch {
        /* already closed */
      }
      roomSubCallIdRef.current = callId

      // ROOM# row keep-alive (see ROOM_KEEPALIVE_INTERVAL_MS). Cleared with the
      // room subscription in disposeAdapter.
      if (roomKeepAliveTimerRef.current) clearInterval(roomKeepAliveTimerRef.current)
      roomKeepAliveTimerRef.current = setInterval(() => {
        const live = getVoiceCallSession()
        if (
          !live ||
          !live.isGroup ||
          live.callId !== callId ||
          isTerminalCallPhase(live.phase) ||
          live.phase === 'elsewhere'
        ) {
          return
        }
        sendRoomSignal(callId, 'HEARTBEAT', { kind: 'keepalive' })
      }, ROOM_KEEPALIVE_INTERVAL_MS)

      let attempt = 0
      const connect = () => {
        if (roomSubCallIdRef.current !== callId) return
        const obs = graphqlSubscription(onCallRoomSignalSubscription, {
          callId,
        }) as VoiceCallSubscription
        roomSubRef.current = obs.subscribe({
          next: (event) => {
            attempt = 0
            const signal = event.data?.onCallRoomSignal
            if (signal) handleRoomSignalRef.current(signal)
          },
          error: (err) => {
            if (roomSubCallIdRef.current !== callId) return
            console.warn('[call-room] subscription error — reconnecting', err)
            const delay = Math.min(15_000, 1000 * 2 ** Math.min(attempt, 4))
            attempt += 1
            setTimeout(() => {
              if (roomSubCallIdRef.current === callId) connect()
            }, delay)
          },
        })
      }
      connect()
    },
    [sendRoomSignal],
  )

  // --- Adapter group callbacks (stale-closure safe via refs) ---
  const handleRosterPresence = useCallback(
    (attendee: { attendeeId: string; externalUserId: string }, present: boolean) => {
      const session = getVoiceCallSession()
      if (!session?.isGroup) return
      const userId = attendee.externalUserId
      if (!userId || userId === currentUserId) return
      // Defensive: the media-capture/recording pipeline joins as a synthetic
      // `MediaPipeline-*` attendee. The adapter already filters it, but never
      // let one slip through as a phantom "Participant" tile/roster entry.
      if (/^MediaPipeline/i.test(userId) || /^MediaPipeline/i.test(attendee.attendeeId)) return
      if (present) {
        upsertGroupParticipant(userId, { attendeeId: attendee.attendeeId, state: 'active' })
        const existing = getVoiceCallSession()?.participants?.find((p) => p.userId === userId)
        if (!existing || !existing.displayName || existing.displayName === 'Participant') {
          void resolveDirectoryUser(userId)
            .then((u) => {
              if (u) upsertGroupParticipant(userId, { displayName: u.displayName, avatarUrl: u.avatarUrl })
            })
            .catch(() => {})
        }
        // A real attendee just became present — reconcile away any ghost entry.
        scheduleRosterReconcileRef.current()
      } else {
        upsertGroupParticipant(userId, {
          state: 'left',
          attendeeId: null,
          tileId: null,
          speaking: false,
        })
        evaluateGroupLiveness()
      }
    },
    [currentUserId, evaluateGroupLiveness],
  )
  handleRosterPresenceRef.current = handleRosterPresence

  const handleAttendeeAudio = useCallback(
    (
      attendee: { attendeeId: string; externalUserId: string },
      state: { muted: boolean; speaking: boolean },
    ) => {
      const session = getVoiceCallSession()
      if (!session?.isGroup) return
      if (attendee.externalUserId === currentUserId) return
      upsertGroupParticipant(attendee.externalUserId, {
        mutedSelf: state.muted,
        speaking: state.speaking,
      })
    },
    [currentUserId],
  )
  handleAttendeeAudioRef.current = handleAttendeeAudio

  const handleRemoteTileAdded = useCallback(
    (tile: { tileId: number; externalUserId: string }) => {
      const session = getVoiceCallSession()
      if (!session?.isGroup) return
      if (tile.externalUserId === currentUserId) return
      upsertGroupParticipant(tile.externalUserId, { tileId: tile.tileId, cameraOn: true })
    },
    [currentUserId],
  )
  handleRemoteTileAddedRef.current = handleRemoteTileAdded

  const handleRemoteTileRemoved = useCallback((tileId: number) => {
    const participant = findParticipantByTile(tileId)
    if (participant) {
      upsertGroupParticipant(participant.userId, { tileId: null, cameraOn: false })
    }
  }, [])
  handleRemoteTileRemovedRef.current = handleRemoteTileRemoved

  const handleVideoSendUnavailable = useCallback(() => {
    patchVoiceCallSession({ videoSendUnavailable: true })
    showNotice('Video is full right now')
  }, [showNotice])
  handleVideoSendUnavailableRef.current = handleVideoSendUnavailable

  // --- Join a multi-party meeting (audio-only; camera enabled on demand) ---
  const joinGroupMeeting = useCallback(
    async (callId: string, displayName?: string, mediaType: CallMediaType = 'voice') => {
      if (!currentUserId) return
      if (shouldAbortJoin(callId)) return
      const audioEl = await waitForRemoteAudioElement()
      if (shouldAbortJoin(callId)) return

      callAudioRouteArbiter.setMediaType(mediaType === 'video' ? 'video' : 'voice')
      callAudioRouteArbiter.setPhase('connecting')
      patchVoiceCallSession({
        phase: 'connecting',
        mediaType,
        isSpeakerOn: desiredCallSpeakerOn(),
      })

      // NATIVE DOUBLE-JOIN GUARD (mirrors the 1:1 join path): on iOS a locked /
      // background CallKit answer starts the NATIVE Chime engine before any JS
      // runs (AscendCallManager.maybeStartNativeMediaMint). Joining the same
      // meeting again through the JS adapter uses the SAME attendeeId, so Chime
      // kicks one session with AudioJoinedFromAnotherDevice — which the kick
      // handler used to misread as "call on another device" (2026-08-17:
      // Olivia's iOS group join). If native media already owns this callId,
      // adopt it: skip the JS adapter entirely but still announce/subscribe to
      // the room (signaling is independent of the media plane).
      let nativeMediaAdopted = false
      if (mediaType !== 'video' && isNativeIosAudioEnabled()) {
        const nativeActive = await getNativeIosMediaActiveCall()
        if (shouldAbortJoin(callId)) return
        if (nativeActive && nativeActive.callId === callId) {
          nativeMediaAdopted = true
          nativeMediaActiveRef.current = callId
          recordCallFlow(
            'ctrl:nativeMedia:adoptExistingGroup',
            { connected: nativeActive.connected },
            { callId },
          )
          const liveNow = getVoiceCallSession()
          if (liveNow?.isMuted) void setNativeIosMediaMuted(true)
          // Narrowed to voice by the mediaType guard above (native audio is voice-only).
          const speakerOnNative = desiredCallSpeakerOn()
          void setNativeIosMediaSpeaker(speakerOnNative)
          patchVoiceCallSession({ isSpeakerOn: speakerOnNative })
          if (nativeActive.connected) {
            const s = getVoiceCallSession()
            if (s && s.callId === callId && !isTerminalCallPhase(s.phase)) {
              onAdapterConnected()
              if (nativeActive.remotePresent) onAdapterRemotePresent()
            }
          }
        }
      }

      if (!nativeMediaAdopted) {
        if (getNativePlatform() === 'ios' && isDocumentHidden()) {
          recordCallFlow(
            'ctrl:join:waitVisible',
            { mediaType, group: true, visibility: 'hidden' },
            { callId },
          )
          const waited = await waitUntilDocumentVisible(() => shouldAbortJoin(callId))
          if (waited === 'aborted' || shouldAbortJoin(callId)) return
        }
        const requestToken = () =>
          chimeService.requestJoinToken({
            externalMeetingId: callId,
            attendeeDisplayName: displayName,
            enableVideo: false,
          })

        let credentials = await requestToken()
        if (shouldAbortJoin(callId)) return
        for (let attempt = 0; attempt < 3; attempt++) {
          if (shouldAbortJoin(callId)) return
          try {
            await getCallAdapter().join(
              credentials,
              currentUserId,
              {
                audio: audioEl,
                localVideo: localVideoRef.current,
                remoteVideo: remoteVideoRef.current,
                contentVideo: contentVideoRef.current,
              },
              {
                enableVideo: false,
                multiparty: true,
                speakerOn: desiredCallSpeakerOn(),
              },
            )
            break
          } catch (err) {
            if (err instanceof CallFullError) throw err
            if (shouldAbortJoin(callId)) return
            const message = err instanceof Error ? err.message : ''
            const retriable =
              message.includes('audioHostUrl') || message.includes('incomplete meeting')
            if (!retriable || attempt === 2) throw err
            await disposeAdapter()
            await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
            credentials = await requestToken()
          }
        }

        const live = getVoiceCallSession()
        if (live?.isMuted) callAdapterRef.current?.setMuted(true)
        if (isNativeApp()) {
          const speakerOn = desiredCallSpeakerOn()
          const ok = (await callAdapterRef.current?.setSpeakerphone(speakerOn)) ?? false
          if (ok) patchVoiceCallSession({ isSpeakerOn: speakerOn })
        }
      }

      // Mark self active + announce to the room; subscribe to the channel.
      upsertGroupParticipant(currentUserId, { state: 'active' })
      subscribeRoom(callId)
      const me = cachedSelfChatUser(currentUserId)
      const current = getVoiceCallSession()
      sendRoomSignal(callId, 'PARTICIPANT_JOIN', {
        kind: 'join',
        userId: currentUserId,
        displayName: me?.displayName ?? displayName ?? 'Participant',
        avatarUrl: me?.avatarUrl,
        role: current?.isHost ? 'host' : 'guest',
      })

      const joined = getVoiceCallSession()
      if (
        joined?.transferRoom &&
        joined.callerId &&
        joined.callerId !== currentUserId &&
        joined.peer.userId !== joined.callerId
      ) {
        const forwardedPeer = await resolvePeerByUserId(joined.callerId, {
          displayName: joined.peer.displayName,
          avatarUrl: joined.peer.avatarUrl,
        })
        patchVoiceCallSession({
          callerId: joined.callerId,
          peer: forwardedPeer,
        })
      }
    },
    [
      currentUserId,
      disposeAdapter,
      getCallAdapter,
      onAdapterConnected,
      onAdapterRemotePresent,
      sendRoomSignal,
      subscribeRoom,
      waitForRemoteAudioElement,
    ],
  )
  joinGroupMeetingRef.current = joinGroupMeeting

  const buildParticipantsFromOneToOne = useCallback(
    (
      session: NonNullable<ReturnType<typeof getVoiceCallSession>>,
      hostId: string,
    ): CallParticipant[] => {
      const participants: CallParticipant[] = []
      const seen = new Set<string>()
      const add = (userId: string, state: CallParticipant['state']) => {
        // Skip empties, the synthetic `group:<callId>` placeholder peer, and any
        // id already added — these are the classic sources of a phantom extra
        // participant when a 1:1 is upgraded into a group.
        if (!userId || userId.startsWith('group:') || seen.has(userId)) return
        seen.add(userId)
        const isPeer = userId !== currentUserId && session.peer.userId === userId
        participants.push({
          userId,
          displayName:
            userId === currentUserId
              ? (cachedSelfChatUser(currentUserId)?.displayName ?? 'You')
              : isPeer
                ? session.peer.displayName
                : 'Participant',
          avatarUrl:
            userId === currentUserId
              ? cachedSelfChatUser(currentUserId)?.avatarUrl
              : isPeer
                ? session.peer.avatarUrl
                : undefined,
          role: userId === hostId ? 'host' : 'guest',
          state,
        })
      }
      add(session.callerId, 'active')
      add(session.calleeId, 'active')
      return participants
    },
    [currentUserId],
  )

  const migrateToGroupMode = useCallback(
    async (asHost: boolean, hostIdOverride?: string, options?: { transfer?: boolean }) => {
      const session = getVoiceCallSession()
      if (!session || session.isGroup || session.phase !== 'active' || !currentUserId) return

      // The host is whoever initiated the upgrade — passed explicitly to the
      // non-host party so every view agrees on who the host is (the original
      // 1:1 caller is NOT necessarily the upgrader).
      const hostId = asHost ? currentUserId : (hostIdOverride ?? session.callerId)
      const participants = buildParticipantsFromOneToOne(session, hostId)

      // A transfer keeps the room mechanics under the hood (multi-party audio,
      // liveness, terminal record) but must still render the 1:1 overlay — so we
      // mark it a transferRoom and KEEP the real 1:1 peer (don't swap it for a
      // "Group call" placeholder).
      const isTransfer = options?.transfer === true

      patchVoiceCallSession({
        isGroup: true,
        ...(isTransfer ? { transferRoom: true } : {}),
        isHost: asHost,
        hostId,
        maxParticipants: MAX_CALL_PARTICIPANTS,
        groupTitle: session.groupTitle ?? session.peer.displayName,
        participants,
        ...(isTransfer
          ? {}
          : {
              peer: {
                userId: `group:${session.callId}`,
                displayName: session.groupTitle ?? 'Group call',
              },
            }),
      })

      callAdapterRef.current?.enableMultipartyMode()
      clearHeartbeatTimers()
      subscribeRoom(session.callId)

      if (asHost) {
        const live = getVoiceCallSession()
        sendRoomSignal(session.callId, 'ROSTER', {
          kind: 'roster',
          participants: live?.participants ?? participants,
        })
      } else if (currentUserId) {
        const me = cachedSelfChatUser(currentUserId)
        sendRoomSignal(session.callId, 'PARTICIPANT_JOIN', {
          kind: 'join',
          userId: currentUserId,
          displayName: me?.displayName ?? 'Participant',
          avatarUrl: me?.avatarUrl,
          role: 'guest',
        })
      }
    },
    [buildParticipantsFromOneToOne, clearHeartbeatTimers, currentUserId, sendRoomSignal, subscribeRoom],
  )
  migrateToGroupModeRef.current = migrateToGroupMode

  const sendPeerGroupUpgrade = useCallback(
    async (options?: { transfer?: boolean }) => {
      const session = getVoiceCallSession()
      if (!session || !currentUserId) return
      const base = {
        callId: session.callId,
        callerId: session.callerId,
        calleeId: session.calleeId,
        conversationId: session.conversationId,
      }
      await publishVoiceCallSignal(
        publishTransferInput(base, 'upgrade_group', {
          transferrerId: currentUserId,
          ...(options?.transfer ? { transfer: true } : {}),
        }),
      ).catch(() => {})
    },
    [currentUserId],
  )

  const inviteUsersToActiveCall = useCallback(
    async (users: ChatUser[], options?: { transfer?: boolean }) => {
      const session = getVoiceCallSession()
      if (!session || !currentUserId || session.phase !== 'active') return

      const isTransfer = options?.transfer === true

      if (!session.isGroup) {
        const peerUserId =
          session.callerId === currentUserId ? session.calleeId : session.callerId
        await migrateToGroupMode(true, undefined, { transfer: isTransfer })
        await sendPeerGroupUpgrade({ transfer: isTransfer })
        await waitForPeerGroupMigration(session.callId, peerUserId)
      }

      const live = getVoiceCallSession()
      if (!live?.isGroup || !live.isHost) return

      const current = live.participants ?? []
      const liveCount = current.filter(
        (p) => p.state !== 'left' && p.state !== 'declined',
      ).length
      let remaining = MAX_CALL_PARTICIPANTS - liveCount
      const me = cachedSelfChatUser(currentUserId)

      // People the server refused to ring because they haven't accepted call
      // recording (one INVITE per invitee): skipped, reported once at the end.
      const invitePublishes: Promise<GroupInviteOutcome>[] = []

      for (const user of users) {
        if (!user.userId || user.userId === currentUserId) continue
        if (
          current.some(
            (p) => p.userId === user.userId && p.state !== 'left' && p.state !== 'declined',
          )
        ) {
          continue
        }
        if (remaining <= 0) {
          showNotice(`This call is limited to ${MAX_CALL_PARTICIPANTS} participants`)
          break
        }
        remaining -= 1
        const heldPartyId = resolveHeldPartyUserId(live, currentUserId)
        const heldParty = live.participants?.find((p) => p.userId === heldPartyId)
        const heldPeer =
          heldPartyId === live.peer.userId
            ? live.peer
            : await resolvePeerByUserId(heldPartyId, {
                displayName: heldParty?.displayName,
                avatarUrl: heldParty?.avatarUrl,
              })
        upsertGroupParticipant(user.userId, {
          displayName: user.displayName,
          avatarUrl: user.avatarUrl,
          role: 'guest',
          state: 'ringing',
        })
        invitePublishes.push(publishVoiceCallSignal({
          callId: live.callId,
          type: 'INVITE',
          callerId: currentUserId,
          calleeId: user.userId,
          conversationId: live.conversationId,
          roomCallId: live.callId,
          roomInvite: true,
          joinCallId: live.callId,
          payload: {
            callerDisplayName: me?.displayName ?? 'Someone',
            callerAvatarUrl: me?.avatarUrl ?? null,
            mediaType: live.mediaType ?? 'voice',
            isGroup: true,
            // Transfer invites render the 1:1 overlay on the invitee too.
            ...(isTransfer
              ? {
                  transfer: true,
                  originalCallerId: heldPartyId,
                  originalCallerDisplayName: heldPeer.displayName,
                  originalCallerAvatarUrl: heldPeer.avatarUrl ?? null,
                }
              : {}),
            groupTitle: live.groupTitle ?? '',
            // Carry the ORIGINAL call start (this may be a 1:1 upgraded to a
            // group mid-call) so the room roster row records the true start and
            // the persisted duration matches the full call length.
            ...(live.startedAt
              ? { startedAt: new Date(live.startedAt).toISOString() }
              : {}),
          },
        }).then(
          (): GroupInviteOutcome => ({ user, error: null }),
          (error: unknown): GroupInviteOutcome => ({ user, error }),
        ))
      }

      // Not called because they haven't accepted call recording: drop them
      // from the roster (nobody rang) and tell the host who was skipped.
      void Promise.all(invitePublishes).then((outcomes) => {
        const { skipped } = summarizeGroupInviteConsent(outcomes)
        if (skipped.length === 0) return
        const skippedIds = new Set(skipped.map((p) => p.userId))
        const now = getVoiceCallSession()
        if (now?.callId === live.callId && now.participants) {
          patchVoiceCallSession({
            participants: now.participants.filter((p) => !skippedIds.has(p.userId)),
          })
        }
        recordCallFlow('ctrl:groupInvite:consentSkipped', { count: skipped.length })
        showRecordingConsentPeerNotice({ kind: 'skipped', people: skipped })
      })
    },
    [currentUserId, migrateToGroupMode, sendPeerGroupUpgrade, showNotice, waitForPeerGroupMigration],
  )

  // --- Call-waiting: merge a second incoming call into the current one ---
  const mergeWaitingCall = useCallback(async () => {
    clearWaitingRingTimer()
    const waiting = getWaitingCall()
    const session = getVoiceCallSession()
    // A telehealth session call (the active one OR the waiting one) can't
    // become part of a group call: decline instead.
    if (
      !waiting ||
      !session ||
      session.phase !== 'active' ||
      !currentUserId ||
      session.sessionUuid ||
      waiting.sessionUuid
    ) {
      dropWaitingCall('BUSY')
      return
    }
    clearWaitingCall()

    // 1. Upgrade our active 1:1 call to a group (we host) so the original peer
    //    comes along; no-op if we're already in a group call.
    const peerUserId =
      session.callerId === currentUserId ? session.calleeId : session.callerId
    if (!session.isGroup) {
      await migrateToGroupMode(true)
      await sendPeerGroupUpgrade()
      await waitForPeerGroupMigration(session.callId, peerUserId)
    }

    // 2. End the waiting caller's original outgoing leg so their dialer stops,
    //    then invite them into THIS call (reuses the room-invite / add-people
    //    path). The short delay lets the END land before the new INVITE so their
    //    client doesn't auto-reject the invite as "busy" on the old leg.
    await publishVoiceCallSignal({
      callId: waiting.callId,
      type: 'END',
      callerId: waiting.callerId,
      calleeId: waiting.calleeId,
      conversationId: waiting.conversationId,
      payload: buildCallEndPayload('CANCELLED', waiting.startedAt),
    }).catch(() => {})

    const targetUser: ChatUser = {
      userId: waiting.peer.userId,
      email: '',
      displayName: waiting.peer.displayName,
      avatarUrl: waiting.peer.avatarUrl ?? null,
    }
    showNotice(`Merging ${waiting.peer.displayName} into the call…`)
    // Brief pause so the waiting caller's END lands before the room INVITE.
    await new Promise((r) => setTimeout(r, 400))
    void inviteUsersToActiveCall([targetUser])
  }, [
    currentUserId,
    clearWaitingRingTimer,
    dropWaitingCall,
    migrateToGroupMode,
    sendPeerGroupUpgrade,
    inviteUsersToActiveCall,
    showNotice,
    waitForPeerGroupMigration,
  ])

  const declineWaitingCall = useCallback(() => {
    dropWaitingCall('BUSY')
  }, [dropWaitingCall])

  const handleTransferSignal = useCallback(
    async (payload: TransferSignalPayload, senderId: string) => {
      const session = getVoiceCallSession()
      if (!session || session.phase !== 'active') return

      switch (payload.action) {
        case 'upgrade_group':
          // The sender is the host that initiated the upgrade. A transfer-
          // flavored upgrade keeps the 1:1 overlay on the held peer too.
          await migrateToGroupMode(false, payload.transferrerId ?? senderId, {
            transfer: payload.transfer === true,
          })
          return
        case 'blind_redial': {
          // We are the held party being forwarded. Drop this leg and place a
          // fresh 1:1 call to the target so they see OUR caller ID. No room /
          // group bridge → no phantom participant, no group UI.
          if (senderId === currentUserId) return
          const targetUserId = payload.targetUserId?.trim()
          if (!targetUserId || targetUserId === currentUserId) return
          const targetName = payload.targetDisplayName ?? 'your contact'
          const mediaType = session.mediaType ?? 'voice'
          const conversationId = session.conversationId

          // Mark completing+onHold first so the imminent remote-left from the
          // transferrer dropping off doesn't tear us down before we re-dial.
          patchVoiceCallSession({
            transfer: { phase: 'completing', mode: 'blind', onHold: true },
          })
          holdMusicPlayerRef.current.stop()
          showNotice(`Connecting you to ${targetName}…`)

          // End the original leg ourselves (writes its terminal history + drops
          // the transferrer off). We're the only party driving teardown, so the
          // sequence can't reorder. Our own END is filtered on this device, and
          // by the time any cross-relay lands we're on a new callId (guarded).
          void sendEnd(
            session.callId,
            session.callerId,
            session.calleeId,
            session.conversationId,
            'COMPLETED',
            session.startedAt,
            undefined,
            { mediaType },
          )

          await disposeAdapter()
          // End the OLD leg's native CallKit/Telecom call too. This path skips
          // finishCall on purpose (UI continuity into the redial), but that
          // also skipped finishCall's native teardown — on iOS the answered
          // CallKit call stayed alive OWNING the AVAudioSession while the
          // redial joined the new meeting with no CallKit anchor, so the new
          // getUserMedia track came up muted and audio never routed (2026-08-18
          // "transfer connected but neither side could hear" — Jaiden's
          // chime:mic:started had trackMuted:true, callKitOwnsAudioSession:true,
          // hadCallKitUuid:false). Awaited so CallKit releases the session
          // before the settle pause below.
          resetNativeAudioRoute()
          if (isNativeApp()) await setNativeCallDisconnected(session.callId)
          // Clear the old session so startCallWithMedia's "already in a call"
          // guard doesn't block the forwarded call.
          setVoiceCallSession(null)
          // Give WebKit/Blink a beat to fully release the old call's mic
          // stream — an immediate getUserMedia after teardown can hang on iOS,
          // stalling the redial join (mirrors the merge path's settle pause).
          await new Promise((r) => setTimeout(r, 300))

          const target: ChatUser = {
            userId: targetUserId,
            email: '',
            displayName: targetName,
            avatarUrl: payload.targetAvatarUrl ?? null,
          }
          const me = cachedSelfChatUser(currentUserId)
          try {
            await startCallWithMediaRef.current?.(
              target,
              mediaType,
              conversationId,
              me?.displayName
                ? { displayName: me.displayName, avatarUrl: me.avatarUrl }
                : undefined,
              { bypassGate: true },
            )
          } catch (err) {
            // The redial INVITE can fail (e.g. the target isn't callable for
            // this user). The old leg is already torn down, so surface a clean
            // failure instead of an unhandled rejection with a half-dead UI
            // (2026-08-18: the transfer-to-Olivia redial threw "Not authorized
            // to call this user" and the overlay imploded).
            recordCallFlow(
              'ctrl:transfer:redialFailed',
              { error: err instanceof Error ? err.message : String(err) },
              { callId: session.callId },
            )
            // startCallWithMedia cleared its own failed session before
            // rethrowing; resurrect the ORIGINAL call's session as a terminal
            // "ended with error" so the overlay shows what happened.
            const liveAfterFail = getVoiceCallSession()
            if (!liveAfterFail || isTerminalCallPhase(liveAfterFail.phase)) {
              setVoiceCallSession({
                ...session,
                transfer: undefined,
                phase: 'ended',
                endReason: 'error',
                errorMessage: `Transfer to ${targetName} failed — you can call them back directly.`,
                endedAt: Date.now(),
                reconnecting: false,
                notice: undefined,
              })
              const failedCallId = session.callId
              setTimeout(() => {
                if (shouldClearEndedSession(getVoiceCallSession(), failedCallId)) {
                  setVoiceCallSession(null)
                }
              }, 4000)
            }
          }
          return
        }
        default:
          return
      }
    },
    [currentUserId, disposeAdapter, migrateToGroupMode, sendEnd, showNotice],
  )
  handleTransferSignalRef.current = handleTransferSignal

  /**
   * TRUE blind transfer (transferrer / "B" side). We do NOT bridge anyone into a
   * group/room. Instead we tell the held party ("A") to drop this leg and place a
   * fresh 1:1 call to the target ("C"), then we drop off entirely. Because the
   * A↔C call is a normal INVITE, C sees A's caller ID and there is no room roster
   * (so no phantom participant, no group UI).
   */
  const completeBlindTransfer = useCallback(
    async (target: ChatUser) => {
      const session = getVoiceCallSession()
      if (!session || !currentUserId || session.phase !== 'active') return
      // A telehealth session call stays between the session's two parties.
      if (session.sessionUuid) return

      const heldPartyId = resolveHeldPartyUserId(session, currentUserId)
      const base = {
        callId: session.callId,
        callerId: session.callerId,
        calleeId: session.calleeId,
        conversationId: session.conversationId,
      }

      patchVoiceCallSession({
        transfer: {
          phase: 'completing',
          mode: 'blind',
          targetUserId: target.userId,
          targetDisplayName: target.displayName,
          holdingPeer: true,
        },
      })
      showNotice(`Transferring to ${target.displayName}…`)

      // Tell the held party to forward themselves to the target as a fresh 1:1.
      // They own the teardown: once they re-dial they END the original A↔B leg
      // (writes its terminal history), which lands here and drops us off — so the
      // sequence is controlled by a single party and can't reorder. The fallback
      // timer below guards the rare case the held party never responds.
      try {
        await publishVoiceCallSignal(
          publishTransferInput(base, 'blind_redial', {
            mode: 'blind',
            parentCallId: session.callId,
            targetUserId: target.userId,
            targetDisplayName: target.displayName,
            targetAvatarUrl: target.avatarUrl ?? null,
            transferrerId: currentUserId,
            originalCallerId: heldPartyId,
          }),
        )
      } catch {
        patchVoiceCallSession({ transfer: { phase: 'failed', mode: 'blind' } })
        showNotice('Transfer failed — try again.')
        return
      }

      window.setTimeout(() => {
        const live = getVoiceCallSession()
        if (live?.callId !== base.callId || isTerminalCallPhase(live.phase)) return
        // Held party didn't tear us down — end the leg ourselves and drop off.
        void sendEnd(
          base.callId,
          base.callerId,
          base.calleeId,
          base.conversationId,
          'COMPLETED',
          session.startedAt,
          undefined,
          { mediaType: session.mediaType },
        )
        finishCall('completed')
        void disposeAdapter()
      }, 5000)
    },
    [currentUserId, disposeAdapter, sendEnd, showNotice],
  )
  completeBlindTransferRef.current = completeBlindTransfer

  // --- Start a group call as host ---
  const dialGroupCallAsHost = useCallback(
    async (
      callId: string,
      mediaType: CallMediaType,
      allowed: ChatUser[],
      conversationId: string | null | undefined,
      groupTitle: string | undefined,
      callerName: string,
      callerAvatarUrl: string | null | undefined,
      startedAt: number,
    ) => {
      patchVoiceCallSession({ phase: 'connecting' })

      const permissions = await ensureCallPermissionsForCall({ video: mediaType === 'video' })
      if (permissions.microphone === 'denied') {
        finishCall('error', microphoneDeniedMessage())
        await disposeAdapter()
        return
      }
      if (mediaType === 'video' && permissions.camera === 'denied') {
        finishCall('error', cameraDeniedMessage())
        await disposeAdapter()
        return
      }

      try {
        await joinGroupMeeting(callId, localAttendeeDisplayName(currentUserId!), mediaType)
      } catch (err) {
        finishCall(
          'error',
          err instanceof CallFullError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to start group call',
        )
        await disposeAdapter()
        return
      }

      // Honor lobby camera choice — group meetings join audio-only by default.
      if (mediaType === 'video') {
        const live = getVoiceCallSession()
        if (live && !live.isCameraOff && callAdapterRef.current) {
          try {
            await callAdapterRef.current.enableVideo({
              localVideo: localVideoRef.current,
              remoteVideo: remoteVideoRef.current,
              contentVideo: contentVideoRef.current,
            })
          } catch {
            patchVoiceCallSession({ isCameraOff: true })
          }
        }
      }

      const invitePublishes = allowed.map(
        (invitee): Promise<GroupInviteOutcome> =>
          publishVoiceCallSignal({
            callId,
            type: 'INVITE',
            callerId: currentUserId!,
            calleeId: invitee.userId,
            conversationId,
            roomCallId: callId,
            roomInvite: true,
            joinCallId: callId,
            payload: {
              callerDisplayName: callerName,
              callerAvatarUrl: callerAvatarUrl ?? null,
              mediaType,
              isGroup: true,
              groupTitle: groupTitle ?? '',
              startedAt: new Date(startedAt).toISOString(),
            },
          }).then(
            () => ({ user: invitee, error: null }),
            (error: unknown) => ({ user: invitee, error }),
          ),
      )

      // Consent BEFORE connect: the server skips anyone who hasn't accepted
      // call recording (their INVITE is refused, nobody rings). Drop them from
      // the roster and tell the host; with nobody left to call, the call isn't
      // placed. A host who hasn't accepted is asked first, then the same call
      // is started again.
      void Promise.all(invitePublishes).then(async (outcomes) => {
        const { skipped, selfRequired, nobodyLeft } = summarizeGroupInviteConsent(outcomes)
        const live = getVoiceCallSession()
        if (live?.callId !== callId || isTerminalCallPhase(live.phase)) return
        if (selfRequired) {
          recordCallFlow('ctrl:groupDial:consentRequiredSelf', undefined, { callId })
          const retried = groupCallStartOptionsRef.current.recordingConsentRetried
          if (retried) {
            finishCall('error', recordingConsentRequiredSelfMessage())
            await disposeAdapter()
            return
          }
          finishCall('cancelled')
          await disposeAdapter()
          await loadRecordingPolicyConsent().catch(() => undefined)
          setPendingRecordingPolicyGate({
            peer: null,
            mediaType,
            proceed: () => {
              void startGroupCallRef.current?.(
                allowed,
                mediaType,
                conversationId,
                groupTitle,
                { displayName: callerName, avatarUrl: callerAvatarUrl ?? null },
                { bypassRecordingPolicyGate: true, recordingConsentRetried: true },
              )
            },
          })
          return
        }
        if (skipped.length === 0) return
        recordCallFlow('ctrl:groupDial:consentSkipped', { count: skipped.length, nobodyLeft }, { callId })
        if (nobodyLeft) {
          finishCall('cancelled')
          await disposeAdapter()
        } else {
          const skippedIds = new Set(skipped.map((p) => p.userId))
          patchVoiceCallSession({
            participants: (live.participants ?? []).filter((p) => !skippedIds.has(p.userId)),
          })
        }
        showRecordingConsentPeerNotice({
          kind: nobodyLeft && skipped.length === 1 ? 'peer' : 'skipped',
          people: skipped,
        })
      })
    },
    [currentUserId, disposeAdapter, joinGroupMeeting],
  )

  const confirmGroupJoin = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session?.isGroup || !session.isHost || session.phase !== 'prejoin') return

    const allowed = (session.participants ?? [])
      .filter((p) => p.state === 'ringing' && p.userId !== currentUserId)
      .map(
        (p) =>
          ({
            userId: p.userId,
            displayName: p.displayName,
            avatarUrl: p.avatarUrl,
          }) as ChatUser,
      )

    await dialGroupCallAsHost(
      session.callId,
      session.mediaType ?? 'video',
      allowed,
      session.conversationId,
      session.groupTitle,
      session.participants?.find((p) => p.userId === currentUserId)?.displayName ?? 'Someone',
      session.participants?.find((p) => p.userId === currentUserId)?.avatarUrl,
      session.startedAt ?? Date.now(),
    )
  }, [currentUserId, dialGroupCallAsHost])

  const startGroupCall = useCallback(
    async (
      members: ChatUser[],
      mediaType: CallMediaType,
      conversationId?: string | null,
      groupTitle?: string,
      caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
      options?: Pick<StartCallOptions, 'bypassRecordingPolicyGate' | 'recordingConsentRetried'>,
    ) => {
      if (!currentUserId) throw new Error('Not signed in')
      if (isAppLocked()) {
        const unlocked = await ensureSoftUnlocked()
        if (!unlocked) throw new Error("Verify it's you before starting a call")
      }
      const existing = getVoiceCallSession()
      if (existing && existing.phase !== 'ended' && existing.phase !== 'idle') {
        throw new Error('Already in a call')
      }

      // Same pre-dial recording-policy gate as 1:1: an unconsented member host
      // must Accept BEFORE we create/join the Chime meeting.
      if (!options?.bypassRecordingPolicyGate && needsOutgoingRecordingPolicyGate()) {
        setPendingRecordingPolicyGate({
          peer: null,
          mediaType,
          proceed: () => {
            void startGroupCallRef.current?.(members, mediaType, conversationId, groupTitle, caller, {
              ...options,
              bypassRecordingPolicyGate: true,
            })
          },
        })
        return
      }
      groupCallStartOptionsRef.current = {
        recordingConsentRetried: options?.recordingConsentRetried,
      }

      const invitees = members.filter((m) => m.userId && m.userId !== currentUserId)
      // Host counts toward the cap, so we can invite up to (cap - 1) others.
      const maxInvitees = MAX_CALL_PARTICIPANTS - 1
      const allowed = invitees.slice(0, maxInvitees)
      const overflow = invitees.length > allowed.length

      const callId = createCallId()
      const startedAt = Date.now()
      const callerName =
        caller?.displayName?.trim() ||
        [caller?.givenName, caller?.familyName].filter(Boolean).join(' ') ||
        cachedSelfChatUser(currentUserId)?.displayName ||
        'Someone'

      const participants: CallParticipant[] = [
        {
          userId: currentUserId,
          displayName: cachedSelfChatUser(currentUserId)?.displayName ?? callerName,
          avatarUrl: caller?.avatarUrl ?? cachedSelfChatUser(currentUserId)?.avatarUrl,
          role: 'host',
          state: 'active',
        },
        ...allowed.map<CallParticipant>((m) => ({
          userId: m.userId,
          displayName: m.displayName,
          avatarUrl: m.avatarUrl,
          role: 'guest',
          state: 'ringing',
        })),
      ]

      // Group video: Meet-style lobby first — dial only after the host taps Join.
      if (mediaType === 'video') {
        const permissions = await ensureCallPermissionsForCall({ video: true })
        if (permissions.microphone === 'denied') {
          throw new Error(microphoneDeniedMessage())
        }
        if (permissions.camera === 'denied') {
          throw new Error(cameraDeniedMessage())
        }

        setVoiceCallSession({
          callId,
          callerId: currentUserId,
          calleeId: allowed[0]?.userId ?? currentUserId,
          conversationId,
          localRole: 'caller',
          peer: {
            userId: `group:${callId}`,
            displayName: groupTitle ?? 'Group call',
          },
          phase: 'prejoin',
          mediaType,
          isMuted: false,
          isCameraOff: false,
          isGroup: true,
          isHost: true,
          hostId: currentUserId,
          maxParticipants: MAX_CALL_PARTICIPANTS,
          groupTitle,
          participants,
          startedAt,
        })

        if (overflow) {
          showNotice(`This call is limited to ${MAX_CALL_PARTICIPANTS} participants`)
        }
        return
      }

      setVoiceCallSession({
        callId,
        callerId: currentUserId,
        calleeId: allowed[0]?.userId ?? currentUserId,
        conversationId,
        localRole: 'caller',
        peer: {
          userId: `group:${callId}`,
          displayName: groupTitle ?? 'Group call',
        },
        phase: 'connecting',
        mediaType,
        isMuted: false,
        isCameraOff: true,
        isGroup: true,
        isHost: true,
        hostId: currentUserId,
        maxParticipants: MAX_CALL_PARTICIPANTS,
        groupTitle,
        participants,
        startedAt,
      })

      await dialGroupCallAsHost(
        callId,
        mediaType,
        allowed,
        conversationId,
        groupTitle,
        callerName,
        caller?.avatarUrl,
        startedAt,
      )

      if (overflow) {
        showNotice(`This call is limited to ${MAX_CALL_PARTICIPANTS} participants`)
      }
    },
    [currentUserId, dialGroupCallAsHost, showNotice],
  )

  startGroupCallRef.current = startGroupCall

  const startGroupVideoCall = useCallback(
    (
      members: ChatUser[],
      conversationId?: string | null,
      groupTitle?: string,
      caller?: Pick<ChatUser, 'displayName' | 'avatarUrl' | 'givenName' | 'familyName'>,
    ) => startGroupCall(members, 'video', conversationId, groupTitle, caller),
    [startGroupCall],
  )

  // --- Host (or 1:1 upgrader): add more people mid-call ---
  const addPeople = useCallback(
    async (users: ChatUser[]) => {
      const session = getVoiceCallSession()
      if (!session || !currentUserId || session.phase !== 'active') return
      if (session.isGroup && !session.isHost) return
      if (session.sessionUuid) return
      await inviteUsersToActiveCall(users)
    },
    [currentUserId, inviteUsersToActiveCall],
  )

  // --- Host: remove a participant / cooperative "ask to mute" ---
  const removeParticipant = useCallback(
    (userId: string) => {
      const session = getVoiceCallSession()
      if (!session?.isGroup || !session.isHost) return
      sendRoomSignal(session.callId, 'PARTICIPANT_LEFT', { kind: 'remove', userId })
      upsertGroupParticipant(userId, { state: 'left', tileId: null, speaking: false })
    },
    [sendRoomSignal],
  )

  const askToMute = useCallback(
    (userId: string) => {
      const session = getVoiceCallSession()
      if (!session?.isGroup || !session.isHost) return
      sendRoomSignal(session.callId, 'MEDIA_SWITCH', { kind: 'ask-to-mute', userId })
      showNotice('Asked to mute')
    },
    [sendRoomSignal, showNotice],
  )

  // --- Host: promote the group call to video ---
  const promoteGroupToVideo = useCallback(async () => {
    const session = getVoiceCallSession()
    if (!session?.isGroup || !session.isHost || session.mediaType === 'video') return
    patchVoiceCallSession({ mediaType: 'video' })
    sendRoomSignal(session.callId, 'MEDIA_SWITCH', {
      kind: 'media',
      to: 'video',
    } as unknown as RoomSignalPayload)
  }, [sendRoomSignal])

  // --- Per-participant video tile binding (multi-party grid) ---
  const bindParticipantTile = useCallback((tileId: number, el: HTMLVideoElement) => {
    callAdapterRef.current?.bindRemoteTile(tileId, el)
  }, [])

  const unbindParticipantTile = useCallback((tileId: number) => {
    callAdapterRef.current?.unbindRemoteTile(tileId)
  }, [])

  /** Register the local self-view element for the group video grid. */
  const setGroupSelfVideoElement = useCallback((el: HTMLVideoElement | null) => {
    localVideoRef.current = el
    if (el) callAdapterRef.current?.updateElements({ localVideo: el })
  }, [])

  // Every member below is a stable `useCallback`, so memoizing the returned
  // object keeps the voice-call context value referentially stable across the
  // provider's re-renders (which fire on every session/roster update). Without
  // this, every `useVoiceCall()` consumer re-rendered on each roster tick during
  // add-people / merge — the source of the call-overlay flicker.
  return useMemo(
    () => ({
      startCall,
      startLeadershipUrgentRing,
      startVideoCall,
      startGroupCall,
      startGroupVideoCall,
      confirmGroupJoin,
      addPeople,
      mergeWaitingCall,
      declineWaitingCall,
      completeBlindTransfer,
      endCallForEveryone,
      removeParticipant,
      askToMute,
      promoteGroupToVideo,
      bindParticipantTile,
      unbindParticipantTile,
      setGroupSelfVideoElement,
      acceptCall,
      requestAcceptCall,
      cancelRecordingConsentRequest,
      declineCall,
      declineRecordingConsent,
      notePostConnectConsentNoticeShown,
      confirmPostConnectRecordingConsent,
      declinePostConnectRecordingConsent,
      hangUp,
      recallLastCall,
      switchToThisDevice,
      dismissActiveElsewhere,
      upgradeToVideo,
      downgradeToVoice,
      acceptVideoRequest,
      declineVideoRequest,
      resumeDrivingVideo,
      startScreenShare,
      stopScreenShare,
      setBackgroundFilter,
      toggleMute,
      notifyPeerHold,
      toggleCamera,
      toggleSpeaker,
      setRemoteAudioElement,
      setVideoElements,
      setContentVideoElement,
      refreshContentVideoBinding,
    }),
    [
      startCall,
      startLeadershipUrgentRing,
      startVideoCall,
      startGroupCall,
      startGroupVideoCall,
      confirmGroupJoin,
      addPeople,
      mergeWaitingCall,
      declineWaitingCall,
      completeBlindTransfer,
      endCallForEveryone,
      removeParticipant,
      askToMute,
      promoteGroupToVideo,
      bindParticipantTile,
      unbindParticipantTile,
      setGroupSelfVideoElement,
      acceptCall,
      requestAcceptCall,
      cancelRecordingConsentRequest,
      declineCall,
      declineRecordingConsent,
      notePostConnectConsentNoticeShown,
      confirmPostConnectRecordingConsent,
      declinePostConnectRecordingConsent,
      hangUp,
      recallLastCall,
      switchToThisDevice,
      dismissActiveElsewhere,
      upgradeToVideo,
      downgradeToVoice,
      acceptVideoRequest,
      declineVideoRequest,
      resumeDrivingVideo,
      startScreenShare,
      stopScreenShare,
      setBackgroundFilter,
      toggleMute,
      notifyPeerHold,
      toggleCamera,
      toggleSpeaker,
      setRemoteAudioElement,
      setVideoElements,
      setContentVideoElement,
      refreshContentVideoBinding,
    ],
  )
}
